import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Tabs, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Select, 
  Input, 
  Row, 
  Col, 
  Typography, 
  message, 
  Tooltip,
  Badge,
  Modal,
  Collapse,
  Pagination,
  Dropdown,
  Spin,
  DatePicker,
  Form,
  Radio,
  Alert,
  Drawer,
  Upload,
  List,
  Popconfirm,
  Checkbox
} from 'antd';
import { 
  TableOutlined, 
  AppstoreOutlined, 
  SearchOutlined, 
  FilterOutlined,
  EyeOutlined,
  UserOutlined,
  CalendarOutlined,
  PhoneOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  EditOutlined,
  FileTextOutlined,
  CommentOutlined,
  PaperClipOutlined,
  StarFilled,
  CrownOutlined,
  MailOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  FolderOutlined,
  DeleteOutlined,
  PlusOutlined,
  FilePdfOutlined as FilePdfIcon,
  ShoppingCartOutlined,
  FireOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { Lead, User } from '../services/api';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { CognitoUser } from '../services/cognitoService';
import apiService from '../services/api';
import cognitoApiService from '../services/cognitoApi';
import LeadDetailsModal from './LeadDetailsModal';
import VodafoneDocumentWizard from './VodafoneDocumentWizard';
import LeadDocumentsModal from './LeadDocumentsModal';
import EventModal from './EventModal';
import NOSDocumentWizard from './NOSDocumentWizard';
import NOSSecuritasDocumentWizard from './NOSSecuritasDocumentWizard';
import ProsegurForm from './ProsegurForm';
import PDFUploadModal from './PDFUploadModal';
import NotesPanel from './NotesPanel';
import AlticeResidencialForm from './AlticeResidencialForm';
import EnergyProposalWizard from './EnergyProposalWizard';
import VendaModal, { OperatorType, OperatorCustomerData } from './VendaModal';
import { salesApi } from '../services/salesApi';
import dayjs from 'dayjs';
import moment from 'moment';
import TaskMaster from './TaskMaster';
import EmailModal from './EmailModal';
import GhostLoader from './GhostLoader';
import './ClientManagement.css';
// Chat functionality disabled
// import ChatModal from './ChatModal';
import {
  TELECOM_SECTIONS
} from './statusConstants';
import { isAdminOrDCOrTeamLeader, canCreateFolders } from '../utils/roleUtils';
import httpClient from '../services/httpClient';
import { getLeadCategoryStyle } from '../utils/leadCategoryColors';
import { userMayUseClientManagementEmailButton } from '../utils/clientManagementEmailAccess';

const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Option } = Select;
const { Search } = Input;
const { Title, Text } = Typography;

// Document Validation List Component
const DocumentValidationList: React.FC<{ lead: Lead; onPreview: (file: any) => void; onDownload: (file: any) => void; refreshTrigger?: number }> = ({ lead, onPreview, onDownload, refreshTrigger = 0 }) => {
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLeadFiles();
  }, [lead.id, refreshTrigger]);

  const fetchLeadFiles = async () => {
    if (!lead?.id) return;
    
    setLoading(true);
    try {
      // Fetch uploaded files
      const filesResponse = await apiService.getLeadFiles(lead.id, {
        stage: 'registro',
        field_name: 'documentos_assinados'
      });
      
      // Fetch generated documents
      const documentsResponse = await apiService.getLeadDocuments(lead.id);
      
      let allFiles: any[] = [];
      
      // Add uploaded files with lead_id
      if (filesResponse.success && filesResponse.files) {
        const filesWithLeadId = filesResponse.files.map((file: any) => ({
          ...file,
          lead_id: lead.id // Ensure lead_id is set
        }));
        allFiles = [...filesWithLeadId];
      }
      
      // Add generated documents with lead_id
      if (documentsResponse.success && documentsResponse.documents) {
        const documentsWithLeadId = documentsResponse.documents.map((doc: any) => ({
          ...doc,
          lead_id: lead.id // Ensure lead_id is set
        }));
        allFiles = [...allFiles, ...documentsWithLeadId];
      }
      

      setFiles(allFiles);
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>A carregar documentos...</div>;
  }

  if (files.length === 0) {
    return <div style={{ padding: '20px', color: 'rgba(255, 255, 255, 0.7)' }}>Nenhum documento carregado</div>;
  }

  return (
    <div style={{ marginTop: 8 }}>
      {files.map((file) => (
        <div
          key={file.id}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px',
            border: '1px solid #f0f0f0',
            borderRadius: '4px',
            marginBottom: '8px',
            backgroundColor: '#fafafa'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FilePdfOutlined style={{ color: '#ff4d4f' }} />
            <div>
              <Text strong style={{ fontSize: 12 }}>
                {file.file_name || file.document_type || 'Documento'}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 10 }}>
                {new Date(file.uploaded_at || file.created_at).toLocaleDateString('pt-PT')} • {file.user_name || 'Sistema'}
              </Text>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <Button
              type="text"
              size="small"
              onClick={() => onPreview(file)}
              icon={<EyeOutlined />}
            >
              Pré-visualizar
            </Button>
            <Button
              type="text"
              size="small"
              onClick={() => onDownload(file)}
              icon={<DownloadOutlined />}
            >
              Download
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

interface ClientManagementProps {
  /** Compact panel for Workspace de Cliente: same Gestão logic, Kanban-only, no full-page chrome. */
  workspaceEmbed?: boolean;
  /** When set with `workspaceEmbed`, "Ver detalhes" selects this lead in the parent workspace instead of opening the modal. */
  onOpenLeadInWorkspace?: (lead: Lead) => void;
  /** Admin workspace: view leads/kanban as this user (from parent ClientWorkspace). */
  workspaceViewAsUserId?: number;
}

interface SectionConfig {
  key: string;
  label: string;
  states: string[];
  color: string;
}

// Utility function to get business area as array
function getBusinessAreaArray(business_area: any): string[] {
  if (!business_area) return [];
  if (Array.isArray(business_area)) return business_area;
  if (typeof business_area === 'string') {
    try {
      const parsed = JSON.parse(business_area);
      if (Array.isArray(parsed)) return parsed;
      return [parsed];
    } catch {
      return [business_area];
    }
  }
  return [String(business_area)];
}

// --- Move 'Outros' to the top in grouping functions ---
function groupEntradaLeads(leads: Lead[], currentPage: Map<string, number> = new Map(), leadsPerPage: number = 5) {
  // Safety check to ensure leads is an array
  if (!Array.isArray(leads)) {
    return [
      { key: 'outros', label: 'Outros (0)', leads: [], totalLeads: 0, currentPage: 1 },
      { key: 'naoAtribuido', label: 'Não Atribuído (0)', leads: [], totalLeads: 0, currentPage: 1 },
      { key: 'naoAtendeu', label: 'Não Atendeu (0)', leads: [], totalLeads: 0, currentPage: 1 },
    ];
  }
  
  const safeLeads = Array.isArray(leads) ? leads : [];
  const naoAtribuido = safeLeads.filter(l => l.lead_status === 'Não Atribuido');
  const naoAtendeu = safeLeads.filter(l => l.lead_status === 'Não Atendeu');
  const outros = safeLeads.filter(l => l.lead_status !== 'Não Atribuido' && l.lead_status !== 'Não Atendeu');
    

  
  // Apply pagination to each group
  const naoAtribuidoPage = currentPage.get('naoAtribuido') || 1;
  const naoAtribuidoStartIndex = (naoAtribuidoPage - 1) * leadsPerPage;
  const naoAtribuidoPaginatedLeads = naoAtribuido.slice(naoAtribuidoStartIndex, naoAtribuidoStartIndex + leadsPerPage);
  
  const naoAtendeuPage = currentPage.get('naoAtendeu') || 1;
  const naoAtendeuStartIndex = (naoAtendeuPage - 1) * leadsPerPage;
  const naoAtendeuPaginatedLeads = naoAtendeu.slice(naoAtendeuStartIndex, naoAtendeuStartIndex + leadsPerPage);
  
  const outrosPage = currentPage.get('outros') || 1;
  const outrosStartIndex = (outrosPage - 1) * leadsPerPage;
  const outrosPaginatedLeads = outros.slice(outrosStartIndex, outrosStartIndex + leadsPerPage);
  
  return [
    { 
      key: 'outros', 
      label: `Outros (${outros.length})`, 
      leads: outrosPaginatedLeads,
      totalLeads: outros.length,
      currentPage: outrosPage
    },
    { 
      key: 'naoAtribuido', 
      label: `Não Atribuído (${naoAtribuido.length})`, 
      leads: naoAtribuidoPaginatedLeads,
      totalLeads: naoAtribuido.length,
      currentPage: naoAtribuidoPage
    },
    { 
      key: 'naoAtendeu', 
      label: `Não Atendeu (${naoAtendeu.length})`, 
      leads: naoAtendeuPaginatedLeads,
      totalLeads: naoAtendeu.length,
      currentPage: naoAtendeuPage
    },
  ];
}

function groupFeedbackLeads(leads: Lead[], currentPage: Map<string, number> = new Map(), leadsPerPage: number = 5) {
  // Safety check to ensure leads is an array
  if (!Array.isArray(leads)) {
    return [];
  }
  
  const statuses = [
    // Removed 'Atendeu' subsection
    { key: 'pendente', label: 'Pendente' },
    { key: 'followUp', label: 'Follow-up' },
    { key: 'semInteresse', label: 'Sem Interesse' },
    { key: 'fidelizado', label: 'Fidelizado' },
    { key: 'informacaoErrada', label: 'Informação Errada' },
    { key: 'semCobertura', label: 'Sem Cobertura' }
  ];
  
  // Get all the specific status labels
  const specificLabels = statuses.map(s => s.label);
  
  // Add "Outros" group for leads that don't match any specific status (including 'Atendeu' and empty status)
  const safeLeads = Array.isArray(leads) ? leads : [];
  const outrosLeads = safeLeads.filter(l => !specificLabels.includes(l.lead_status || ''));
  const outrosPage = currentPage.get('outros') || 1;
  const outrosStartIndex = (outrosPage - 1) * leadsPerPage;
  const outrosPaginatedLeads = outrosLeads.slice(outrosStartIndex, outrosStartIndex + leadsPerPage);
  
  const outrosGroup = {
    key: 'outros',
    label: `Outros (${outrosLeads.length})`,
    leads: outrosPaginatedLeads,
    totalLeads: outrosLeads.length,
    currentPage: outrosPage
  };
  
  // Group leads by specific statuses
  const groupedByStatus = statuses.map(({ key, label }) => {
    const groupLeads = safeLeads.filter(l => l.lead_status === label);
    const page = currentPage.get(key) || 1;
    const startIndex = (page - 1) * leadsPerPage;
    const paginatedLeads = groupLeads.slice(startIndex, startIndex + leadsPerPage);
    
    const group = {
      key,
      label: `${label} (${groupLeads.length})`,
      leads: paginatedLeads,
      totalLeads: groupLeads.length,
      currentPage: page
    };
    
    return group;
  });
  
  // Return 'Outros' first, then the rest
  const result = [outrosGroup, ...groupedByStatus];
  return result;
}

function groupRegistroLeads(leads: Lead[], currentPage: Map<string, number> = new Map(), leadsPerPage: number = 5) {
  // Safety check to ensure leads is an array
  if (!Array.isArray(leads)) {
    return [];
  }
  
  const safeLeads = Array.isArray(leads) ? leads : [];
  
  const statuses = [
    { key: 'fecho', label: 'Fecho' },
    { key: 'validacao', label: 'Validação' }
  ];
  
  // Get all the specific status labels
  const specificLabels = statuses.map(s => s.label);
  
  // Group leads by specific statuses
  const groupedByStatus = statuses.map(({ key, label }) => {
    const groupLeads = safeLeads.filter(l => l.lead_status === label);
    const page = currentPage.get(key) || 1;
    const startIndex = (page - 1) * leadsPerPage;
    const paginatedLeads = groupLeads.slice(startIndex, startIndex + leadsPerPage);
    
    const group = {
      key,
      label: `${label} (${groupLeads.length})`,
      leads: paginatedLeads,
      totalLeads: groupLeads.length,
      currentPage: page
    };
    
    return group;
  });
  
  // Add "Outros" group for leads that don't match any specific status
  const outrosLeads = safeLeads.filter(l => !specificLabels.includes(l.lead_status || ''));
  const outrosPage = currentPage.get('outros') || 1;
  const outrosStartIndex = (outrosPage - 1) * leadsPerPage;
  const outrosPaginatedLeads = outrosLeads.slice(outrosStartIndex, outrosStartIndex + leadsPerPage);
  
  const outrosGroup = {
    key: 'outros',
    label: `Outros (${outrosLeads.length})`,
    leads: outrosPaginatedLeads,
    totalLeads: outrosLeads.length,
    currentPage: outrosPage
  };
  
  const result = [...groupedByStatus, outrosGroup];
  return result;
}

function groupInstaladosLeads(leads: Lead[], currentPage: Map<string, number> = new Map(), leadsPerPage: number = 5) {
  // Safety check to ensure leads is an array
  if (!Array.isArray(leads)) {
    return [];
  }
  
  const safeLeads = Array.isArray(leads) ? leads : [];
  
  const statuses = [
    { key: 'porInstalar', label: 'Por Instalar' },
    { key: 'instalados', label: 'Instalados' }
  ];
  
  // Get all the specific status labels
  const specificLabels = statuses.map(s => s.label);
  
  // Group leads by specific statuses
  const groupedByStatus = statuses.map(({ key, label }) => {
    const groupLeads = safeLeads.filter(l => l.lead_status === label);
    const page = currentPage.get(key) || 1;
    const startIndex = (page - 1) * leadsPerPage;
    const paginatedLeads = groupLeads.slice(startIndex, startIndex + leadsPerPage);
    
    const group = {
      key,
      label: `${label} (${groupLeads.length})`,
      leads: paginatedLeads,
      totalLeads: groupLeads.length,
      currentPage: page
    };
    
    return group;
  });
  
  // Add "Outros" group for leads that don't match any specific status
  const outrosLeads = safeLeads.filter(l => !specificLabels.includes(l.lead_status || ''));
  const outrosPage = currentPage.get('outros') || 1;
  const outrosStartIndex = (outrosPage - 1) * leadsPerPage;
  const outrosPaginatedLeads = outrosLeads.slice(outrosStartIndex, outrosStartIndex + leadsPerPage);
  
  const outrosGroup = {
    key: 'outros',
    label: `Outros (${outrosLeads.length})`,
    leads: outrosPaginatedLeads,
    totalLeads: outrosLeads.length,
    currentPage: outrosPage
  };
  
  const result = [...groupedByStatus, outrosGroup];
  return result;
}

// Helper function to check if user has a specific role (case-insensitive and format-flexible)
const hasRole = (user: User | CognitoUser | null, allowedRoles: string[]): boolean => {
  if (!user || !user.role) return false;
  
  const userRole = user.role.trim();
  
  return allowedRoles.some(role => {
    const normalizedRole = role.toLowerCase().replace(/\s+/g, '_');
    const normalizedUserRole = userRole.toLowerCase().replace(/\s+/g, '_');
    return normalizedRole === normalizedUserRole;
  });
};

const ClientManagement: React.FC<ClientManagementProps> = ({
  workspaceEmbed = false,
  onOpenLeadInWorkspace,
  workspaceViewAsUserId,
}) => {
  const { user, isLoading } = useCognitoAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  
  // Ensure leads is always an array to prevent TypeError
  const safeLeads = Array.isArray(leads) ? leads : [];
  const [activeSection, setActiveSection] = useState<string>('entrada');
  const [viewMode, setViewMode] = useState<'grid' | 'kanban'>('kanban');

  useEffect(() => {
    if (workspaceEmbed) {
      setViewMode('kanban');
    }
  }, [workspaceEmbed]);
  const [searchText, setSearchText] = useState('');
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  
  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState({
    operadora: [] as string[],
    mes: [] as string[], // No default month filter
    dia: [] as number[]
  });

  // See Own Leads filter state
  const [showOwnLeadsOnly, setShowOwnLeadsOnly] = useState(false);
  
  // Radio button state for Fecho subsection document generation - per lead
  const [fechoDocumentMode, setFechoDocumentMode] = useState<Map<number, string>>(new Map());
  
  const handleFechoDocumentModeChange = (leadId: number, value: string) => {
    setFechoDocumentMode(prev => {
      const newMap = new Map(prev);
      newMap.set(leadId, value);
      return newMap;
    });
  };
  
  const getFechoDocumentMode = (leadId: number): string => {
    return fechoDocumentMode.get(leadId) || '1';
  };

  // State for tracking last contact date changes
  const [lastContactDates, setLastContactDates] = useState<Map<number, string>>(new Map());
  const [lastContactDatesChanged, setLastContactDatesChanged] = useState<Set<number>>(new Set());
  
  // State for tracking installation date changes per business area
  // Map<leadId, Map<businessArea, date>>
  const [installationDates, setInstallationDates] = useState<Map<number, Map<string, string>>>(new Map());
  const [installationDatesChanged, setInstallationDatesChanged] = useState<Set<number>>(new Set());
  // Remove modal state since we're using dropdown instead
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [unsavedChanges, setUnsavedChanges] = useState<Map<number, string | null>>(new Map());
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [unsavedProductTypes, setUnsavedProductTypes] = useState<Map<number, string>>(new Map());
  const [unsavedAdditionalCards, setUnsavedAdditionalCards] = useState<Map<number, string>>(new Map());
  
  // State for Instalados business area selection (similar to fechoDocumentMode)
  const [instaladosBusinessArea, setInstaladosBusinessArea] = useState<Map<number, string>>(new Map());
  const [instaladosBusinessAreaChanged, setInstaladosBusinessAreaChanged] = useState<Set<number>>(new Set());
  
  // State for selected products in Instalados section (per lead)
  const [instaladosSelectedProducts, setInstaladosSelectedProducts] = useState<Map<number, number[]>>(new Map());
  const [instaladosSelectedProductsChanged, setInstaladosSelectedProductsChanged] = useState<Set<number>>(new Set());
  
  // State for products and sections from Gestor de Vendas
  const [salesProducts, setSalesProducts] = useState<any[]>([]);
  const [salesSections, setSalesSections] = useState<any[]>([]);
  
  // Fetch products and sections from Gestor de Vendas
  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        const [productsData, sectionsData] = await Promise.all([
          salesApi.getProducts(),
          salesApi.getSections()
        ]);
        setSalesProducts(productsData);
        setSalesSections(sectionsData);
      } catch (error) {
        console.error('Error fetching sales data:', error);
      }
    };
    fetchSalesData();
  }, []);
  
  const handleInstaladosBusinessAreaChange = (leadId: number, value: string) => {
    setInstaladosBusinessArea(prev => {
      const newMap = new Map(prev);
      newMap.set(leadId, value);
      return newMap;
    });
    // Mark as changed
    setInstaladosBusinessAreaChanged(prev => new Set(prev).add(leadId));
    // Clear selected products when changing business area
    setInstaladosSelectedProducts(prev => {
      const newMap = new Map(prev);
      newMap.delete(leadId);
      return newMap;
    });
    // Also mark products as changed since we're clearing them
    setInstaladosSelectedProductsChanged(prev => new Set(prev).add(leadId));
  };
  
  const getInstaladosBusinessArea = (leadId: number): string => {
    return instaladosBusinessArea.get(leadId) || '1'; // Default to '1' (not selected)
  };
  
  const handleInstaladosProductToggle = (leadId: number, productId: number) => {
    setInstaladosSelectedProducts(prev => {
      const newMap = new Map(prev);
      const currentProducts = newMap.get(leadId) || [];
      if (currentProducts.includes(productId)) {
        // Remove product
        newMap.set(leadId, currentProducts.filter(id => id !== productId));
      } else {
        // Add product
        newMap.set(leadId, [...currentProducts, productId]);
      }
      return newMap;
    });
    // Mark as changed
    setInstaladosSelectedProductsChanged(prev => new Set(prev).add(leadId));
  };
  
  const getInstaladosSelectedProducts = (leadId: number): number[] => {
    return instaladosSelectedProducts.get(leadId) || [];
  };
  
  const [pendingSection, setPendingSection] = useState<string | null>(null);
  const [activePanels, setActivePanels] = useState<string[]>([]);

  // Pagination state for subsections
  const [subsectionPagination, setSubsectionPagination] = useState<Map<string, number>>(new Map());
  const LEADS_PER_PAGE = 5;

  // Vodafone Document Wizard state
  const [vodafoneWizardVisible, setVodafoneWizardVisible] = useState(false);
  const [selectedLeadForDocuments, setSelectedLeadForDocuments] = useState<Lead | null>(null);

  // Lead Documents Modal state
  const [documentsModalVisible, setDocumentsModalVisible] = useState(false);
  const [selectedLeadForDocumentsList, setSelectedLeadForDocumentsList] = useState<Lead | null>(null);
  const [documentsRefreshTrigger, setDocumentsRefreshTrigger] = useState(0);

      // Pendente subsection state
    const [pendenteReasons, setPendenteReasons] = useState<Map<number, string>>(new Map());
  
  // Product Resources Drawer state
  // Load state from sessionStorage on mount
  const [resourcesDrawerVisible, setResourcesDrawerVisible] = useState(false);
  const [activeResourceTab, setActiveResourceTab] = useState<string>(() => {
    const saved = sessionStorage.getItem('recursos_activeTab');
    return saved || 'telecomunicacao';
  });
  const [productPdfs, setProductPdfs] = useState<{
    telecomunicacao: Array<{ id: number; name: string; url: string; uploaded_at: string; uploaded_by: string; folder_id: number | null }>;
    seguranca: Array<{ id: number; name: string; url: string; uploaded_at: string; uploaded_by: string; folder_id: number | null }>;
    energia: Array<{ id: number; name: string; url: string; uploaded_at: string; uploaded_by: string; folder_id: number | null }>;
  }>({
    telecomunicacao: [],
    seguranca: [],
    energia: []
  });
  const [uploadFolderModalVisible, setUploadFolderModalVisible] = useState(false);
  const [vendaModalVisible, setVendaModalVisible] = useState(false);
  const [vendaModalSkipCustomer, setVendaModalSkipCustomer] = useState(false);
  const [selectedLeadForVenda, setSelectedLeadForVenda] = useState<Lead | null>(null);
  const [pendingUpload, setPendingUpload] = useState<{ category: string } | null>(null);
  const [currentInstalados, setCurrentInstalados] = useState<number>(0);
  const [segurancaProductsSold, setSegurancaProductsSold] = useState<number>(0);
  const [adjustedCommissions, setAdjustedCommissions] = useState<any>(null);
  const [adjustedCommissionsSeguranca, setAdjustedCommissionsSeguranca] = useState<any>(null);
  
  // Fetch instalados count
  useEffect(() => {
    const fetchInstalados = async () => {
      try {
        const data = await salesApi.getCurrentMonthInstalados();
        setCurrentInstalados(data.count);
      } catch (error) {
        console.error('Error fetching instalados:', error);
      }
    };
    fetchInstalados();
    // Refresh every 30 seconds
    const interval = setInterval(fetchInstalados, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch adjusted commissions to check for active breakpoint
  useEffect(() => {
    const fetchAdjustedCommissions = async () => {
      try {
        const data = await salesApi.getAdjustedCommissions('telecomunicacao');
        setAdjustedCommissions(data);
      } catch (error) {
        console.error('Error fetching adjusted commissions:', error);
      }
    };
    fetchAdjustedCommissions();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAdjustedCommissions, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch adjusted commissions for Segurança to check for active breakpoint
  useEffect(() => {
    const fetchAdjustedCommissionsSeguranca = async () => {
      try {
        const data = await salesApi.getAdjustedCommissions('seguranca');
        setAdjustedCommissionsSeguranca(data);
      } catch (error) {
        console.error('Error fetching adjusted commissions for Segurança:', error);
      }
    };
    fetchAdjustedCommissionsSeguranca();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAdjustedCommissionsSeguranca, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Segurança products sold count
  useEffect(() => {
    const fetchSegurancaProductsSold = async () => {
      try {
        const data = await salesApi.getCurrentMonthSegurancaProductsSold();
        setSegurancaProductsSold(data.count);
      } catch (error) {
        console.error('Error fetching Segurança products sold:', error);
      }
    };
    fetchSegurancaProductsSold();
    // Refresh every 30 seconds
    const interval = setInterval(fetchSegurancaProductsSold, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // Handler to open VendaModal for a specific lead (skipping customer step)
  const handleOpenVendaForLead = (lead: Lead) => {
    setSelectedLeadForVenda(lead);
    setVendaModalSkipCustomer(true);
    setVendaModalVisible(true);
  };
  
  // Handler to open VendaModal without a lead (start from scratch)
  const handleOpenVendaNew = () => {
    setSelectedLeadForVenda(null);
    setVendaModalSkipCustomer(false);
    setVendaModalVisible(true);
  };
  const [pdfViewerVisible, setPdfViewerVisible] = useState(() => {
    const saved = sessionStorage.getItem('recursos_pdfViewerVisible');
    return saved === 'true';
  });
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string>(() => {
    return sessionStorage.getItem('recursos_selectedPdfUrl') || '';
  });
  const [selectedPdfName, setSelectedPdfName] = useState<string>(() => {
    return sessionStorage.getItem('recursos_selectedPdfName') || '';
  });
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [loadingResources, setLoadingResources] = useState(false);
  const [resourceFolders, setResourceFolders] = useState<{
    telecomunicacao: Array<{ id: number; name: string; category: string; parent_folder_id: number | null; created_at: string; created_by: string }>;
    seguranca: Array<{ id: number; name: string; category: string; parent_folder_id: number | null; created_at: string; created_by: string }>;
    energia: Array<{ id: number; name: string; category: string; parent_folder_id: number | null; created_at: string; created_by: string }>;
  }>({
    telecomunicacao: [],
    seguranca: [],
    energia: []
  });
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderModalVisible, setFolderModalVisible] = useState(false);
  const [folderForm] = Form.useForm();
    const [pendenteReasonsChanged, setPendenteReasonsChanged] = useState<Set<number>>(new Set());
  const [creatingEvent, setCreatingEvent] = useState<number | null>(null);

      // Fidelizado subsection state
    const [endOfContractDates, setEndOfContractDates] = useState<Map<number, string>>(new Map());
    const [endOfContractDatesChanged, setEndOfContractDatesChanged] = useState<Set<number>>(new Set());

    // Não Atendeu subsection state - last contact attempt tracking
    const [lastContactAttempts, setLastContactAttempts] = useState<Map<number, string>>(new Map());
    const [lastContactAttemptsChanged, setLastContactAttemptsChanged] = useState<Set<number>>(new Set());
    const [contactAttemptCounts, setContactAttemptCounts] = useState<Map<number, number>>(new Map());
    const [contactAttemptCountsChanged, setContactAttemptCountsChanged] = useState<Set<number>>(new Set());

  // Event Modal state
  const [eventModalVisible, setEventModalVisible] = useState(false);
  const [selectedLeadForEvent, setSelectedLeadForEvent] = useState<(Lead & { title?: string; activity_type?: string }) | null>(null);

  // PDF Upload Modal state
  const [pdfUploadModalVisible, setPdfUploadModalVisible] = useState(false);
  const [selectedLeadForPdfUpload, setSelectedLeadForPdfUpload] = useState<Lead | null>(null);

  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [selectedLeadForEmail, setSelectedLeadForEmail] = useState<Lead | null>(null);

  // Chat Modal state - Disabled
  // const [chatModalVisible, setChatModalVisible] = useState(false);
  // const [selectedLeadForChat, setSelectedLeadForChat] = useState<Lead | null>(null);
  // const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);

  // Document Validation Modal state
  const [validarDocumentosModalVisible, setValidarDocumentosModalVisible] = useState(false);
  const [validationLeads, setValidationLeads] = useState<Lead[]>([]);
  const [loadingValidationLeads, setLoadingValidationLeads] = useState(false);
  const [validationRefreshTrigger, setValidationRefreshTrigger] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState<string>('');
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string>('');

  // Assignment functionality state
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);
  const [leadToAssign, setLeadToAssign] = useState<Lead | null>(null);

  // Track the last changed lead and its new status
  const [lastChangedLead, setLastChangedLead] = useState<{ id: number, newStatus: string | null } | null>(null);

  // Add staged section changes
  const [unsavedSectionChanges, setUnsavedSectionChanges] = useState<Map<number, string>>(new Map());

  // Add a state variable to track which lead to follow after save/revert
  const [leadToFollowId, setLeadToFollowId] = useState<number | null>(null);
  const urlLeadIdHandledRef = useRef<string | null>(null);

  // Add a state variable to highlight the latest changed lead
  const [highlightedLeadId, setHighlightedLeadId] = useState<number | null>(null);

  // User selection state for Team Leaders and up
  const [viewableUsers, setViewableUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Edit Lead Modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [editFormData, setEditFormData] = useState<any>({});
  const [savingEdit, setSavingEdit] = useState(false);

  // Notes Panel state
  const [notesPanelVisible, setNotesPanelVisible] = useState(false);
  const [selectedLeadForNotes, setSelectedLeadForNotes] = useState<Lead | null>(null);
  const [leadNotes, setLeadNotes] = useState<any[]>([]);

  // Highlighted notes state
  const [highlightedNotesMap, setHighlightedNotesMap] = useState<Map<number, any[]>>(new Map());

  // Track original status for special cases
  const [originalStatusForDisplay, setOriginalStatusForDisplay] = useState<Map<number, string>>(new Map());

  // Altice Residencial Modal state
  const [alticeResidencialModalVisible, setAlticeResidencialModalVisible] = useState(false);

  // Task Modal state
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [selectedLeadForTask, setSelectedLeadForTask] = useState<Lead | null>(null);

  // Check for lead following data from URL parameters (when coming from other screens)
  useEffect(() => {
    // Client Workspace owns ?leadId= in the URL when embedded; handling it here causes a navigate loop.
    if (workspaceEmbed) return;

    const searchParams = new URLSearchParams(location.search);
    const leadId = searchParams.get('leadId');
    const createEvent = searchParams.get('createEvent');

    if (!leadId || leadToFollowId !== null || urlLeadIdHandledRef.current === leadId) {
      return;
    }

    urlLeadIdHandledRef.current = leadId;
    setLeadToFollowId(parseInt(leadId, 10));

    if (createEvent === 'true') {
      localStorage.setItem('createEventForLead', leadId);
    }

    navigate('/client-management', { replace: true });
  }, [location.search, leadToFollowId, navigate, workspaceEmbed]);

  // Isolated follow-the-lead logic: useEffect
  useEffect(() => {
    // First, check if there's any lead following data in localStorage
    const storedLeadId = localStorage.getItem('leadToFollowId');
    if (storedLeadId && leadToFollowId === null) {
      setLeadToFollowId(parseInt(storedLeadId));
      localStorage.removeItem('leadToFollowId');
      localStorage.removeItem('leadToFollowSection');
      localStorage.removeItem('leadToFollowStatus');
      return; // Exit early, let the next render handle the lead following
    }

    if (leadToFollowId !== null) {
      const updatedLead = safeLeads.find(l => l.id === leadToFollowId);
      if (updatedLead) {
        let newSection: string = updatedLead.section || 'entrada';
        let subsectionKey: string = 'outros';
        if (newSection === 'entrada') {
          if (updatedLead.lead_status === 'Não Atribuido') subsectionKey = 'naoAtribuido';
          else if (updatedLead.lead_status === 'Não Atendeu') subsectionKey = 'naoAtendeu';
          else subsectionKey = 'outros';
        } else if (newSection === 'feedback') {
          const statuses = [
            { key: 'pendente', label: 'Pendente' },
            { key: 'followUp', label: 'Follow-up' },
            { key: 'semInteresse', label: 'Sem Interesse' },
            { key: 'fidelizado', label: 'Fidelizado' },
            { key: 'informacaoErrada', label: 'Informação Errada' },
            { key: 'semCobertura', label: 'Sem Cobertura' }
          ];
          const found = statuses.find(s => s.label === updatedLead.lead_status);
          subsectionKey = found ? found.key : 'outros';
        } else if (newSection === 'registro') {
          if (updatedLead.lead_status === 'Fecho') subsectionKey = 'fecho';
          else if (updatedLead.lead_status === 'Validação') subsectionKey = 'validacao';
          else subsectionKey = 'outros';
        }
        
        // Change section if it's different from current active section
        if (newSection !== activeSection) {
          setActiveSection(newSection);
          // When changing sections, start with just the target subsection
          setActivePanels([subsectionKey]);
        } else {
          // When staying in the same section, add the target subsection to existing panels
          setActivePanels(prev => {
            const newPanels = [...prev];
            if (!newPanels.includes(subsectionKey)) {
              newPanels.push(subsectionKey);
            }
            return newPanels;
          });
        }
        
        setHighlightedLeadId(updatedLead.id);
        setTimeout(() => setHighlightedLeadId(null), 3000);
        
        // Check if we need to create an event for this lead
        const createEventForLead = localStorage.getItem('createEventForLead');
        if (createEventForLead && parseInt(createEventForLead) === updatedLead.id) {
          // Clear the flag
          localStorage.removeItem('createEventForLead');
          
          // Create event data with pre-filled contact information
          const eventData = {
            ...updatedLead,
            title: `Contacto: ${updatedLead.name}`,
            activity_type: 'contact'
          } as Lead & { title: string; activity_type: string };
          
          setSelectedLeadForEvent(eventData);
          setEventModalVisible(true);
        }
        
        // Clear the leadToFollowId after processing
        setLeadToFollowId(null);
      }
      setLeadToFollowId(null);
    }
  }, [safeLeads, leadToFollowId, activeSection]);

  // Reset panels to collapsed when section or view changes, but preserve existing panels when following a lead
  useEffect(() => {
    // Only reset panels if we're not in the middle of following a lead
    // and if this is a manual section change (not triggered by lead following)
    if (leadToFollowId === null) {
      setActivePanels([]);
    }
  }, [viewMode]); // Removed activeSection dependency to prevent interference with lead following

  // Section configurations
  const sections: SectionConfig[] = [
    {
      key: 'entrada',
      label: 'Entrada',
      states: TELECOM_SECTIONS.entrada,
      color: '#6366f1' // Professional indigo
    },
    {
      key: 'feedback',
      label: 'Feedback',
      states: TELECOM_SECTIONS.feedback,
      color: '#10b981' // Professional emerald green
    },
    {
      key: 'registro',
      label: 'Registo e Documentos',
      states: TELECOM_SECTIONS.registro,
      color: '#8b5cf6' // Professional violet
    },
    {
      key: 'instalados',
      label: 'Instalados',
      states: ['Por Instalar', 'Instalados'],
      color: '#06b6d4' // Professional cyan
    }
  ];

  // Section colors map - accessible throughout component
  const sectionColors = {
    entrada: '#6366f1',      // Professional indigo
    feedback: '#10b981',     // Professional emerald green
    registro: '#8b5cf6',     // Professional violet
    instalados: '#06b6d4'    // Professional cyan
  };

  // Load pendente reasons from backend when leads are fetched
  useEffect(() => {
    const reasons = new Map<number, string>();
    safeLeads.forEach(lead => {
      if (lead.pendente_reason !== undefined && lead.pendente_reason !== null) {
        reasons.set(lead.id, lead.pendente_reason);
      }
    });
    setPendenteReasons(reasons);
  }, [safeLeads]);

  // Load followup notes from backend when leads are fetched
  useEffect(() => {
    const notes = new Map<number, string>();
    safeLeads.forEach(lead => {
      if (lead.followup_note !== undefined && lead.followup_note !== null) {
        notes.set(lead.id, lead.followup_note);
      }
    });
    setFollowUpNotes(notes);
  }, [safeLeads]);

  // Load seminteresse notes from backend when leads are fetched
  useEffect(() => {
    const notes = new Map<number, string>();
    safeLeads.forEach(lead => {
      if (lead.seminteresse_note !== undefined && lead.seminteresse_note !== null) {
        notes.set(lead.id, lead.seminteresse_note);
      }
    });
    setSeminteresseNotes(notes);
  }, [safeLeads]);

  // Load end of contract dates from backend when leads are fetched
  useEffect(() => {
    const dates = new Map<number, string>();
    safeLeads.forEach(lead => {
      if (lead.end_of_contract !== undefined && lead.end_of_contract !== null) {
        dates.set(lead.id, lead.end_of_contract);
      }
    });
    setEndOfContractDates(dates);
  }, [safeLeads]);

  // Load informacao errada notes from backend when leads are fetched
  useEffect(() => {
    const notes = new Map<number, string>();
    safeLeads.forEach(lead => {
      if (lead.informacao_errada_note !== undefined && lead.informacao_errada_note !== null) {
        notes.set(lead.id, lead.informacao_errada_note);
      }
    });
    setInformacaoErradaNotes(notes);
  }, [safeLeads]);

  // Load sem cobertura values from backend when leads are fetched
  useEffect(() => {
    const values = new Map<number, string>();
    safeLeads.forEach(lead => {
      if (lead.sem_cobertura_de !== undefined && lead.sem_cobertura_de !== null) {
        values.set(lead.id, lead.sem_cobertura_de);
      }
    });
    setSemCoberturaValues(values);
  }, [safeLeads]);

  // Load installation dates from backend when leads are fetched
  useEffect(() => {
    const dates = new Map<number, Map<string, string>>();
    safeLeads.forEach(lead => {
      // For backward compatibility, if installation_date exists and instalados_business_area is set,
      // store it under that business area
      if (lead.installation_date !== undefined && lead.installation_date !== null && (lead as any).instalados_business_area) {
        const businessArea = (lead as any).instalados_business_area;
        if (businessArea !== '1') {
          const leadDates = new Map<string, string>();
          leadDates.set(businessArea, lead.installation_date);
          dates.set(lead.id, leadDates);
        }
      }
      // Also check for instalados_installation_dates JSON field if it exists
      if ((lead as any).instalados_installation_dates) {
        try {
          const parsedDates = JSON.parse((lead as any).instalados_installation_dates);
          if (typeof parsedDates === 'object' && parsedDates !== null) {
            const leadDates = new Map<string, string>();
            Object.entries(parsedDates).forEach(([area, date]) => {
              if (date) leadDates.set(area, date as string);
            });
            if (leadDates.size > 0) {
              dates.set(lead.id, leadDates);
            }
          }
        } catch (e) {
          console.error('Error parsing instalados_installation_dates:', e);
        }
      }
    });
    setInstallationDates(dates);
  }, [safeLeads]);

  // Load address values from backend when leads are fetched
  useEffect(() => {
    const addressValues = new Map<number, string>();
    const cityValues = new Map<number, string>();
    const firstParts = new Map<number, string>();
    const secondParts = new Map<number, string>();
    
    safeLeads.forEach(lead => {
      if (lead.address !== undefined && lead.address !== null) {
        addressValues.set(lead.id, lead.address);
      }
      if (lead.city !== undefined && lead.city !== null) {
        cityValues.set(lead.id, lead.city);
      }
      if (lead.postal_code !== undefined && lead.postal_code !== null) {
        const [firstPart, secondPart] = lead.postal_code.split('-');
        firstParts.set(lead.id, firstPart || '');
        secondParts.set(lead.id, secondPart || '');
      }
    });
    setAddressValues(addressValues);
    setCityValues(cityValues);
    setPostalCodeFirstPart(firstParts);
    setPostalCodeSecondPart(secondParts);
  }, [safeLeads]);

  // Fetch leads and filter leads
  useEffect(() => {
    if (!isLoading && user) {
      fetchViewableUsers();
      fetchAvailableUsers();
      fetchLeads();
    }
  }, [user, isLoading]);

  // Refetch leads when selected user changes (standalone) or admin impersonation in workspace embed
  useEffect(() => {
    if (user) {
      fetchLeads();
    }
  }, [selectedUserId, workspaceViewAsUserId]);

  useEffect(() => {
    filterLeads();
  }, [safeLeads, activeSection, searchText, advancedFilters, showOwnLeadsOnly]);

  // Save state to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem('recursos_activeTab', activeResourceTab);
  }, [activeResourceTab]);

  useEffect(() => {
    sessionStorage.setItem('recursos_pdfViewerVisible', pdfViewerVisible.toString());
  }, [pdfViewerVisible]);

  useEffect(() => {
    if (selectedPdfUrl) {
      sessionStorage.setItem('recursos_selectedPdfUrl', selectedPdfUrl);
    } else {
      sessionStorage.removeItem('recursos_selectedPdfUrl');
    }
  }, [selectedPdfUrl]);

  useEffect(() => {
    if (selectedPdfName) {
      sessionStorage.setItem('recursos_selectedPdfName', selectedPdfName);
    } else {
      sessionStorage.removeItem('recursos_selectedPdfName');
    }
  }, [selectedPdfName]);

  // Fetch product resources and folders when drawer opens
  useEffect(() => {
    if (resourcesDrawerVisible) {
      fetchProductResources();
      fetchResourceFolders();
    }
  }, [resourcesDrawerVisible]);

  // Product Resources Management Functions
  const fetchProductResources = async () => {
    setLoadingResources(true);
    try {
      const response = await apiService.getProductResources();
      // Ensure folder_id is included (default to null if missing for backward compatibility)
      const normalizedResponse = {
        telecomunicacao: response.telecomunicacao.map(pdf => ({ ...pdf, folder_id: (pdf as any).folder_id ?? null })),
        seguranca: response.seguranca.map(pdf => ({ ...pdf, folder_id: (pdf as any).folder_id ?? null })),
        energia: response.energia.map(pdf => ({ ...pdf, folder_id: (pdf as any).folder_id ?? null }))
      };
      setProductPdfs(normalizedResponse);
    } catch (error) {
      console.error('Error fetching product resources:', error);
      message.error('Erro ao carregar recursos');
      // Set empty state on error
      setProductPdfs({
        telecomunicacao: [],
        seguranca: [],
        energia: []
      });
      setResourceFolders({
        telecomunicacao: [],
        seguranca: [],
        energia: []
      });
    } finally {
      setLoadingResources(false);
    }
  };

  const handleUploadProductPdf = async (category: string, file: File, folderId?: number | null) => {
    if (!user) return;
    
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      if (folderId !== null && folderId !== undefined && folderId !== 0) {
        formData.append('folder_id', folderId.toString());
      }
      
      const response = await apiService.uploadProductResource(formData);
      
      if (response.success) {
        message.success('PDF carregado com sucesso');
        await fetchProductResources();
        await fetchResourceFolders(); // Refresh folders to show updated counts
      } else {
        message.error(response.message || 'Erro ao carregar PDF');
      }
    } catch (error: any) {
      console.error('Error uploading PDF:', error);
      message.error(error.response?.data?.error || 'Erro ao carregar PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleUploadClick = (category: string) => {
    // Always show modal to select folder (even if no folders exist, user can upload to root)
    setPendingUpload({ category });
    setUploadFolderModalVisible(true);
  };

  const handleDeleteProductPdf = async (category: string, pdfId: number) => {
    if (!user) {
      message.error('Utilizador não autenticado');
      return;
    }
    
    try {
      const response = await apiService.deleteProductResource(pdfId);
      
      if (response.success) {
        message.success('PDF eliminado com sucesso');
        fetchProductResources();
      } else {
        message.error(response.error || response.message || 'Erro ao eliminar PDF');
      }
    } catch (error: any) {
      console.error('Error deleting PDF:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Erro ao eliminar PDF';
      message.error(errorMessage);
    }
  };

  // Resource Folders Management Functions
  const fetchResourceFolders = async () => {
    try {
      const response = await apiService.getResourceFolders();
      setResourceFolders(response);
    } catch (error) {
      console.error('Error fetching resource folders:', error);
      // Set empty state on error
      setResourceFolders({
        telecomunicacao: [],
        seguranca: [],
        energia: []
      });
    }
  };

  const handleCreateFolder = async (values: { name: string; category: string; parent_folder_id?: number | string }) => {
    if (!user) {
      message.error('Utilizador não autenticado');
      return;
    }

    setCreatingFolder(true);
    try {
      // Convert parent_folder_id to number or null
      let parentFolderId: number | null = null;
      if (values.parent_folder_id !== undefined && values.parent_folder_id !== null && values.parent_folder_id !== '') {
        const parsedId = typeof values.parent_folder_id === 'string' 
          ? parseInt(values.parent_folder_id, 10) 
          : values.parent_folder_id;
        if (!isNaN(parsedId) && parsedId > 0) {
          parentFolderId = parsedId;
        }
      }

      const response = await apiService.createResourceFolder(
        values.name,
        values.category,
        parentFolderId
      );

      if (response.success) {
        message.success('Pasta criada com sucesso');
        setFolderModalVisible(false);
        folderForm.resetFields();
        fetchResourceFolders();
      } else {
        message.error(response.error || response.message || 'Erro ao criar pasta');
      }
    } catch (error: any) {
      console.error('Error creating folder:', error);
      message.error(error.response?.data?.error || 'Erro ao criar pasta');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDeleteFolder = async (category: string, folderId: number) => {
    if (!user) {
      message.error('Utilizador não autenticado');
      return;
    }
    
    try {
      const response = await apiService.deleteResourceFolder(folderId);
      
      if (response.success) {
        message.success('Pasta eliminada com sucesso');
        fetchResourceFolders();
        fetchProductResources();
      } else {
        message.error(response.error || response.message || 'Erro ao eliminar pasta');
      }
    } catch (error: any) {
      console.error('Error deleting folder:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Erro ao eliminar pasta';
      message.error(errorMessage);
    }
  };

  // Helper function to get the full path of a folder (e.g., "Child Folder (Parent Folder)" or "Grandchild (Child > Parent)")
  const getFolderPath = (
    folder: { id: number; name: string; parent_folder_id: number | null },
    allFolders: Array<{ id: number; name: string; parent_folder_id: number | null }>
  ): string => {
    if (!folder.parent_folder_id) {
      return folder.name;
    }
    
    // Build the path from root to this folder
    const pathParts: string[] = [];
    let currentFolder: { id: number; name: string; parent_folder_id: number | null } | undefined = folder;
    
    // Traverse up the hierarchy to build the path
    while (currentFolder && currentFolder.parent_folder_id) {
      const parent = allFolders.find(f => f.id === currentFolder!.parent_folder_id);
      if (parent) {
        pathParts.unshift(parent.name); // Add parent name at the beginning
        currentFolder = parent;
      } else {
        break;
      }
    }
    
    // If we have path parts, show them in parentheses
    if (pathParts.length > 0) {
      return `${folder.name} (${pathParts.join(' > ')})`;
    }
    
    return folder.name;
  };

  // Helper function to count all PDFs in a folder and its subfolders recursively
  const countTotalPdfsInFolder = (
    folderId: number,
    allFolders: Array<{ id: number; name: string; category: string; parent_folder_id: number | null; created_at: string; created_by: string }>,
    categoryPdfs: Array<any>
  ): number => {
    // Count PDFs directly in this folder
    const directPdfs = categoryPdfs.filter(pdf => {
      const pdfFolderId = pdf.folder_id !== null && pdf.folder_id !== undefined ? Number(pdf.folder_id) : null;
      return pdfFolderId === folderId;
    }).length;

    // Get subfolders
    const subfolders = allFolders.filter(f => f.parent_folder_id === folderId);
    
    // Recursively count PDFs in subfolders
    const subfolderPdfs = subfolders.reduce((total, subfolder) => {
      return total + countTotalPdfsInFolder(subfolder.id, allFolders, categoryPdfs);
    }, 0);

    return directPdfs + subfolderPdfs;
  };

  // Helper function to render a folder with its subfolders recursively
  const renderFolderWithSubfolders = (
    folder: { id: number; name: string; category: string; parent_folder_id: number | null; created_at: string; created_by: string },
    category: 'telecomunicacao' | 'seguranca' | 'energia',
    allFolders: Array<{ id: number; name: string; category: string; parent_folder_id: number | null; created_at: string; created_by: string }>,
    categoryPdfs: Array<any>
  ) => {
    const folderPdfs = categoryPdfs.filter(pdf => {
      const pdfFolderId = pdf.folder_id !== null && pdf.folder_id !== undefined ? Number(pdf.folder_id) : null;
      return pdfFolderId === folder.id;
    });

    // Get total PDF count including subfolders
    const totalPdfCount = countTotalPdfsInFolder(folder.id, allFolders, categoryPdfs);

    // Get subfolders (folders that have this folder as parent)
    const subfolders = allFolders.filter(f => f.parent_folder_id === folder.id);

    return (
      <Panel
        key={folder.id}
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
            <FolderOutlined style={{ fontSize: '18px', color: '#6366f1' }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: '#ffffff', fontWeight: 500, fontSize: '14px' }}>{folder.name}</div>
              <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginTop: '4px' }}>
                {totalPdfCount} {totalPdfCount === 1 ? 'PDF' : 'PDFs'} • Criado por {folder.created_by} em {new Date(folder.created_at).toLocaleDateString('pt-PT')}
              </div>
            </div>
            {user && user.role && canCreateFolders(user.role) && (
              <Popconfirm
                title={
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>Eliminar Pasta</div>
                    <div>Tem a certeza que deseja eliminar esta pasta? Esta ação não pode ser desfeita.</div>
                  </div>
                }
                onConfirm={() => handleDeleteFolder(category, folder.id)}
                okText="Sim"
                cancelText="Não"
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: '#ff4d4f', flexShrink: 0 }}
                />
              </Popconfirm>
            )}
          </div>
        }
        style={{
          background: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '8px',
          marginBottom: '12px'
        }}
      >
        {/* Render subfolders if any */}
        {subfolders.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600, marginBottom: '12px', fontSize: '13px', paddingLeft: '8px' }}>
              Subpastas
            </div>
            <Collapse ghost>
              {subfolders.map((subfolder) => renderFolderWithSubfolders(subfolder, category, allFolders, categoryPdfs))}
            </Collapse>
          </div>
        )}

        {/* Render PDFs in this folder */}
        {folderPdfs.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
            Nenhum PDF nesta pasta
          </div>
        ) : (
          <Collapse ghost>
            {folderPdfs.map((item) => (
              <Panel
                key={item.id.toString()}
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', minWidth: 0 }}>
                    <FilePdfIcon style={{ fontSize: '20px', color: '#ffffff', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <div style={{ color: '#ffffff', fontWeight: 600, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name.length > 30 ? item.name.substring(0, 30) + '...' : item.name}</div>
                      <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        Carregado por {item.uploaded_by} em {new Date(item.uploaded_at).toLocaleDateString('pt-PT')}
                      </div>
                    </div>
                    {user && user.role && isAdminOrDCOrTeamLeader(user.role) && (
                      <Popconfirm
                        title={
                          <div>
                            <div style={{ fontWeight: 600, marginBottom: '8px' }}>Eliminar PDF</div>
                            <div>Tem a certeza que deseja eliminar este PDF?</div>
                          </div>
                        }
                        onConfirm={() => handleDeleteProductPdf(category, item.id)}
                        okText="Sim"
                        cancelText="Não"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: '#ff4d4f', flexShrink: 0 }}
                        />
                      </Popconfirm>
                    )}
                  </div>
                }
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}
              >
                <PdfViewerPanel item={item} />
              </Panel>
            ))}
          </Collapse>
        )}
      </Panel>
    );
  };

  // Helper component to render PDF in collapse panel
  const PdfViewerPanel: React.FC<{ item: any }> = ({ item }) => {
    const [pdfBlobUrl, setPdfBlobUrl] = React.useState<string>('');
    const [loadingPdf, setLoadingPdf] = React.useState(false);
    const hasLoadedRef = React.useRef(false);

    // Auto-load PDF when component mounts (panel is expanded) - only once
    React.useEffect(() => {
      // Prevent multiple loads
      if (hasLoadedRef.current || pdfBlobUrl) return;
      
      const loadPdf = async () => {
        hasLoadedRef.current = true;
        setLoadingPdf(true);
        try {
          const token = localStorage.getItem('jwt_token');
          if (!token) {
            message.error('Token de autenticação não encontrado');
            setLoadingPdf(false);
            hasLoadedRef.current = false; // Allow retry
            return;
          }
          
          const apiBaseUrl = window.location.protocol + '//' + window.location.host;
          const fullUrl = item.url.startsWith('http') ? item.url : `${apiBaseUrl}${item.url}`;
          
          const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            credentials: 'include'
          });
          
          if (!response.ok) {
            throw new Error('Erro ao carregar PDF');
          }
          
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          setPdfBlobUrl(blobUrl);
        } catch (error: any) {
          console.error('Error loading PDF:', error);
          message.error('Erro ao carregar PDF');
          hasLoadedRef.current = false; // Allow retry on error
        } finally {
          setLoadingPdf(false);
        }
      };

      loadPdf();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Cleanup blob URL on unmount
    React.useEffect(() => {
      return () => {
        if (pdfBlobUrl) {
          URL.revokeObjectURL(pdfBlobUrl);
        }
        hasLoadedRef.current = false; // Reset on unmount
      };
    }, [pdfBlobUrl]);

    const handleRetryLoad = async () => {
      hasLoadedRef.current = false;
      setLoadingPdf(true);
      try {
        const token = localStorage.getItem('jwt_token');
        if (!token) {
          message.error('Token de autenticação não encontrado');
          setLoadingPdf(false);
          return;
        }
        
        const apiBaseUrl = window.location.protocol + '//' + window.location.host;
        const fullUrl = item.url.startsWith('http') ? item.url : `${apiBaseUrl}${item.url}`;
        
        const response = await fetch(fullUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Erro ao carregar PDF');
        }
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(blobUrl);
        hasLoadedRef.current = true;
      } catch (error: any) {
        console.error('Error loading PDF:', error);
        message.error('Erro ao carregar PDF');
      } finally {
        setLoadingPdf(false);
      }
    };

    return (
      <div style={{ width: '100%', height: '600px', marginTop: '16px' }}>
        {loadingPdf ? (
          <GhostLoader />
        ) : pdfBlobUrl ? (
          <object
            data={`${pdfBlobUrl}#toolbar=0&navpanes=0`}
            type="application/pdf"
            style={{
              width: '100%',
              height: '100%',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px'
            }}
          >
            <iframe
              src={`${pdfBlobUrl}#toolbar=0&navpanes=0`}
              style={{
                width: '100%',
                height: '100%',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px'
              }}
              title={item.name}
            />
          </object>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'rgba(255, 255, 255, 0.7)' }}>
            <Button type="primary" onClick={handleRetryLoad}>
              Carregar PDF
            </Button>
          </div>
        )}
      </div>
    );
  };

  const handleViewPdf = async (pdfUrl: string, pdfName: string) => {
    try {
      // Extract resource ID from URL (format: /api/product-resources/{id}/download)
      const match = pdfUrl.match(/\/api\/product-resources\/(\d+)\/download/);
      if (!match) {
        message.error('URL inválida para visualização do PDF');
        return;
      }
      
      const resourceId = parseInt(match[1]);
      
      // Fetch PDF as blob using the api service to ensure proper authentication
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        message.error('Token de autenticação não encontrado');
        return;
      }
      
      const apiBaseUrl = window.location.protocol + '//' + window.location.host;
      const fullUrl = pdfUrl.startsWith('http') ? pdfUrl : `${apiBaseUrl}${pdfUrl}`;
      
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF fetch error:', response.status, errorText);
        throw new Error(`Erro ao carregar PDF: ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      setSelectedPdfUrl(blobUrl);
      setSelectedPdfName(pdfName);
      setPdfViewerVisible(true);
    } catch (error: any) {
      console.error('Error loading PDF:', error);
      message.error(error.message || 'Erro ao carregar PDF para visualização');
    }
  };

  const fetchViewableUsers = async () => {
    // Only fetch viewable users for Team Leaders and up
    if (!user || !user.role || !hasRole(user, ['Admin', 'Diretor Comercial', 'Team Leader', 'team_leader', 'Gestor de Equipa'])) {
      return;
    }

    try {
      setLoadingUsers(true);
      const users = await apiService.getViewableUsers();
      setViewableUsers(users);
      
      // Set current user as default selection
      if (users.length > 0) {
        setSelectedUserId(user.id);
      }
    } catch (error) {
      message.error('Erro ao carregar utilizadores');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAvailableUsers = async () => {
    if (!user) return;
    try {
      const assignableUsers = await apiService.getAssignableUsers();
      setAvailableUsers(assignableUsers);
    } catch (error) {
      message.error('Erro ao carregar utilizadores disponíveis.');
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const businessArea = user?.business_area || 'telecommunications';
      
      // For team leaders: if they select themselves, show all team leads
      // For other users: show only their own leads
      let targetUserId;
      if (workspaceEmbed && workspaceViewAsUserId !== undefined) {
        targetUserId = workspaceViewAsUserId;
      } else if (selectedUserId === null) {
        // No user selected - show leads based on user role
        if (user && (user.role === 'admin' || user.role === 'Admin' || 
                     user.role === 'Diretor Comercial' || user.role === 'diretor comercial')) {
          // Admin/Director - show all leads (no user_id parameter)
          targetUserId = undefined;
        } else {
          // Regular users and team leaders - show their own leads
          targetUserId = user?.id;
        }
      } else if (selectedUserId === user?.id && 
                 user && (user.role === 'team_leader' || user.role === 'Team Leader' || 
                         user.role === 'admin' || user.role === 'Admin' || 
                         user.role === 'Diretor Comercial')) {
        // Team leader/admin/director selecting themselves - show all team leads
        targetUserId = undefined;
      } else {
        // Specific user selected - show only that user's leads
        targetUserId = selectedUserId;
      }
  
      const leadsData = await apiService.getLeads(businessArea, targetUserId);
      
      // Backend now handles role-based filtering, so we can use all returned leads
      
      // Ensure leadsData is always an array and maintain backend ordering
      const leadsArray = Array.isArray(leadsData) ? leadsData : [];
      // Add a display order field to maintain backend ordering
      const leadsWithOrder = leadsArray.map((lead, index) => ({
        ...lead,
        _displayOrder: index // Maintain the order from backend
      }));
      setLeads(leadsWithOrder);
      
      // Initialize instalados business area and selected products from backend
      const instaladosAreaMap = new Map<number, string>();
      const instaladosProductsMap = new Map<number, number[]>();
      leadsWithOrder.forEach(lead => {
        if ((lead as any).instalados_business_area) {
          instaladosAreaMap.set(lead.id, (lead as any).instalados_business_area);
        }
        if ((lead as any).instalados_selected_products) {
          try {
            const products = JSON.parse((lead as any).instalados_selected_products);
            if (Array.isArray(products)) {
              instaladosProductsMap.set(lead.id, products);
            }
          } catch (e) {
            console.error('Error parsing instalados_selected_products:', e);
          }
        }
      });
      setInstaladosBusinessArea(instaladosAreaMap);
      setInstaladosSelectedProducts(instaladosProductsMap);
      
      // Initialize lastContactAttempts with data from backend
      const lastContactAttemptsMap = new Map<number, string>();
      const lastContactDatesMap = new Map<number, string>();
      const contactAttemptCountsMap = new Map<number, number>();
      leadsArray.forEach(lead => {
        if (lead.last_contact_attempt) {
          lastContactAttemptsMap.set(lead.id, lead.last_contact_attempt);
        }
        if (lead.last_contact_date) {
          lastContactDatesMap.set(lead.id, lead.last_contact_date);
        }
        if (lead.contact_attempt_count !== undefined && lead.contact_attempt_count !== null) {
          contactAttemptCountsMap.set(lead.id, lead.contact_attempt_count);
        }
      });
      setLastContactAttempts(lastContactAttemptsMap);
      setLastContactDates(lastContactDatesMap);
      setContactAttemptCounts(contactAttemptCountsMap);



    } catch (error) {
      message.error('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchHighlightedNotesForLeads = async (leads: Lead[]) => {
    try {
      const highlightedNotesMap = new Map<number, any[]>();
      
      // Fetch highlighted notes for each lead
      for (const lead of leads) {
        try {
          const response = await apiService.getHighlightedNotes(lead.id);
          if (response.highlighted_notes && response.highlighted_notes.length > 0) {
            highlightedNotesMap.set(lead.id, response.highlighted_notes);
          }
        } catch (error) {
          // Handle error silently
        }
      }
      
      setHighlightedNotesMap(highlightedNotesMap);
    } catch (error) {
      // Handle error silently
    }
  };

  const fetchHighlightedNotesForVisibleLeads = async (subsectionKey: string, page: number) => {
    try {
      const sectionLeads = filteredLeads;
      let visibleLeads: Lead[] = [];
      
      // Get the visible leads based on the active section and subsection
      if (activeSection === 'entrada') {
        const groupedLeads = groupEntradaLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
        const group = groupedLeads.find(g => g.key === subsectionKey);
        if (group) {
          visibleLeads = group.leads;
        }
      } else if (activeSection === 'feedback') {
        const groupedLeads = groupFeedbackLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
        const group = groupedLeads.find(g => g.key === subsectionKey);
        if (group) {
          visibleLeads = group.leads;
        }
      } else if (activeSection === 'registro') {
        const groupedLeads = groupRegistroLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
        const group = groupedLeads.find(g => g.key === subsectionKey);
        if (group) {
          visibleLeads = group.leads;
        }
      } else if (activeSection === 'instalados') {
        const groupedLeads = groupInstaladosLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
        const group = groupedLeads.find(g => g.key === subsectionKey);
        if (group) {
          visibleLeads = group.leads;
        }
      }
      
      // Fetch highlighted notes only for visible leads
      if (visibleLeads.length > 0) {
        await fetchHighlightedNotesForLeads(visibleLeads);
      }
    } catch (error) {
      // Handle error silently
    }
  };

  // Helper to get previous section
  function getPreviousSection(section: string): string | null {
    if (section === 'feedback') return 'entrada';
    if (section === 'registro') return 'feedback';
    if (section === 'instalados') return 'registro';
    return null;
  }

  // Assignment functions
  const handleAssignLead = (lead: Lead) => {
    setLeadToAssign(lead);
    setAssignmentModalVisible(true);
  };

  const handleAssignmentConfirm = async (assignedTo: number) => {
    if (!leadToAssign) return;
    
    try {
      await apiService.updateLead(leadToAssign.id, { assigned_to: assignedTo });
      message.success('Lead atribuído com sucesso!');
      setAssignmentModalVisible(false);
      setLeadToAssign(null);
      fetchLeads(); // Refresh the leads list
    } catch (error) {
      message.error('Erro ao atribuir lead.');
    }
  };

  const handleAssignmentCancel = () => {
    setAssignmentModalVisible(false);
    setLeadToAssign(null);
  };

      // --- Edit getSectionColumns to allow 'Atendeu' in Entrada ---
  
  // Helper function to get button colors based on section and action type
  const getButtonColors = (actionType: string, isPrimary: boolean = false) => {
    const actionColors: { [key: string]: { bg: string; border: string; text: string; hover?: string } } = {
      // Standard actions - consistent across all sections
      view: { bg: 'transparent', border: 'transparent', text: '#60a5fa' }, // Light blue
      notes: { bg: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', border: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', text: '#ffffff' },
      task: { bg: 'transparent', border: 'transparent', text: '#f59e0b' }, // Amber
      email: { bg: 'transparent', border: 'transparent', text: '#6366f1' }, // Indigo
      edit: { bg: 'transparent', border: 'transparent', text: '#8b5cf6' }, // Purple
      revert: { bg: '#ef4444', border: '#ef4444', text: '#ffffff', hover: '#dc2626' }, // Red
      // Section-specific primary actions
      createEvent: { bg: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', border: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', text: '#ffffff' },
      // Document generation - brand colors
      vodafone: { bg: '#e60000', border: '#e60000', text: '#ffffff' },
      meo: { bg: '#ff6b00', border: '#ff6b00', text: '#ffffff' },
      nos: { bg: '#00a651', border: '#00a651', text: '#ffffff' },
      // Approval actions
      approve: { bg: '#10b981', border: '#10b981', text: '#ffffff' }, // Green
      reject: { bg: '#ef4444', border: '#ef4444', text: '#ffffff' }, // Red
      // Utility buttons
      save: { bg: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', border: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', text: '#ffffff' },
      apply: { bg: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', border: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', text: '#ffffff' },
      clear: { bg: 'rgba(255, 255, 255, 0.1)', border: 'rgba(255, 255, 255, 0.3)', text: '#ffffff' },
      collapse: { bg: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', border: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', text: '#ffffff' },
      // View mode buttons
      viewMode: { bg: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', border: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', text: '#ffffff' },
      viewModeInactive: { bg: 'rgba(255, 255, 255, 0.1)', border: 'rgba(255, 255, 255, 0.3)', text: '#ffffff' },
      // My Leads button
      myLeads: { bg: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', border: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', text: '#ffffff' },
      myLeadsInactive: { bg: 'rgba(255, 255, 255, 0.1)', border: 'rgba(255, 255, 255, 0.3)', text: '#ffffff' },
      // Cobertura button
      cobertura: { bg: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', border: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', text: '#ffffff' },
      // Filter button
      filter: { bg: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', border: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', text: '#ffffff' },
      // PDF management
      pdf: { bg: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', border: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', text: '#ffffff' }
    };
    
    if (workspaceEmbed && actionType === 'collapse') {
      return {
        bg: '#ffffff',
        border: 'rgba(15, 23, 42, 0.12)',
        text: '#475569',
      };
    }
    return actionColors[actionType] || { bg: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', border: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1', text: '#ffffff' };
  };

  // Radio button component for Fecho document mode
  const FechoDocumentModeRadio = ({ leadId, value, onChange, viewMode = 'grid' }: { leadId: number; value: string; onChange: (value: string) => void; viewMode?: 'grid' | 'kanban' }) => {
    const uniqueName = `fecho-document-mode-${leadId}`;
    return (
      <div className={`fecho-radio-input ${viewMode === 'grid' ? 'table-view' : 'kanban-view'}`}>
        <label>
          <input 
            type="radio" 
            id={`fecho-mode-${leadId}-1`} 
            name={uniqueName} 
            value="1" 
            checked={value === '1'}
            onChange={(e) => onChange(e.target.value)}
          />
          <span>1</span>
        </label>
        <label>
          <input 
            type="radio" 
            id={`fecho-mode-${leadId}-2`} 
            name={uniqueName} 
            value="2" 
            checked={value === '2'}
            onChange={(e) => onChange(e.target.value)}
          />
          <span>2</span>
        </label>
        <label>
          <input 
            type="radio" 
            id={`fecho-mode-${leadId}-3`} 
            name={uniqueName} 
            value="3" 
            checked={value === '3'}
            onChange={(e) => onChange(e.target.value)}
          />
          <span>3</span>
        </label>
        <label>
          <input 
            type="radio" 
            id={`fecho-mode-${leadId}-4`} 
            name={uniqueName} 
            value="4" 
            checked={value === '4'}
            onChange={(e) => onChange(e.target.value)}
          />
          <span>4</span>
        </label>
        <span className="selection" />
      </div>
    );
  };

  // Radio button component for Instalados business area selection
  const InstaladosBusinessAreaRadio = ({ leadId, value, onChange, viewMode = 'grid' }: { leadId: number; value: string; onChange: (value: string) => void; viewMode?: 'grid' | 'kanban' }) => {
    const uniqueName = `instalados-business-area-${leadId}`;
    return (
      <div className={`fecho-radio-input ${viewMode === 'grid' ? 'table-view' : 'kanban-view'}`}>
        <label>
          <input 
            type="radio" 
            id={`instalados-area-${leadId}-1`} 
            name={uniqueName} 
            value="1" 
            checked={value === '1'}
            onChange={(e) => onChange(e.target.value)}
          />
          <span>1</span>
        </label>
        <label>
          <input 
            type="radio" 
            id={`instalados-area-${leadId}-2`} 
            name={uniqueName} 
            value="2" 
            checked={value === '2'}
            onChange={(e) => onChange(e.target.value)}
          />
          <span>2</span>
        </label>
        <label>
          <input 
            type="radio" 
            id={`instalados-area-${leadId}-3`} 
            name={uniqueName} 
            value="3" 
            checked={value === '3'}
            onChange={(e) => onChange(e.target.value)}
          />
          <span>3</span>
        </label>
        <label>
          <input 
            type="radio" 
            id={`instalados-area-${leadId}-4`} 
            name={uniqueName} 
            value="4" 
            checked={value === '4'}
            onChange={(e) => onChange(e.target.value)}
          />
          <span>4</span>
        </label>
        <span className="selection" />
      </div>
    );
  };

    const getSectionColumns = (groupKey?: string) => {

    const baseColumns = [
      {
        title: 'Nome',
        dataIndex: 'name',
        key: 'name',
        render: (name: string, record: Lead) => (
          <div>
            <Space>
              <span style={getLeadCategoryStyle(record.lead_category)}>{name}</span>
              {/* Show user icon for high rank users' own leads */}
              {user && (user.role === 'team_leader' || user.role === 'Team Leader' || user.role === 'admin' || user.role === 'Admin' || user.role === 'super_admin' || user.role === 'Diretor Comercial' || user.role === 'Gestor de Equipa') && 
               record.assigned_to === user.id && (
                <span style={{ fontSize: '14px' }}>🌸</span>
              )}
              {/* Show star icon for leads with highlighted notes */}
              {(() => {
                const highlightedNotes = highlightedNotesMap.get(record.id);
                return highlightedNotes && highlightedNotes.length > 0;
              })() && (
                <StarFilled style={{ color: '#faad14', fontSize: '14px' }} />
              )}
            </Space>
          </div>
        ),
        sorter: (a: Lead, b: Lead) => a.name.localeCompare(b.name)
      },
      {
        title: 'Contacto',
        dataIndex: 'phone',
        key: 'phone',
        render: (text: string, record: Lead) => (
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.phone || '-'}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
              NIF: {record.dif || '-'}
            </div>
          </div>
        )
      },
      {
        title: 'Status',
        dataIndex: 'lead_status',
        key: 'status',
        render: (status: string, record: Lead) => {
          // Entrada: allow 'Atendeu' as an option
          // Feedback: allow 'Fecho' as an option
          // Registro: allow 'Fecho' and 'Validação' as options
          let statusOptions = sections.find(s => s.key === activeSection)?.states || [];
          if (activeSection === 'entrada' && !statusOptions.includes('Atendeu')) {
            statusOptions = [...statusOptions, 'Atendeu'];
          }
          if (activeSection === 'feedback' && !statusOptions.includes('Fecho')) {
            statusOptions = [...statusOptions, 'Fecho'];
          }
          if (activeSection === 'registro') {
            const registroStatuses = ['Fecho', 'Validação'];
            registroStatuses.forEach(status => {
              if (!statusOptions.includes(status)) {
                statusOptions = [...statusOptions, status];
              }
            });
          }
          if (activeSection === 'instalados') {
            const instaladosStatuses = ['Por Instalar', 'Instalados'];
            instaladosStatuses.forEach(status => {
              if (!statusOptions.includes(status)) {
                statusOptions = [...statusOptions, status];
              }
            });
          }
          return (
            <Select
              value={unsavedChanges.has(record.id) ? unsavedChanges.get(record.id) || undefined : record.lead_status || undefined}
              className="modern-status-select"
              style={{ width: 140, minWidth: 140 }}
              size="small"
              placeholder="Selecione o estado..."
              onChange={(value) => handleStatusChange(record.id, value)}
              allowClear
            >
              {statusOptions.map(state => (
                <Option key={state} value={state}>
                  {state}
                </Option>
              ))}
            </Select>
          );
        }
      },
      {
        title: 'Ações',
        key: 'actions',
        render: (text: string, record: Lead) => (
          <Space>
            <Tooltip title={workspaceEmbed && onOpenLeadInWorkspace ? 'Abrir no workspace de cliente' : 'Ver detalhes'}>
              <Button 
                type="text" 
                icon={<EyeOutlined />} 
                size="small"
                onClick={() => handleViewLead(record)}
                className="action-btn-view"
                style={{ 
                  color: getButtonColors('view').text,
                  borderColor: 'transparent'
                }}
              />
            </Tooltip>
            <Tooltip title="Notas">
              <Button 
                type="primary" 
                icon={<PaperClipOutlined />} 
                size="small"
                onClick={() => handleOpenNotes(record)}
                className="action-btn-notes"
                style={{ 
                  fontSize: '11px', 
                  height: '24px',
                  '--section-color': getButtonColors('notes').bg
                } as React.CSSProperties & { '--section-color': string }}
              />
            </Tooltip>
            <Tooltip title="Criar Tarefa">
              <Button 
                type="text" 
                icon={<CrownOutlined />} 
                size="small"
                onClick={() => handleOpenTaskModal(record)}
                className="action-btn-task"
                style={{ 
                  color: getButtonColors('task').text,
                  borderColor: 'transparent'
                }}
              />
            </Tooltip>
            {userMayUseClientManagementEmailButton(user) && (
              <Tooltip title="Enviar Email">
                <Button
                  type="text"
                  icon={<MailOutlined />}
                  size="small"
                  onClick={() => handleSendEmail(record)}
                  className="action-btn-email"
                  style={{
                    color: getButtonColors('email').text,
                    borderColor: 'transparent',
                  }}
                />
              </Tooltip>
            )}
            {user?.role && isAdminOrDCOrTeamLeader(user.role) && record.assigned_to && record.assigned_to !== user.id && (
              <Tooltip title="Mensagem ao Responsável">
                {/* Chat button disabled */}
                {/* <Button 
                  type="text" 
                  icon={<MessageOutlined />} 
                  size="small"
                  onClick={() => handleMessageLead(record)}
                  style={{ color: '#52c41a' }}
                /> */}
              </Tooltip>
            )}
            {(['feedback', 'registro'].includes(activeSection) || (activeSection === 'entrada' && record.lead_status === 'Não Atendeu')) && (
              <Button 
                size="small" 
                onClick={() => handleRevertLead(record)}
                data-action="revert"
                className="action-btn-revert"
              >
                Reverter
              </Button>
            )}
            <Tooltip title="Nova Venda">
              <Button 
                type="primary" 
                icon={<ShoppingCartOutlined />} 
                size="small"
                onClick={() => handleOpenVendaForLead(record)}
                style={{ 
                  fontSize: '11px', 
                  height: '24px',
                  backgroundColor: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1',
                  borderColor: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1'
                }}
              >
                Venda
              </Button>
            </Tooltip>
          </Space>
        )
      }
    ];

    // Add pendente-specific columns for feedback section
    if (activeSection === 'feedback') {
      // Only add columns for specific subsections, not for 'outros'
      if (groupKey === 'pendente') {
        baseColumns.splice(2, 0, {
          title: 'Motivo',
          key: 'pendente_reason',
          render: (text: string, record: Lead) => {
            return (
              <Input.TextArea
                placeholder="Escreva o motivo do estado pendente..."
                value={pendenteReasons.get(record.id) || ''}
                onChange={(e) => handlePendenteReasonChange(record.id, e.target.value)}
                style={{ fontSize: '11px', minHeight: '60px' }}
                required
              />
            );
          }
        });

        baseColumns.splice(3, 0, {
          title: 'Criar Evento',
          key: 'create_event',
          render: (text: string, record: Lead) => {
            const reason = pendenteReasons.get(record.id);
            const hasReason = reason && reason.trim().length > 0;
            return (
              <Button 
                type="primary" 
                size="small" 
                onClick={() => handleCreateEventForPendente(record)}
                disabled={!hasReason || creatingEvent === record.id}
                loading={creatingEvent === record.id}
                className="action-btn-create-event"
                style={{ 
                  fontSize: '11px', 
                  height: '24px',
                  '--section-color': getButtonColors('createEvent').bg
                } as React.CSSProperties & { '--section-color': string }}
                title={!hasReason ? "Deve escrever o motivo pendente primeiro" : "Criar evento de follow-up"}
              >
                Criar Evento
              </Button>
            );
          }
        });
      }

      // Add Fidelizado-specific column for end_of_contract
      if (groupKey === 'fidelizado') {
        baseColumns.splice(2, 0, {
          title: 'Fim da fidelização',
          key: 'end_of_contract',
          render: (text: string, record: Lead) => {
            return (
              <Input
                type="date"
                value={endOfContractDates.get(record.id) || ''}
                onChange={(e) => handleEndOfContractChange(record.id, e.target.value)}
                style={{ fontSize: '11px' }}
                required
              />
            );
          }
        });
      }
    }

    if (activeSection === 'registro') {
      // Cobertura column removed from Fecho subsection
      
      // Add Documentos Assinados column for Validação group
      if (groupKey === 'validacao') {
        baseColumns.splice(2, 0, {
          title: 'Documentos Assinados',
          key: 'documentos_assinados',
          render: (text: string, record: Lead) => (
            <Button
              type="primary"
              size="small"
              onClick={() => handlePdfUpload(record)}
              className="action-btn-pdf"
              style={{ 
                fontSize: '11px', 
                height: '24px',
                '--section-color': getButtonColors('pdf').bg
              } as React.CSSProperties & { '--section-color': string }}
              icon={<FilePdfOutlined />}
            >
              Gerir PDFs
            </Button>
          )
        });
      }
      
      // Gerar Documentos column now at index 3 (but NOT for validacao subsection)
      if (groupKey !== 'validacao') {
        baseColumns.splice(3, 0, {
          title: 'Gerar Documentos',
          key: 'documents',
          render: (text: string, record: Lead) => {
            const documentMode = getFechoDocumentMode(record.id);
            return (
              <div>
                {groupKey === 'fecho' && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                    <FechoDocumentModeRadio 
                      leadId={record.id} 
                      value={documentMode} 
                      onChange={(value) => handleFechoDocumentModeChange(record.id, value)} 
                      viewMode="grid" 
                    />
                  </div>
                )}
                <Space direction="vertical" size="small">
                  {groupKey === 'fecho' && documentMode === '1' ? (
                    <>
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => handleFechoDocumentModeChange(record.id, '2')}
                        style={{ 
                          fontSize: '11px', 
                          height: '24px',
                          width: '100%',
                          backgroundColor: '#722ed1',
                          borderColor: '#722ed1',
                          color: '#ffffff'
                        }}
                      >
                        Telecomunicação
                      </Button>
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => handleFechoDocumentModeChange(record.id, '3')}
                        style={{ 
                          fontSize: '11px', 
                          height: '24px',
                          width: '100%',
                          backgroundColor: '#722ed1',
                          borderColor: '#722ed1',
                          color: '#ffffff'
                        }}
                      >
                        Segurança
                      </Button>
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => handleFechoDocumentModeChange(record.id, '4')}
                        style={{ 
                          fontSize: '11px', 
                          height: '24px',
                          width: '100%',
                          backgroundColor: '#722ed1',
                          borderColor: '#722ed1',
                          color: '#ffffff'
                        }}
                      >
                        Energia
                      </Button>
                    </>
                  ) : groupKey === 'fecho' && documentMode === '3' ? (
                    <>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Button 
                          type="primary" 
                          size="small" 
                          onClick={() => handleNOSSecuritasDocument(record)}
                          style={{ 
                            fontSize: '11px', 
                            height: '24px',
                            width: '100%',
                            backgroundColor: '#722ed1',
                            borderColor: '#722ed1',
                            color: '#ffffff'
                          }}
                        >
                          NOS Securitas
                        </Button>
                        <Button 
                          type="primary" 
                          size="small" 
                          onClick={() => handleProsegurForm(record)}
                          style={{ 
                            fontSize: '11px', 
                            height: '24px',
                            width: '100%',
                            backgroundColor: '#52c41a',
                            borderColor: '#52c41a',
                            color: '#ffffff'
                          }}
                        >
                          Prosegur
                        </Button>
                      </Space>
                    </>
                  ) : groupKey === 'fecho' && documentMode === '4' ? (
                    <>
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => handleEnergyProposal(record, (record as any).marcacao_id)}
                        style={{ 
                          fontSize: '11px', 
                          height: '24px',
                          width: '100%',
                          backgroundColor: '#722ed1',
                          borderColor: '#722ed1',
                          color: '#ffffff'
                        }}
                      >
                        Pedir Proposta Energia/Solar
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => handleVodafoneDocument(record)}
                        className="action-btn-vodafone"
                        style={{ fontSize: '11px', height: '24px' }}
                      >
                        Vodafone
                      </Button>
                      {groupKey === 'fecho' && (
                        <>
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleAlticeResidencialClick(record)}
                            className="action-btn-meo"
                            style={{ 
                              fontSize: '11px', 
                              height: '24px', 
                              width: '100%',
                              backgroundColor: '#ff6b00',
                              borderColor: '#ff6b00',
                              color: '#ffffff'
                            }}
                          >
                            MEO
                          </Button>
                          {user && user.role && hasRole(user, ['Admin', 'Diretor Comercial', 'Team Leader', 'team_leader', 'Gestor de Equipa']) && (
                            <div style={{ position: 'relative', width: '100%' }}>
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '-2px',
                                  left: '-2px',
                                  right: '-2px',
                                  bottom: '-2px',
                                  borderRadius: '6px',
                                  background: 'linear-gradient(45deg, #6366f1, #818cf8, #a5b4fc, #c7d2fe)',
                                  backgroundSize: '400% 400%',
                                  animation: 'pulse 2s ease-in-out infinite',
                                  zIndex: 0,
                                }}
                              />
                              <Button
                                type="primary"
                                size="small"
                                onClick={() => handleMeoPortalClick()}
                                className="action-btn-meo-portal"
                                style={{
                                  fontSize: '11px',
                                  height: '24px',
                                  width: '100%',
                                  position: 'relative',
                                  zIndex: 1,
                                  backgroundColor: '#6366f1',
                                  borderColor: '#6366f1',
                                  color: '#ffffff'
                                }}
                              >
                                MEO
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => handleNOSDocument(record)}
                        className="action-btn-nos"
                        style={{ 
                          fontSize: '11px', 
                          height: '24px',
                          backgroundColor: '#00a651',
                          borderColor: '#00a651',
                          color: '#ffffff'
                        }}
                      >
                        NOS
                      </Button>
                    </>
                  )}
                </Space>
              </div>
            );
          }
        });
      }
      
      // Ver Documentos column now at index 4 (but NOT for validacao subsection)
      if (groupKey !== 'validacao') {
        baseColumns.splice(4, 0, {
          title: 'Ver Documentos',
          key: 'view_documents',
          render: (text: string, record: Lead) => (
            <Button 
              type="default" 
              size="small" 
              onClick={() => handleViewDocuments(record)}
              style={{ fontSize: '11px', height: '24px' }}
            >
              Documentos
            </Button>
          )
        });
      }
      
      // For Fecho subsection, reorder columns: move Status before Ações
      if (groupKey === 'fecho') {
        // Find the Status column and remove it
        const statusColumnIndex = baseColumns.findIndex(col => col.key === 'status');
        if (statusColumnIndex !== -1) {
          const statusColumn = baseColumns.splice(statusColumnIndex, 1)[0];
          // Insert Status column before Ações (at index 4, before Ações which is at index 5)
          baseColumns.splice(4, 0, statusColumn);
        }
      }
      
      // For Outros subsection, move Status to be before Ações
      if (groupKey === 'outros') {
        // Find the Status column and remove it
        const statusColumnIndex = baseColumns.findIndex(col => col.key === 'status');
        if (statusColumnIndex !== -1) {
          const statusColumn = baseColumns.splice(statusColumnIndex, 1)[0];
          // Insert Status column before Ações (at the end, before Ações)
          baseColumns.splice(baseColumns.length - 1, 0, statusColumn);
        }
      }
    }

    if (activeSection === 'instalados') {
      // Add Criar Evento and Documentos columns for Por Instalar group
      if (groupKey === 'porInstalar') {
        baseColumns.splice(2, 0, {
          title: 'Criar Evento',
          key: 'create_instalacao_event',
          render: (text: string, record: Lead) => (
            <Button 
              type="primary" 
              size="small" 
              onClick={() => handleCreateEventForInstalacao(record)}
              disabled={creatingEvent === record.id}
              loading={creatingEvent === record.id}
              style={{ fontSize: '11px', height: '24px' }}
              title="Criar evento de instalação"
            >
              Criar Evento
            </Button>
          )
        });
        
        // Add Documentos button for Team Leaders and up
        if (user && (user.role === 'team_leader' || user.role === 'Team Leader' || user.role === 'admin' || user.role === 'Admin' || user.role === 'super_admin' || user.role === 'Diretor Comercial' || user.role === 'Gestor de Equipa')) {
          baseColumns.splice(3, 0, {
            title: 'Documentos',
            key: 'view_documents',
            render: (text: string, record: Lead) => (
              <Button 
                type="default" 
                size="small" 
                icon={<FileTextOutlined />}
                onClick={() => {
                  setSelectedLeadForDocumentsList(record);
                  setDocumentsModalVisible(true);
                }}
                style={{ fontSize: '11px', height: '24px' }}
                title="Ver documentos do lead"
              >
                Documentos
              </Button>
            )
          });
        }
      }
      
      // Add Data da Instalação and Documentos columns for Instalados group
      if (groupKey === 'instalados') {
        baseColumns.splice(2, 0, {
          title: 'Data da Instalação',
          dataIndex: 'installation_date',
          key: 'installation_date',
          render: (date: string, record: Lead) => {
            const businessArea = getInstaladosBusinessArea(record.id);
            // Only show date picker if a business area is selected (not '1')
            if (businessArea === '1') {
              return <Text type="secondary" style={{ fontSize: '11px' }}>Selecione área primeiro</Text>;
            }
            
            // Get date for the specific business area
            const currentValue = getInstallationDate(record.id, businessArea);
            
            const areaLabels: { [key: string]: string } = {
              '2': 'Telecomunicação',
              '3': 'Segurança',
              '4': 'Energia / Solar'
            };
            
            return (
              <div>
                <div style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '4px' }}>
                  {areaLabels[businessArea] || 'Instalação'}
                </div>
                <DatePicker
                  key={`${record.id}-${businessArea}-${currentValue || 'empty'}`}
                  placeholder="Selecionar data"
                  size="small"
                  style={{ width: '100%' }}
                  defaultValue={currentValue ? moment(currentValue) : undefined}
                  onChange={(date) => {
                    if (date) {
                      const formattedDate = date.format('YYYY-MM-DD');
                      handleInstallationDateChange(record.id, businessArea, formattedDate);
                    } else {
                      // Handle clearing the date
                      handleInstallationDateChange(record.id, businessArea, '');
                    }
                  }}
                />
              </div>
            );
          }
        });
        
        // Add Business Area Radio and Product Selection column for Instalados group
        baseColumns.splice(3, 0, {
          title: 'Área de Negócio / Produtos',
          key: 'business_area_products',
          render: (text: string, record: Lead) => {
            const businessArea = getInstaladosBusinessArea(record.id);
            const selectedProducts = getInstaladosSelectedProducts(record.id);
            
            // Get products for the selected business area
            const getProductsForArea = (area: string) => {
              if (area === '1') return [];
              const areaMap: { [key: string]: string } = {
                '2': 'telecomunicacao',
                '3': 'seguranca',
                '4': 'energia'
              };
              const businessAreaKey = areaMap[area] || '';
              if (!businessAreaKey) return [];
              
              const areaSections = salesSections.filter(s => s.business_area === businessAreaKey && s.is_active);
              const sectionIds = areaSections.map(s => s.id);
              return salesProducts.filter(p => sectionIds.includes(p.section_id) && p.is_active);
            };
            
            const areaProducts = getProductsForArea(businessArea);
            
            return (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                  <InstaladosBusinessAreaRadio
                    leadId={record.id}
                    value={businessArea}
                    onChange={(value) => handleInstaladosBusinessAreaChange(record.id, value)}
                    viewMode={viewMode}
                  />
                </div>
                <Space direction="vertical" size="small">
                  {businessArea === '1' ? (
                    <>
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => handleInstaladosBusinessAreaChange(record.id, '2')}
                        style={{ 
                          fontSize: '11px', 
                          height: '24px',
                          width: '100%',
                          backgroundColor: '#722ed1',
                          borderColor: '#722ed1',
                          color: '#ffffff'
                        }}
                      >
                        Telecomunicação
                      </Button>
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => handleInstaladosBusinessAreaChange(record.id, '3')}
                        style={{ 
                          fontSize: '11px', 
                          height: '24px',
                          width: '100%',
                          backgroundColor: '#722ed1',
                          borderColor: '#722ed1',
                          color: '#ffffff'
                        }}
                      >
                        Segurança
                      </Button>
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => handleInstaladosBusinessAreaChange(record.id, '4')}
                        style={{ 
                          fontSize: '11px', 
                          height: '24px',
                          width: '100%',
                          backgroundColor: '#722ed1',
                          borderColor: '#722ed1',
                          color: '#ffffff'
                        }}
                      >
                        Energia
                      </Button>
                    </>
                  ) : businessArea !== '1' && (
                    <div style={{ marginTop: 8, maxHeight: '200px', overflowY: 'auto', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 4, padding: 8 }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: 6, color: '#fff' }}>
                        Selecionar Produtos:
                      </div>
                      {(() => {
                        // Group products by section
                        const productsBySection = new Map<number, typeof areaProducts>();
                        areaProducts.forEach(product => {
                          const sectionId = product.section_id;
                          if (!productsBySection.has(sectionId)) {
                            productsBySection.set(sectionId, []);
                          }
                          productsBySection.get(sectionId)!.push(product);
                        });
                        
                        return (
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            {areaProducts.length === 0 ? (
                              <Text type="secondary" style={{ fontSize: '11px' }}>Sem produtos disponíveis</Text>
                            ) : (
                              Array.from(productsBySection.entries()).map(([sectionId, products]) => {
                                const section = salesSections.find(s => s.id === sectionId);
                                return (
                                  <div key={sectionId} style={{ marginBottom: '8px' }}>
                                    {section && (
                                      <div style={{ fontSize: '10px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.7)', marginBottom: '4px', textTransform: 'uppercase' }}>
                                        {section.name}
                                      </div>
                                    )}
                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                      {products.map(product => {
                                        const isSelected = selectedProducts.includes(product.id);
                                        return (
                                          <div
                                            key={product.id}
                                            onClick={() => handleInstaladosProductToggle(record.id, product.id)}
                                            style={{
                                              padding: '4px 8px',
                                              borderRadius: 4,
                                              cursor: 'pointer',
                                              backgroundColor: isSelected ? 'rgba(24, 144, 255, 0.3)' : 'rgba(255, 255, 255, 0.05)',
                                              border: `1px solid ${isSelected ? '#1890ff' : 'rgba(255, 255, 255, 0.2)'}`,
                                              fontSize: '11px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 6
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() => handleInstaladosProductToggle(record.id, product.id)}
                                              style={{ margin: 0, cursor: 'pointer' }}
                                            />
                                            <span style={{ color: '#fff', flex: 1 }}>
                                              {product.name}
                                            </span>
                                            <span style={{ color: '#52c41a', fontWeight: 600, marginLeft: '8px' }}>
                                              {product.commission || 0}pts
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </Space>
                                  </div>
                                );
                              })
                            )}
                          </Space>
                        );
                      })()}
                    </div>
                  )}
                </Space>
              </div>
            );
          },
        });
        
        // Add Documentos button for Team Leaders and up
        if (user && (user.role === 'team_leader' || user.role === 'Team Leader' || user.role === 'admin' || user.role === 'Admin' || user.role === 'super_admin' || user.role === 'Diretor Comercial' || user.role === 'Gestor de Equipa')) {
          baseColumns.splice(3, 0, {
            title: 'Documentos',
            key: 'view_documents',
            render: (text: string, record: Lead) => (
              <Button 
                type="default" 
                size="small" 
                icon={<FileTextOutlined />}
                onClick={() => {
                  setSelectedLeadForDocumentsList(record);
                  setDocumentsModalVisible(true);
                }}
                style={{ fontSize: '11px', height: '24px' }}
                title="Ver documentos do lead"
              >
                Documentos
              </Button>
            )
          });
        }
      }
    }

    // Add Última tentativa de Contacto Falhada column for Não Atendeu section
    if (activeSection === 'entrada' && groupKey === 'naoAtendeu') {

      baseColumns.splice(2, 0, {
        title: 'Última tentativa de Contacto Falhada',
        key: 'last_contact_attempt',
        render: (text: string, record: Lead) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500, marginBottom: '2px' }}>
              Tentativas: {contactAttemptCounts.get(record.id) || 0}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(10px)', padding: '8px', borderRadius: '6px', minWidth: '220px', border: '1px solid rgba(255, 255, 255, 0.3)' }}>
              <Input
                value={lastContactAttempts.get(record.id) || ''}
                placeholder="Data e hora"
                style={{ fontSize: '13px', width: '160px', height: '32px' }}
                readOnly
              />
              <Button 
                type="primary" 
                size="small" 
                onClick={() => handleLastContactAttempt(record.id)}
                style={{ fontSize: '13px', height: '32px', minWidth: '60px', fontWeight: '500' }}
              >
                Set
              </Button>
            </div>
          </div>
        )
      });
    }

    return baseColumns;
  };

  // Function to apply all filters to leads for a specific section
  const applyFiltersToLeads = (leads: Lead[], sectionKey: string) => {
    // Filter by section first
    // For 'entrada', include leads with null/undefined/empty section (default section)
    let filtered = leads.filter(lead => {
      if (sectionKey === 'entrada') {
        const section = lead.section;
        return section === sectionKey || !section || section === null || section === undefined || section === '';
      }
      return lead.section === sectionKey;
    });
    
    // Apply "See Own Leads" filter (only for Team Leader and up)
    if (showOwnLeadsOnly && user && user.role && isAdminOrDCOrTeamLeader(user.role)) {
      filtered = filtered.filter(lead => 
        lead.assigned_to === user.id
      );
    }
    
    // Apply search text filter
    if (searchText) {
      filtered = filtered.filter(lead =>
        `${lead.name}`.toLowerCase().includes(searchText.toLowerCase()) ||
        lead.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        lead.phone?.toLowerCase().includes(searchText.toLowerCase()) ||
        lead.dif?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    // Apply advanced filters
    if (advancedFilters.operadora.length > 0) {
      filtered = filtered.filter(lead =>
        lead.current_provider && advancedFilters.operadora.includes(lead.current_provider)
      );
    }
    
    if (advancedFilters.mes.length > 0) {
      filtered = filtered.filter(lead => {
        // For "Instalados" subsection, use installation_date field only
        let dateField;
        if (sectionKey === 'instalados' && lead.lead_status === 'Instalados') {
          dateField = lead.installation_date; // Use "Data da Instalação" field only
        } else {
          dateField = lead.created_at; // Use "Data de Criação" field for other subsections
        }
        
        if (!dateField) return false;
        const date = new Date(dateField);
        const month = date.toLocaleString('pt-BR', { month: 'long' });
        return advancedFilters.mes.includes(month);
      });
    }
    
    if (advancedFilters.dia.length > 0) {
      filtered = filtered.filter(lead => {
        // For "Instalados" subsection, use installation_date field only
        let dateField;
        if (sectionKey === 'instalados' && lead.lead_status === 'Instalados') {
          dateField = lead.installation_date; // Use "Data da Instalação" field only
        } else {
          dateField = lead.created_at; // Use "Data de Criação" field for other subsections
        }
        
        if (!dateField) return false;
        const date = new Date(dateField);
        const day = date.getDate();
        return advancedFilters.dia.includes(day);
      });
    }
    
    return filtered;
  };

  // Function to get filtered count for a section
  const getFilteredCount = (sectionKey: string) => {
    if (!safeLeads || safeLeads.length === 0) {
      return 0;
    }
    // Create a temporary array with unsaved section changes applied
    const leadsWithUnsavedChanges = safeLeads.map(lead => {
      const unsavedSection = unsavedSectionChanges.get(lead.id);
      if (unsavedSection !== undefined) {
        return { ...lead, section: unsavedSection };
      }
      return lead;
    });
    const count = applyFiltersToLeads(leadsWithUnsavedChanges, sectionKey).length;
    return count;
  };

  // --- Only use backend data for filtering/grouping ---
  const filterLeads = () => {
    const currentSection = sections.find(s => s.key === activeSection);
    if (!currentSection) return;
    
    // Use the new function to get filtered leads for the current section
    const filtered = applyFiltersToLeads(safeLeads, activeSection);
    setFilteredLeads(filtered);
  };

  // --- Restore local staging of status and section changes ---
  // In handleStatusChange, when moving from 'entrada' to 'feedback' via 'Atendeu', set status to '' so it appears in 'Outros'
  const handleStatusChange = (leadId: number, newStatus: string | null) => {
    const stagedStatus = newStatus === null || newStatus === undefined ? '' : newStatus;
    setUnsavedChanges(prev => new Map(prev).set(leadId, stagedStatus));
    setLastChangedLead({ id: leadId, newStatus: stagedStatus });

    // Automatic section movement logic
    if (stagedStatus === 'Atendeu' && activeSection === 'entrada') {
      // Move from Entrada to Feedback when status becomes "Atendeu"
      setUnsavedSectionChanges(prev => new Map(prev).set(leadId, 'feedback'));
      // Store the original "Atendeu" status for saving, but display empty for "Outros" subsection
      setOriginalStatusForDisplay(prev => new Map(prev).set(leadId, 'Atendeu'));
      // Set status to '' for display purposes only (will be saved as "Atendeu")
      setUnsavedChanges(prev => new Map(prev).set(leadId, ''));
    } else if (stagedStatus === 'Fecho' && activeSection === 'feedback') {
      // Move from Feedback to Registro when status becomes "Fecho"
      setUnsavedSectionChanges(prev => new Map(prev).set(leadId, 'registro'));
    } else if (stagedStatus === 'Validação' && activeSection === 'registro') {
      // Stay in registro section when status becomes "Validação"
      // The lead will be grouped in the "Validação" subsection
      setUnsavedSectionChanges(prev => { const m = new Map(prev); m.delete(leadId); return m; });
    } else if (stagedStatus === 'Por Instalar' && activeSection === 'instalados') {
      // Stay in instalados section when status becomes "Por Instalar"
      // The lead will be grouped in the "Por Instalar" subsection
      setUnsavedSectionChanges(prev => { const m = new Map(prev); m.delete(leadId); return m; });
    } else if (stagedStatus === 'Instalados' && activeSection === 'instalados') {
      // Stay in instalados section when status becomes "Instalados"
      // The lead will be grouped in the "Instalados" subsection
      setUnsavedSectionChanges(prev => { const m = new Map(prev); m.delete(leadId); return m; });
    } else {
      // For other status changes, clear any stored original status
      setOriginalStatusForDisplay(prev => { const m = new Map(prev); m.delete(leadId); return m; });
    }

    // Automatic last contact date setting for "Não Atendeu" status
    if (stagedStatus === 'Não Atendeu') {
      const currentDate = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
      setLastContactDates(prev => new Map(prev).set(leadId, currentDate));
      setLastContactDatesChanged(prev => new Set(prev).add(leadId));
      
      // Automatically trigger the blue "Set" button for this specific lead
      setTimeout(() => {
        handleLastContactAttempt(leadId);
      }, 100);
    }
  };

  const getStatusColor = (status: string | null | undefined) => {
    if (!status) return 'default';
    const statusColors: { [key: string]: string } = {
      'Atendeu': 'green',
      'Não Atribuido': 'orange',
      'Não Atendeu': 'red',
      'Fecho': 'green',
      'Pendente': 'blue',
      'Follow-up': 'purple',
      'Sem Interesse': 'red',
      'Fidelizado': 'green',
      'Informação Errada': 'orange',
      'Validação': 'cyan',
      'Por Instalar': 'blue',
      'Instalados': 'green'
    };
    return statusColors[status] || 'default';
  };

  const handleCollapseAll = () => {
    setActivePanels([]);
  };

  const handleSubsectionPageChange = async (subsectionKey: string, page: number) => {
    setSubsectionPagination(prev => {
      const newMap = new Map(prev).set(subsectionKey, page);
      return newMap;
    });
    
    // Fetch highlighted notes for visible leads on this page
    await fetchHighlightedNotesForVisibleLeads(subsectionKey, page);
  };

  const handleCollapseChange = async (keys: string | string[]) => {
    const newActivePanels = Array.isArray(keys) ? keys : [keys];
    setActivePanels(newActivePanels);
    
    // Fetch highlighted notes for newly opened subsections
    for (const panelKey of newActivePanels) {
      if (!activePanels.includes(panelKey)) {
        // This is a newly opened subsection
        const currentPage = subsectionPagination.get(panelKey) || 1;
        await fetchHighlightedNotesForVisibleLeads(panelKey, currentPage);
      }
    }
  };

  // Advanced filter functions
  const handleOperadoraFilterChange = (values: string[]) => {
    setAdvancedFilters(prev => ({ ...prev, operadora: values }));
  };

  const handleMesFilterChange = (values: string[]) => {
    setAdvancedFilters(prev => ({ ...prev, mes: values }));
  };

  const handleDiaFilterChange = (values: number[]) => {
    setAdvancedFilters(prev => ({ ...prev, dia: values }));
  };

  const clearAllFilters = () => {
    setSearchText(''); // Clear search text
    setAdvancedFilters({
      operadora: [],
      mes: [], // Clear month filter completely
      dia: []
    });
  };

  const getActiveFiltersCount = () => {
    // Count all active filters
    const operadoraCount = advancedFilters.operadora.length;
    const mesCount = advancedFilters.mes.length;
    const diaCount = advancedFilters.dia.length;
    return operadoraCount + mesCount + diaCount;
  };

  // Add missing function definitions
  const handleViewLead = async (lead: Lead) => {
    if (workspaceEmbed && onOpenLeadInWorkspace) {
      try {
        const freshLead = await apiService.getLead(lead.id);
        onOpenLeadInWorkspace(freshLead);
      } catch (error) {
        console.error('Error fetching fresh lead data:', error);
        onOpenLeadInWorkspace(lead);
      }
      return;
    }
    // Set the lead immediately for quick display
    setSelectedLead(lead);
    setViewModalVisible(true);

    // Fetch fresh lead data from API to ensure we have the latest information
    try {
      const freshLead = await apiService.getLead(lead.id);
      setSelectedLead(freshLead);
    } catch (error) {
      console.error('Error fetching fresh lead data:', error);
      // Keep the original lead data if fetch fails
    }
  };

  // Handle opening task modal with pre-assigned lead
  const handleOpenTaskModal = (lead: Lead) => {
    setSelectedLeadForTask(lead);
    setTaskModalVisible(true);
  };

  // --- Revert: after revert/save, set leadToFollowId ---
  const handleRevertLead = (lead: Lead) => {
    const prevSection = getPreviousSection(lead.section || 'entrada');
    if (!prevSection) {
      message.info('Não é possível reverter mais esta lead.');
      return;
    }
    Modal.confirm({
      title: 'Reverter Lead',
      content: `Tem a certeza que pretende reverter o lead "${lead.name}" para a secção anterior?`,
      okText: 'Reverter',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await apiService.updateLead(lead.id, { section: prevSection, lead_status: '' });
          setUnsavedChanges(prev => { const m = new Map(prev); m.delete(lead.id); return m; });
          setUnsavedSectionChanges(prev => { const m = new Map(prev); m.delete(lead.id); return m; });
          setOriginalStatusForDisplay(prev => { const m = new Map(prev); m.delete(lead.id); return m; });
          setLastChangedLead({ id: lead.id, newStatus: '' });
          // Enable automatic lead following to open new subsection
          setLeadToFollowId(lead.id);
          await fetchLeads();
        } catch (error) {
          message.error('Erro ao reverter lead.');
        }
      }
    });
  };

  const [showMissingMotivoModal, setShowMissingMotivoModal] = useState(false);
  const [missingMotivoLeads, setMissingMotivoLeads] = useState<Lead[]>([]);
  const [pendingSectionChange, setPendingSectionChange] = useState<string | null>(null);

  // Helper to get all Pendente leads missing motivo
  const getPendenteLeadsMissingMotivo = () => {
    return safeLeads.filter(
      l => l.section === 'feedback' && l.lead_status === 'Pendente' && !(pendenteReasons.get(l.id) && pendenteReasons.get(l.id)?.trim())
    );
  };



  // Intercept tab change (if you have tab navigation)
  // If you have a Tabs component, add similar logic to its onChange handler

  // Modal for missing motivo
  // In the renderGridView and renderKanbanView, make sure the 'Criar Evento' button is always enabled (remove motivo check from disabled prop)
  const getDefaultColumns = () => [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Lead) => (
        <div>
          <Space>
            <span style={getLeadCategoryStyle(record.lead_category)}>{name}</span>
            {/* Show user icon for high rank users' own leads */}
            {user && (user.role === 'team_leader' || user.role === 'Team Leader' || user.role === 'admin' || user.role === 'Admin' || user.role === 'super_admin' || user.role === 'Diretor Comercial' || user.role === 'Gestor de Equipa') && 
             record.assigned_to === user.id && (
              <span style={{ fontSize: '14px' }}>🌸</span>
            )}
            {/* Show star icon for leads with highlighted notes */}
            {(() => {
              const highlightedNotes = highlightedNotesMap.get(record.id);
              return highlightedNotes && highlightedNotes.length > 0;
            })() && (
              <StarFilled style={{ color: '#faad14', fontSize: '14px' }} />
            )}
          </Space>
          {/* Show team indicator for high rank users viewing team members' leads */}
        </div>
      ),
      sorter: (a: Lead, b: Lead) => a.name.localeCompare(b.name)
    },
    {
      title: 'Contacto',
      dataIndex: 'phone',
      key: 'phone',
      render: (text: string, record: Lead) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.phone || '-'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            NIF: {record.dif || '-'}
          </div>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'lead_status',
      key: 'status',
      render: (status: string, record: Lead) => {
        let statusOptions = sections.find(s => s.key === activeSection)?.states || [];
        if (activeSection === 'entrada' && !statusOptions.includes('Atendeu')) {
          statusOptions = [...statusOptions, 'Atendeu'];
        }
        if (activeSection === 'feedback' && !statusOptions.includes('Fecho')) {
          statusOptions = [...statusOptions, 'Fecho'];
        }
        if (activeSection === 'registro') {
          const registroStatuses = ['Fecho', 'Validação'];
          registroStatuses.forEach(status => {
            if (!statusOptions.includes(status)) {
              statusOptions = [...statusOptions, status];
            }
          });
        }
        return (
          <Select
            value={unsavedChanges.has(record.id) ? unsavedChanges.get(record.id) || undefined : record.lead_status || undefined}
            style={{ width: 120 }}
            size="small"
            placeholder="Selecione o estado..."
            onChange={(value) => handleStatusChange(record.id, value)}
            allowClear
          >
            {statusOptions.map(state => (
              <Option key={state} value={state}>
                {state}
              </Option>
            ))}
          </Select>
        );
      }
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (text: string, record: Lead) => (
        <Space>
          <Tooltip title={workspaceEmbed && onOpenLeadInWorkspace ? 'Abrir no workspace de cliente' : 'Ver detalhes'}>
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => handleViewLead(record)}
            />
          </Tooltip>
          <Tooltip title="Notas">
            <Button 
              type="primary" 
              icon={<PaperClipOutlined />} 
              size="small"
              onClick={() => handleOpenNotes(record)}
              style={{ fontSize: '11px', height: '24px' }}
            />
          </Tooltip>
          <Tooltip title="Criar Tarefa">
            <Button 
              type="text" 
              icon={<CrownOutlined />} 
              size="small"
              onClick={() => handleOpenTaskModal(record)}
              style={{ color: '#faad14' }}
            />
          </Tooltip>
          {userMayUseClientManagementEmailButton(user) && (
            <Tooltip title="Enviar Email">
              <Button
                type="text"
                icon={<MailOutlined />}
                size="small"
                onClick={() => handleSendEmail(record)}
                style={{ color: '#6366f1' }}
              />
            </Tooltip>
          )}
          {user?.role && isAdminOrDCOrTeamLeader(user.role) && record.assigned_to && record.assigned_to !== user.id && (
            <Tooltip title="Mensagem ao Responsável">
              {/* Chat button disabled */}
              {/* <Button 
                type="text" 
                icon={<MessageOutlined />} 
                size="small"
                onClick={() => handleMessageLead(record)}
                style={{ color: '#52c41a' }}
              /> */}
            </Tooltip>
          )}
          {user?.role && isAdminOrDCOrTeamLeader(user.role) && (
            <Tooltip title="Atribuir Lead">
              <Button 
                type="text" 
                icon={<UserOutlined />} 
                size="small"
                onClick={() => handleAssignLead(record)}
                style={{ color: '#722ed1' }}
              />
            </Tooltip>
          )}
          {(['feedback', 'registro'].includes(activeSection) || (activeSection === 'entrada' && record.lead_status === 'Não Atendeu') || (activeSection === 'instalados' && record.lead_status === 'Por Instalar')) && (
            <Button size="small" danger onClick={() => handleRevertLead(record)}>
              Reverter
            </Button>
          )}
          <Tooltip title="Nova Venda">
            <Button 
              type="primary" 
              icon={<ShoppingCartOutlined />} 
              size="small"
              onClick={() => handleOpenVendaForLead(record)}
              style={{ 
                fontSize: '11px', 
                height: '24px',
                backgroundColor: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1',
                borderColor: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1'
              }}
            >
              Venda
            </Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  const getPendenteColumns = () => {
    const cols = getDefaultColumns();
    cols.splice(2, 0, {
      title: 'Motivo',
      key: 'pendente_reason',
      render: (text: string, record: Lead) => (
        <Input.TextArea
          placeholder="Escreva o motivo do estado pendente..."
          value={pendenteReasons.get(record.id) || ''}
          onChange={(e) => handlePendenteReasonChange(record.id, e.target.value)}
          style={{ fontSize: '11px', minHeight: '60px' }}
          required
        />
      )
    });
    cols.splice(3, 0, {
      title: 'Criar Evento',
      key: 'create_event',
      render: (text: string, record: Lead) => {
        const reason = pendenteReasons.get(record.id);
        const hasReason = reason && reason.trim().length > 0;

        return (
          <Button 
            type="primary" 
            size="small" 
            onClick={() => handleCreateEventForPendente(record)}
            disabled={!hasReason || creatingEvent === record.id}
            loading={creatingEvent === record.id}
            className="action-btn-create-event"
            style={{ 
              fontSize: '11px', 
              height: '24px',
              '--section-color': getButtonColors('createEvent').bg
            } as React.CSSProperties & { '--section-color': string }}
            title={!hasReason ? "Deve escrever o motivo pendente primeiro" : "Criar evento de follow-up"}
          >
            Criar Evento
          </Button>
        );
      }
    });
    return cols;
  };

  // Remove Cobertura modal state and handlers
  const coberturaTitles = ['Vodafone', 'MEO', 'NOS'];
  const coberturaUrls = [
    'https://www.vodafone.pt/pacotes/cobertura.html',
    'https://www.meo.pt/servicos/casa/cobertura-fibra?utm_source=bing&utm_medium=adwords&utm_campaign=brandmeo-servicos&gclid=1f130e0d4fb81933ac7866723919f698&gclsrc=3p.ds&msclkid=1f130e0d4fb81933ac7866723919f698&utm_term=meo%20cobertura&utm_content=MEO-Cobertura_GGA_MX_CXD',
    'https://www.nos.pt/pacotes/testar-cobertura-de-fibra'
  ];

  const renderGridView = () => {
    const sectionLeads = filteredLeads;

    if (activeSection === 'registro') {
      const groupedLeads = groupRegistroLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
      return (
        <div>
          <style>{`
            .highlight-lead {
              background: #fffbe6 !important;
              transition: background 1s;
            }
          `}</style>
          <div style={{ marginBottom: '20px', textAlign: 'right' }}>
            <Button 
              size="small" 
              onClick={handleCollapseAll} 
              className="action-btn-collapse"
              style={{ 
                borderRadius: '8px', 
                fontSize: '13px',
                background: getButtonColors('collapse').bg,
                borderColor: getButtonColors('collapse').border,
                color: getButtonColors('collapse').text
              }}
            >
              Colapsar Todas
            </Button>
          </div>
          <Collapse 
            activeKey={activePanels} 
            onChange={handleCollapseChange}
            ghost
          >
            {groupedLeads.map(group => (
              <Collapse.Panel 
                key={group.key} 
                header={group.label}
                style={{ marginBottom: '8px' }}
              >
                <div className="glassmorphism-table-wrapper" style={{ background: 'transparent' }}>
                  <Table
                    dataSource={group.leads}
                    columns={getSectionColumns(group.key)}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 800 }}
                    className="glassmorphism-table"
                    style={{ background: 'transparent' }}
                    rowClassName={(record: Lead) => {
                      let className = 'glassmorphism-table-row';
                      if (record.id === highlightedLeadId) {
                        className += ' highlight-lead';
                      }
                      return className.trim();
                    }}
                    locale={{ emptyText: "Nenhum lead neste grupo" }}
                    sortDirections={['ascend', 'descend']}
                  />
                </div>
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <Pagination
                    current={group.currentPage}
                    total={group.totalLeads}
                    pageSize={LEADS_PER_PAGE}
                    onChange={(page) => handleSubsectionPageChange(group.key, page)}
                    size="small"
                    showSizeChanger={false}
                    showQuickJumper={false}
                    showTotal={(total, range) => `${range[0]}-${range[1]} de ${total}`}
                  />
                </div>

              </Collapse.Panel>
            ))}
          </Collapse>
        </div>
      );
    }

    if (activeSection === 'instalados') {
      const groupedLeads = groupInstaladosLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
      return (
        <div>
          <style>{`
            .highlight-lead {
              background: #fffbe6 !important;
              transition: background 1s;
            }
          `}</style>
          <div style={{ marginBottom: '20px', textAlign: 'right' }}>
            <Button 
              size="small" 
              onClick={handleCollapseAll} 
              className="action-btn-collapse"
              style={{ 
                borderRadius: '8px', 
                fontSize: '13px',
                background: getButtonColors('collapse').bg,
                borderColor: getButtonColors('collapse').border,
                color: getButtonColors('collapse').text
              }}
            >
              Colapsar Todas
            </Button>
          </div>
          <Collapse 
            activeKey={activePanels} 
            onChange={handleCollapseChange}
            ghost
          >
            {groupedLeads.map(group => (
              <Collapse.Panel 
                key={group.key} 
                header={group.label}
                style={{ marginBottom: '8px' }}
              >
                <div className="glassmorphism-table-wrapper" style={{ background: 'transparent' }}>
                  <Table
                    dataSource={group.leads}
                    columns={getSectionColumns(group.key)}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 800 }}
                    className="glassmorphism-table"
                    style={{ background: 'transparent' }}
                    rowClassName={(record: Lead) => {
                      let className = 'glassmorphism-table-row';
                      if (record.id === highlightedLeadId) {
                        className += ' highlight-lead';
                      }
                      return className.trim();
                    }}
                    locale={{ emptyText: "Nenhum lead neste grupo" }}
                    sortDirections={['ascend', 'descend']}
                  />
                </div>
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <Pagination
                    current={group.currentPage}
                    total={group.totalLeads}
                    pageSize={LEADS_PER_PAGE}
                    onChange={(page) => handleSubsectionPageChange(group.key, page)}
                    size="small"
                    showSizeChanger={false}
                    showQuickJumper={false}
                    showTotal={(total, range) => `${range[0]}-${range[1]} de ${total}`}
                  />
                </div>

              </Collapse.Panel>
            ))}
          </Collapse>
        </div>
      );
    }

    if (sectionLeads.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="secondary">Nenhum lead encontrado nesta secção</Text>
        </div>
      );
    }

    // Group leads by status for grid view (same as kanban)
    let groupedLeads: any[] = [];
    if (activeSection === 'entrada') {
      groupedLeads = groupEntradaLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
    } else if (activeSection === 'feedback') {
      groupedLeads = groupFeedbackLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
    } else if (activeSection === 'registro') {
      groupedLeads = groupRegistroLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
    } else if (activeSection === 'instalados') {
      groupedLeads = groupInstaladosLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
    }

    return (
      <div>
        <style>{`
          .highlight-lead {
            background: #fffbe6 !important;
            transition: background 1s;
          }
          
          /* Force transparent backgrounds on tables - override everything */
          .glassmorphism-table,
          .glassmorphism-table *,
          .glassmorphism-table .ant-table,
          .glassmorphism-table .ant-table *,
          .glassmorphism-table .ant-table-container,
          .glassmorphism-table .ant-table-body,
          .glassmorphism-table .ant-table-thead > tr > th,
          .glassmorphism-table .ant-table-tbody > tr > td,
          .glassmorphism-table .ant-table-tbody > tr {
            background: transparent !important;
            backgroundColor: transparent !important;
          }
          
        `}</style>
        <div style={{ marginBottom: '20px', textAlign: 'right' }}>
              <Button size="small" onClick={handleCollapseAll} className="action-btn-collapse" style={{ borderRadius: '8px', fontSize: '13px' }}>
                Colapsar Todas
              </Button>
        </div>
        <Collapse 
          activeKey={activePanels} 
          onChange={handleCollapseChange}
          ghost
        >
          {groupedLeads.map(group => (
            <Collapse.Panel 
              key={group.key} 
              header={group.label}
              style={{ marginBottom: '12px' }}
            >
              <div className="glassmorphism-table-wrapper" style={{ background: 'transparent' }}>
                <Table
                  dataSource={group.leads}
                  columns={activeSection === 'feedback' && group.key === 'pendente' ? getPendenteColumns() : 
                           activeSection === 'feedback' && group.key === 'followUp' ? getFollowUpColumns() : 
                           activeSection === 'feedback' && group.key === 'semInteresse' ? getSemInteresseColumns() : 
                           activeSection === 'feedback' && group.key === 'informacaoErrada' ? getInformacaoErradaColumns() : 
                           activeSection === 'feedback' && group.key === 'fidelizado' ? getFidelizadoColumns() : 
                           activeSection === 'feedback' && group.key === 'semCobertura' ? getSemCoberturaColumns() : 
                           getSectionColumns(group.key)}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 800 }}
                  className="glassmorphism-table"
                  style={{ background: 'transparent' }}
                  rowClassName={(record: Lead) => {
                    let className = 'glassmorphism-table-row';
                    if (record.id === highlightedLeadId) {
                      className += ' highlight-lead';
                    }
                    return className.trim();
                  }}
                  locale={{ emptyText: "Nenhum lead neste grupo" }}
                />
              </div>
              <div style={{ marginTop: '12px', textAlign: 'center' }}>
                <Pagination
                  current={group.currentPage}
                  total={group.totalLeads}
                  pageSize={LEADS_PER_PAGE}
                  onChange={(page) => handleSubsectionPageChange(group.key, page)}
                  size="small"
                  showSizeChanger={false}
                  showQuickJumper={false}
                  showTotal={(total, range) => `${range[0]}-${range[1]} de ${total}`}
                />
              </div>

            </Collapse.Panel>
          ))}
        </Collapse>
      </div>
    );
  };

  const renderKanbanView = () => {
    const sectionLeads = filteredLeads;
    const kbEmbed = workspaceEmbed;
    const kbMuted = kbEmbed ? '#64748b' : 'rgba(255, 255, 255, 0.8)';
    const kbIcon = kbEmbed ? '#94a3b8' : 'rgba(255, 255, 255, 0.7)';
    const kbStrong = kbEmbed ? '#0f172a' : '#ffffff';
    const kbBorder = kbEmbed ? 'rgba(15, 23, 42, 0.1)' : 'rgba(255, 255, 255, 0.2)';
    const kbCardBorder = kbEmbed ? '1px solid rgba(15, 23, 42, 0.1)' : '2px solid rgba(255, 255, 255, 0.3)';
    const kbCardShadow = kbEmbed
      ? '0 1px 3px rgba(15,23,42,0.06), 0 2px 8px rgba(15,23,42,0.06)'
      : '0 6px 20px rgba(0,0,0,0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset';
    const kbCardShadowHover = kbEmbed
      ? '0 4px 14px rgba(15,23,42,0.12)'
      : '0 8px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
    const kbCardBorderHover = kbEmbed ? 'rgba(30, 58, 95, 0.28)' : 'rgba(255, 255, 255, 0.5)';
    const kbGlassBg = kbEmbed ? 'rgba(15, 23, 42, 0.04)' : 'rgba(255, 255, 255, 0.2)';
    const kbGlassBorder = kbEmbed ? '1px solid rgba(15, 23, 42, 0.1)' : '1px solid rgba(255, 255, 255, 0.3)';
    const kbFechoHdr = kbEmbed ? '#334155' : 'rgba(255, 255, 255, 0.9)';
    
    if (sectionLeads.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="secondary" style={workspaceEmbed ? { color: '#64748b' } : undefined}>
            Nenhum lead encontrado nesta secção
          </Text>
        </div>
      );
    }

    // Group leads by status for kanban view
    let groupedLeads: any[] = [];
    
    if (activeSection === 'entrada') {
      groupedLeads = groupEntradaLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
    } else if (activeSection === 'feedback') {
      groupedLeads = groupFeedbackLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
    } else if (activeSection === 'registro') {
      groupedLeads = groupRegistroLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
    } else if (activeSection === 'instalados') {
      groupedLeads = groupInstaladosLeads(sectionLeads, subsectionPagination, LEADS_PER_PAGE);
    }

    return (
      <div>
        <style>{`
          .highlight-lead {
            background: #fffbe6 !important;
            transition: background 1s;
          }
        `}</style>
        <div style={{ marginBottom: '16px', textAlign: 'right' }}>
          <Button size="small" onClick={handleCollapseAll} className="action-btn-collapse">
            Colapsar Todas
          </Button>
        </div>
        
        <Collapse 
          activeKey={activePanels} 
          onChange={handleCollapseChange}
          ghost
        >
              {groupedLeads.map(group => (
                <Collapse.Panel 
                  key={group.key} 
                  header={group.label}
                  style={{ marginBottom: '8px' }}
                >
                  <Row gutter={[16, 16]}>
                    {group.leads.map((lead: Lead) => {
                      // Status options logic (same as grid view)
                      let statusOptions = sections.find(s => s.key === activeSection)?.states || [];
                      if (activeSection === 'entrada' && !statusOptions.includes('Atendeu')) {
                        statusOptions = [...statusOptions, 'Atendeu'];
                      }
                      if (activeSection === 'feedback' && !statusOptions.includes('Fecho')) {
                        statusOptions = [...statusOptions, 'Fecho'];
                      }
                      if (activeSection === 'registro') {
                        // Add all registro section statuses
                                                 const registroStatuses = ['Fecho', 'Validação'];
                        registroStatuses.forEach(status => {
                          if (!statusOptions.includes(status)) {
                            statusOptions = [...statusOptions, status];
                          }
                        });
                      }
                      if (activeSection === 'instalados') {
                        // Add all instalados section statuses
                        const instaladosStatuses = ['Por Instalar', 'Instalados'];
                        instaladosStatuses.forEach(status => {
                          if (!statusOptions.includes(status)) {
                            statusOptions = [...statusOptions, status];
                          }
                        });
                      }
                      return (
                        <Col xs={24} sm={12} md={8} lg={6} xl={4} key={lead.id}>
                          <Card 
                            size="small" 
                            className={`glassmorphism-kanban-card ${lead.id === highlightedLeadId ? 'highlight-lead' : ''}`}
                            bodyStyle={{ 
                              background: 'transparent', 
                              padding: '16px',
                              backgroundColor: 'transparent'
                            }}
                            style={{ 
                              height: '100%',
                              border: kbCardBorder,
                              borderRadius: '12px',
                              boxShadow: kbCardShadow,
                              transition: 'all 0.2s ease',
                              background: kbEmbed ? '#ffffff' : 'transparent',
                              backgroundColor: kbEmbed ? '#ffffff' : 'transparent'
                            }}
                            hoverable
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = kbCardShadowHover;
                              e.currentTarget.style.borderColor = kbCardBorderHover;
                              e.currentTarget.style.background = kbEmbed ? '#ffffff' : 'transparent';
                              e.currentTarget.style.backgroundColor = kbEmbed ? '#ffffff' : 'transparent';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = kbCardShadow;
                              e.currentTarget.style.borderColor = kbEmbed ? 'rgba(15, 23, 42, 0.1)' : 'rgba(255, 255, 255, 0.3)';
                              e.currentTarget.style.background = kbEmbed ? '#ffffff' : 'transparent';
                              e.currentTarget.style.backgroundColor = kbEmbed ? '#ffffff' : 'transparent';
                            }}
                          >
                            {/* Header Section: Name, Icons, Tags */}
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '6px', ...getLeadCategoryStyle(lead.lead_category) }}>
                                <Space>
                                  {lead.name}
                                  {/* Show user icon for high rank users' own leads */}
                                  {user && (user.role === 'team_leader' || user.role === 'Team Leader' || user.role === 'admin' || user.role === 'Admin' || user.role === 'super_admin' || user.role === 'Diretor Comercial' || user.role === 'Gestor de Equipa') && 
                                   lead.assigned_to === user.id && (
                                    <span style={{ fontSize: '14px' }}>🌸</span>
                                  )}
                                  {/* Show star icon for leads with highlighted notes */}
                                  {(() => {
                                    const highlightedNotes = highlightedNotesMap.get(lead.id);
                                    return highlightedNotes && highlightedNotes.length > 0;
                                  })() && (
                                    <StarFilled style={{ color: '#f59e0b', fontSize: '14px' }} />
                                  )}
                                </Space>
                              </div>
                              <div style={{ fontSize: '12px', color: kbMuted, marginBottom: '6px' }}>
                                {lead.email || '-'}
                              </div>
                              <Space size={4} wrap>
                                {/* Show team indicator for high rank users viewing team members' leads */}
                              </Space>
                            </div>
                            
                            {/* Contact Section */}
                            <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: `1px solid ${kbBorder}` }}>
                              <div style={{ fontSize: '12px', color: kbMuted, marginBottom: '4px' }}>
                                <PhoneOutlined style={{ marginRight: '6px', color: kbIcon }} />
                                {lead.phone || '-'}
                              </div>
                              <div style={{ fontSize: '12px', color: kbMuted }}>
                                <UserOutlined style={{ marginRight: '6px', color: kbIcon }} />
                                NIF: {lead.dif || '-'}
                              </div>
                            </div>
                            
                            {/* Status Section */}
                            <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: `1px solid ${kbBorder}` }}>
                              <div style={{ fontSize: '11px', fontWeight: 600, color: kbStrong, marginBottom: '6px' }}>
                                Status:
                              </div>
                              <Select
                                value={unsavedChanges.has(lead.id) ? unsavedChanges.get(lead.id) || undefined : lead.lead_status || undefined}
                                style={{ width: '100%', fontSize: 12 }}
                                size="small"
                                placeholder="Selecione o estado..."
                                onChange={(value) => handleStatusChange(lead.id, value)}
                                allowClear
                              >
                                {statusOptions.map(state => (
                                  <Option key={state} value={state}>
                                    {state}
                                  </Option>
                                ))}
                              </Select>
                            </div>
                            
                            {/* Subsection-Specific Fields */}
                            <div style={{ marginBottom: '12px' }}>
                              {/* Motivo and Criar Evento for pendente subsection only */}
                              {activeSection === 'feedback' && group.key === 'pendente' && (lead.lead_status === 'Pendente' || unsavedChanges.get(lead.id) === 'Pendente') && (
                                <>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Motivo:
                                  </div>
                                  <Input.TextArea
                                    placeholder="Escreva o motivo do estado pendente..."
                                    value={pendenteReasons.get(lead.id) || ''}
                                    onChange={(e) => handlePendenteReasonChange(lead.id, e.target.value)}
                                    style={{ fontSize: '11px', minHeight: '60px', marginBottom: '8px', borderRadius: '8px' }}
                                    required
                                  />
                                  <Button 
                                    type="primary" 
                                    size="small" 
                                    onClick={() => handleCreateEventForPendente(lead)}
                                    disabled={creatingEvent === lead.id || !pendenteReasons.get(lead.id)?.trim()}
                                    loading={creatingEvent === lead.id}
                                    style={{ fontSize: '11px', height: '24px', width: '100%', borderRadius: '6px' }}
                                    title={!pendenteReasons.get(lead.id)?.trim() ? "Deve escrever o motivo pendente primeiro" : "Criar evento de follow-up"}
                                  >
                                    Criar Evento
                                  </Button>
                                </>
                              )}

                              {/* Nota and Criar Evento for followUp subsection only */}
                              {activeSection === 'feedback' && group.key === 'followUp' && (lead.lead_status === 'Follow-up' || unsavedChanges.get(lead.id) === 'Follow-up') && (
                                <>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Nota:
                                  </div>
                                  <Input.TextArea
                                    placeholder="Escreva a nota de follow-up..."
                                    value={followUpNotes.get(lead.id) || ''}
                                    onChange={(e) => handleFollowUpNoteChange(lead.id, e.target.value)}
                                    style={{ fontSize: '11px', minHeight: '60px', marginBottom: '8px', borderRadius: '8px' }}
                                    required
                                  />
                                  <Button 
                                    type="primary" 
                                    size="small" 
                                    onClick={() => handleCreateEventForFollowUp(lead)}
                                    disabled={creatingEvent === lead.id || !followUpNotes.get(lead.id)?.trim()}
                                    loading={creatingEvent === lead.id}
                                    style={{ fontSize: '11px', height: '24px', width: '100%', borderRadius: '6px' }}
                                    title={!followUpNotes.get(lead.id)?.trim() ? "Deve escrever a nota de follow-up primeiro" : "Criar evento de follow-up"}
                                  >
                                    Criar Evento
                                  </Button>
                                </>
                              )}

                              {/* Nota for semInteresse subsection only (no Criar Evento button) */}
                              {activeSection === 'feedback' && group.key === 'semInteresse' && (lead.lead_status === 'Sem Interesse' || unsavedChanges.get(lead.id) === 'Sem Interesse') && (
                                <>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Nota:
                                  </div>
                                  <Input.TextArea
                                    placeholder="Escreva a nota de sem interesse..."
                                    value={seminteresseNotes.get(lead.id) || ''}
                                    onChange={(e) => handleSemInteresseNoteChange(lead.id, e.target.value)}
                                    style={{ fontSize: '11px', minHeight: '60px', borderRadius: '8px' }}
                                    required
                                  />
                                </>
                              )}

                              {/* Descrição for informacaoErrada subsection only (no Criar Evento button) */}
                              {activeSection === 'feedback' && group.key === 'informacaoErrada' && (lead.lead_status === 'Informação Errada' || unsavedChanges.get(lead.id) === 'Informação Errada') && (
                                <>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Descrição:
                                  </div>
                                  <Input.TextArea
                                    placeholder="Escreva a descrição do motivo..."
                                    value={informacaoErradaNotes.get(lead.id) || ''}
                                    onChange={(e) => handleInformacaoErradaNoteChange(lead.id, e.target.value)}
                                    style={{ fontSize: '11px', minHeight: '60px', borderRadius: '8px' }}
                                    required
                                  />
                                </>
                              )}

                              {/* Fim da fidelização for fidelizado subsection only */}
                              {activeSection === 'feedback' && group.key === 'fidelizado' && (lead.lead_status === 'Fidelizado' || unsavedChanges.get(lead.id) === 'Fidelizado') && (
                                <>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Fim da fidelização:
                                  </div>
                                  <Input
                                    type="date"
                                    value={endOfContractDates.get(lead.id) || ''}
                                    onChange={(e) => handleEndOfContractChange(lead.id, e.target.value)}
                                    style={{ fontSize: '11px', height: '32px', borderRadius: '8px', width: '100%' }}
                                    required
                                  />
                                </>
                              )}

                              {/* Sem Cobertura fields for semCobertura subsection only */}
                              {activeSection === 'feedback' && group.key === 'semCobertura' && (lead.lead_status === 'Sem Cobertura' || unsavedChanges.get(lead.id) === 'Sem Cobertura') && (
                                <>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Sem Cobertura de:
                                  </div>
                                  <Select
                                    value={semCoberturaValues.get(lead.id) || ''}
                                    onChange={(value) => handleSemCoberturaChange(lead.id, value)}
                                    style={{ fontSize: '11px', width: '100%', marginBottom: '12px' }}
                                    placeholder="Selecionar operador"
                                    size="small"
                                  >
                                    <Option value="Vodafone">Vodafone</Option>
                                    <Option value="NOS">NOS</Option>
                                    <Option value="MEO">MEO</Option>
                                  </Select>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Morada:
                                  </div>
                                  <Input
                                    value={addressValues.get(lead.id) || lead.address || ''}
                                    onChange={(e) => handleAddressChange(lead.id, e.target.value)}
                                    style={{ fontSize: '11px', marginBottom: '12px', borderRadius: '8px' }}
                                    placeholder="Inserir morada"
                                    size="small"
                                  />
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Cidade:
                                  </div>
                                  <Input
                                    value={cityValues.get(lead.id) || lead.city || ''}
                                    onChange={(e) => handleCityChange(lead.id, e.target.value)}
                                    style={{ fontSize: '11px', marginBottom: '12px', borderRadius: '8px' }}
                                    placeholder="Inserir cidade"
                                    size="small"
                                  />
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Código Postal:
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Input
                                      value={postalCodeFirstPart.get(lead.id) || (lead.postal_code?.split('-')[0] || '')}
                                      onChange={(e) => handlePostalCodeFirstPartChange(lead.id, e.target.value)}
                                      style={{ fontSize: '11px', width: '60px', borderRadius: '8px' }}
                                      placeholder="0000"
                                      maxLength={4}
                                      size="small"
                                    />
                                    <span style={{ fontSize: '11px', color: kbMuted }}>-</span>
                                    <Input
                                      value={postalCodeSecondPart.get(lead.id) || (lead.postal_code?.split('-')[1] || '')}
                                      onChange={(e) => handlePostalCodeSecondPartChange(lead.id, e.target.value)}
                                      style={{ fontSize: '11px', width: '50px', borderRadius: '8px' }}
                                      placeholder="000"
                                      maxLength={3}
                                      size="small"
                                    />
                                  </div>
                                </>
                              )}

                              {/* Documentos Assinados for Validação subsection only */}
                              {activeSection === 'registro' && group.key === 'validacao' && (lead.lead_status === 'Validação' || unsavedChanges.get(lead.id) === 'Validação') && (
                                <>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Documentos Assinados:
                                  </div>
                                  <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => handlePdfUpload(lead)}
                                    style={{ fontSize: '11px', height: '24px', width: '100%', borderRadius: '6px' }}
                                    icon={<FilePdfOutlined />}
                                  >
                                    Gerir PDFs
                                  </Button>
                                </>
                              )}

                              {/* Última tentativa de Contacto Falhada for naoAtendeu subsection only */}
                              {activeSection === 'entrada' && group.key === 'naoAtendeu' && (lead.lead_status === 'Não Atendeu' || unsavedChanges.get(lead.id) === 'Não Atendeu') && (
                                <>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Última tentativa de Contacto Falhada:</span>
                                    <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500 }}>
                                      Tentativas: {contactAttemptCounts.get(lead.id) || 0}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: kbGlassBg, backdropFilter: kbEmbed ? 'none' : 'blur(10px)', padding: '8px', borderRadius: '8px', border: kbGlassBorder }}>
                                    <Input
                                      value={lastContactAttempts.get(lead.id) || ''}
                                      placeholder="Data e hora"
                                      style={{ fontSize: '12px', flex: 1 }}
                                      readOnly
                                      size="small"
                                    />
                                    <Button 
                                      type="primary" 
                                      size="small" 
                                      onClick={() => handleLastContactAttempt(lead.id)}
                                      style={{ fontSize: '12px', minWidth: '60px' }}
                                    >
                                      Set
                                    </Button>
                                  </div>
                                </>
                              )}

                              {/* Gerar Documentos and Ver Documentos for fecho subsection only */}
                              {activeSection === 'registro' && group.key === 'fecho' && (lead.lead_status === 'Fecho' || unsavedChanges.get(lead.id) === 'Fecho') && (
                                <>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: kbFechoHdr, marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Gerar Documentos:</span>
                                    <div style={{ marginLeft: 'auto' }}>
                                      <FechoDocumentModeRadio 
                                        leadId={lead.id} 
                                        value={getFechoDocumentMode(lead.id)} 
                                        onChange={(value) => handleFechoDocumentModeChange(lead.id, value)} 
                                        viewMode="kanban" 
                                      />
                                    </div>
                                  </div>
                                  <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '12px' }}>
                                    {getFechoDocumentMode(lead.id) === '1' ? (
                                      <>
                                        <Button 
                                          type="primary" 
                                          size="small" 
                                          onClick={() => handleFechoDocumentModeChange(lead.id, '2')}
                                          style={{ 
                                            fontSize: '11px', 
                                            height: '24px', 
                                            width: '100%',
                                            backgroundColor: '#722ed1',
                                            borderColor: '#722ed1',
                                            color: '#ffffff'
                                          }}
                                        >
                                          Telecomunicação
                                        </Button>
                                        <Button 
                                          type="primary" 
                                          size="small" 
                                          onClick={() => handleFechoDocumentModeChange(lead.id, '3')}
                                          style={{ 
                                            fontSize: '11px', 
                                            height: '24px', 
                                            width: '100%',
                                            backgroundColor: '#722ed1',
                                            borderColor: '#722ed1',
                                            color: '#ffffff'
                                          }}
                                        >
                                          Segurança
                                        </Button>
                                        <Button 
                                          type="primary" 
                                          size="small" 
                                          onClick={() => handleFechoDocumentModeChange(lead.id, '4')}
                                          style={{ 
                                            fontSize: '11px', 
                                            height: '24px', 
                                            width: '100%',
                                            backgroundColor: '#722ed1',
                                            borderColor: '#722ed1',
                                            color: '#ffffff'
                                          }}
                                        >
                                          Energia
                                        </Button>
                                      </>
                                    ) : getFechoDocumentMode(lead.id) === '3' ? (
                                      <>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                          <Button 
                                            type="primary" 
                                            size="small" 
                                            onClick={() => handleNOSSecuritasDocument(lead)}
                                            style={{ 
                                              fontSize: '11px', 
                                              height: '24px', 
                                              width: '100%',
                                              backgroundColor: '#722ed1',
                                              borderColor: '#722ed1',
                                              color: '#ffffff'
                                            }}
                                          >
                                            NOS Securitas
                                          </Button>
                                          <Button 
                                            type="primary" 
                                            size="small" 
                                            onClick={() => handleProsegurForm(lead)}
                                            style={{ 
                                              fontSize: '11px', 
                                              height: '24px', 
                                              width: '100%',
                                              backgroundColor: '#52c41a',
                                              borderColor: '#52c41a',
                                              color: '#ffffff'
                                            }}
                                          >
                                            Prosegur
                                          </Button>
                                        </Space>
                                      </>
                                    ) : getFechoDocumentMode(lead.id) === '4' ? (
                                      <>
                                        <Button 
                                          type="primary" 
                                          size="small" 
                                          onClick={() => handleEnergyProposal(lead, (lead as any).marcacao_id)}
                                          style={{ 
                                            fontSize: '11px', 
                                            height: '24px', 
                                            width: '100%',
                                            backgroundColor: '#722ed1',
                                            borderColor: '#722ed1',
                                            color: '#ffffff'
                                          }}
                                        >
                                          Pedir Proposta Energia/Solar
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <Button 
                                          type="primary" 
                                          size="small" 
                                          onClick={() => handleVodafoneDocument(lead)}
                                          className="action-btn-vodafone"
                                          style={{ fontSize: '11px', height: '24px', width: '100%' }}
                                        >
                                          Vodafone
                                        </Button>
                                        {group.key === 'fecho' ? (
                                          <>
                                            <Button
                                              type="primary"
                                              size="small"
                                              onClick={() => handleAlticeResidencialClick(lead)}
                                              className="action-btn-meo"
                                              style={{ 
                                                fontSize: '11px', 
                                                height: '24px', 
                                                width: '100%',
                                                backgroundColor: '#ff6b00',
                                                borderColor: '#ff6b00',
                                                color: '#ffffff'
                                              }}
                                            >
                                              MEO
                                            </Button>
                                            {user && user.role && hasRole(user, ['Admin', 'Diretor Comercial', 'Team Leader', 'team_leader', 'Gestor de Equipa']) && (
                                              <div style={{ position: 'relative' }}>
                                                <div style={{ position: 'absolute', top: '-2px', left: '-2px', right: '-2px', bottom: '-2px', borderRadius: '6px', background: 'linear-gradient(45deg, #6366f1, #818cf8, #a5b4fc, #c7d2fe)', backgroundSize: '400% 400%', animation: 'pulse 2s ease-in-out infinite', zIndex: 0 }} />
                                                <Button
                                                  type="primary"
                                                  size="small"
                                                  onClick={() => handleMeoPortalClick()}
                                                  className="action-btn-meo-portal"
                                                  style={{ 
                                                    fontSize: '11px', 
                                                    height: '24px', 
                                                    width: '100%', 
                                                    position: 'relative', 
                                                    zIndex: 1,
                                                    backgroundColor: '#6366f1',
                                                    borderColor: '#6366f1',
                                                    color: '#ffffff'
                                                  }}
                                                >
                                                  MEO
                                                </Button>
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          user && user.role && hasRole(user, ['Admin', 'Diretor Comercial', 'Team Leader', 'team_leader']) ? (
                                            <div style={{ position: 'relative' }}>
                                              <div style={{ position: 'absolute', top: '-2px', left: '-2px', right: '-2px', bottom: '-2px', borderRadius: '6px', background: 'linear-gradient(45deg, #6366f1, #818cf8, #a5b4fc, #c7d2fe)', backgroundSize: '400% 400%', animation: 'pulse 2s ease-in-out infinite', zIndex: 0 }} />
                                              <Button
                                                type="primary"
                                                size="small"
                                                onClick={() => handleMeoPortalClick()}
                                                className="action-btn-meo-portal"
                                                style={{ 
                                                  fontSize: '11px', 
                                                  height: '24px', 
                                                  width: '100%', 
                                                  position: 'relative', 
                                                  zIndex: 1,
                                                  backgroundColor: '#6366f1',
                                                  borderColor: '#6366f1',
                                                  color: '#ffffff'
                                                }}
                                              >
                                                MEO
                                              </Button>
                                            </div>
                                          ) : (
                                            <Button
                                              type="primary"
                                              size="small"
                                              onClick={() => handleAlticeResidencialClick(lead)}
                                              className="action-btn-meo"
                                              style={{ 
                                                fontSize: '11px', 
                                                height: '24px', 
                                                width: '100%',
                                                backgroundColor: '#ff6b00',
                                                borderColor: '#ff6b00',
                                                color: '#ffffff'
                                              }}
                                            >
                                              MEO
                                            </Button>
                                          )
                                        )}
                                        <Button 
                                          type="primary" 
                                          size="small" 
                                          onClick={() => handleNOSDocument(lead)}
                                          className="action-btn-nos"
                                          style={{ 
                                            fontSize: '11px', 
                                            height: '24px', 
                                            width: '100%',
                                            backgroundColor: '#00a651',
                                            borderColor: '#00a651',
                                            color: '#ffffff'
                                          }}
                                        >
                                          NOS
                                        </Button>
                                      </>
                                    )}
                                  </Space>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Ver Documentos:
                                  </div>
                                  <Button 
                                    type="default" 
                                    size="small" 
                                    onClick={() => handleViewDocuments(lead)}
                                    style={{ fontSize: '11px', height: '24px', width: '100%' }}
                                  >
                                    Documentos
                                  </Button>
                                </>
                              )}

                              {/* Criar Evento and Documentos for porInstalar subsection only */}
                              {activeSection === 'instalados' && group.key === 'porInstalar' && (lead.lead_status === 'Por Instalar' || unsavedChanges.get(lead.id) === 'Por Instalar') && (
                                <>
                                  <Button 
                                    type="primary" 
                                    size="small" 
                                    onClick={() => handleCreateEventForInstalacao(lead)}
                                    disabled={creatingEvent === lead.id}
                                    loading={creatingEvent === lead.id}
                                    className="action-btn-create-event"
                                    style={{ 
                                      fontSize: '11px', 
                                      height: '24px', 
                                      width: '100%', 
                                      marginBottom: '12px', 
                                      borderRadius: '6px',
                                      '--section-color': getButtonColors('createEvent').bg
                                    } as React.CSSProperties & { '--section-color': string }}
                                    title="Criar evento de instalação"
                                  >
                                    Criar Evento
                                  </Button>
                                  {user && (user.role === 'team_leader' || user.role === 'Team Leader' || user.role === 'admin' || user.role === 'Admin' || user.role === 'super_admin' || user.role === 'Diretor Comercial' || user.role === 'Gestor de Equipa') && (
                                    <Button 
                                      type="default" 
                                      size="small" 
                                      icon={<FileTextOutlined />}
                                      onClick={() => {
                                        setSelectedLeadForDocumentsList(lead);
                                        setDocumentsModalVisible(true);
                                      }}
                                      style={{ fontSize: '11px', height: '24px', width: '100%' }}
                                      title="Ver documentos do lead"
                                    >
                                      Documentos
                                    </Button>
                                  )}
                                </>
                              )}

                              {/* Business Area Radio and Product Selection for instalados subsection only */}
                              {activeSection === 'instalados' && group.key === 'instalados' && (lead.lead_status === 'Instalados' || unsavedChanges.get(lead.id) === 'Instalados') && (
                                <>
                                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Área de Negócio:
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                                    <InstaladosBusinessAreaRadio
                                      leadId={lead.id}
                                      value={getInstaladosBusinessArea(lead.id)}
                                      onChange={(value) => handleInstaladosBusinessAreaChange(lead.id, value)}
                                      viewMode="kanban"
                                    />
                                  </div>
                                  <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '12px' }}>
                                    {getInstaladosBusinessArea(lead.id) === '1' ? (
                                      <>
                                        <Button 
                                          type="primary" 
                                          size="small" 
                                          onClick={() => handleInstaladosBusinessAreaChange(lead.id, '2')}
                                          style={{ 
                                            fontSize: '11px', 
                                            height: '24px', 
                                            width: '100%',
                                            backgroundColor: '#722ed1',
                                            borderColor: '#722ed1',
                                            color: '#ffffff'
                                          }}
                                        >
                                          Telecomunicação
                                        </Button>
                                        <Button 
                                          type="primary" 
                                          size="small" 
                                          onClick={() => handleInstaladosBusinessAreaChange(lead.id, '3')}
                                          style={{ 
                                            fontSize: '11px', 
                                            height: '24px', 
                                            width: '100%',
                                            backgroundColor: '#722ed1',
                                            borderColor: '#722ed1',
                                            color: '#ffffff'
                                          }}
                                        >
                                          Segurança
                                        </Button>
                                        <Button 
                                          type="primary" 
                                          size="small" 
                                          onClick={() => handleInstaladosBusinessAreaChange(lead.id, '4')}
                                          style={{ 
                                            fontSize: '11px', 
                                            height: '24px', 
                                            width: '100%',
                                            backgroundColor: '#722ed1',
                                            borderColor: '#722ed1',
                                            color: '#ffffff'
                                          }}
                                        >
                                          Energia
                                        </Button>
                                      </>
                                    ) : getInstaladosBusinessArea(lead.id) !== '1' && (
                                    <>
                                      {(() => {
                                        const businessArea = getInstaladosBusinessArea(lead.id);
                                        const areaLabels: { [key: string]: string } = {
                                          '2': 'Telecomunicação',
                                          '3': 'Segurança',
                                          '4': 'Energia / Solar'
                                        };
                                        const currentValue = getInstallationDate(lead.id, businessArea);
                                        return (
                                          <>
                                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginTop: '12px', marginBottom: '6px' }}>
                                              Data da Instalação ({areaLabels[businessArea] || 'Instalação'}):
                                            </div>
                                            <DatePicker
                                              placeholder="Selecionar data"
                                              size="small"
                                              style={{ width: '100%', marginBottom: '12px', borderRadius: '8px' }}
                                              defaultValue={currentValue ? moment(currentValue) : undefined}
                                              onChange={(date) => {
                                                if (date) {
                                                  handleInstallationDateChange(lead.id, businessArea, date.format('YYYY-MM-DD'));
                                                } else {
                                                  handleInstallationDateChange(lead.id, businessArea, '');
                                                }
                                              }}
                                            />
                                          </>
                                        );
                                      })()}
                                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                        Selecionar Produtos:
                                      </div>
                                      <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: 4, padding: 8, marginBottom: '12px' }}>
                                        {(() => {
                                          const businessArea = getInstaladosBusinessArea(lead.id);
                                          const selectedProducts = getInstaladosSelectedProducts(lead.id);
                                          const areaMap: { [key: string]: string } = {
                                            '2': 'telecomunicacao',
                                            '3': 'seguranca',
                                            '4': 'energia'
                                          };
                                          const businessAreaKey = areaMap[businessArea] || '';
                                          const areaSections = businessAreaKey ? salesSections.filter(s => s.business_area === businessAreaKey && s.is_active) : [];
                                          const sectionIds = areaSections.map(s => s.id);
                                          const areaProducts = businessAreaKey ? salesProducts.filter(p => sectionIds.includes(p.section_id) && p.is_active) : [];
                                          
                                          // Group products by section
                                          const productsBySection = new Map<number, typeof areaProducts>();
                                          areaProducts.forEach(product => {
                                            const sectionId = product.section_id;
                                            if (!productsBySection.has(sectionId)) {
                                              productsBySection.set(sectionId, []);
                                            }
                                            productsBySection.get(sectionId)!.push(product);
                                          });
                                          
                                          return (
                                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                              {areaProducts.length === 0 ? (
                                                <Text type="secondary" style={{ fontSize: '11px' }}>Sem produtos disponíveis</Text>
                                              ) : (
                                                Array.from(productsBySection.entries()).map(([sectionId, products]) => {
                                                  const section = salesSections.find(s => s.id === sectionId);
                                                  return (
                                                    <div key={sectionId} style={{ marginBottom: '8px' }}>
                                                      {section && (
                                                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#666', marginBottom: '4px', textTransform: 'uppercase' }}>
                                                          {section.name}
                                                        </div>
                                                      )}
                                                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                        {products.map(product => {
                                                          const isSelected = selectedProducts.includes(product.id);
                                                          return (
                                                            <div
                                                              key={product.id}
                                                              onClick={() => handleInstaladosProductToggle(lead.id, product.id)}
                                                              style={{
                                                                padding: '4px 8px',
                                                                borderRadius: 4,
                                                                cursor: 'pointer',
                                                                backgroundColor: isSelected ? 'rgba(24, 144, 255, 0.2)' : 'rgba(0, 0, 0, 0.02)',
                                                                border: `1px solid ${isSelected ? '#1890ff' : 'rgba(0, 0, 0, 0.1)'}`,
                                                                fontSize: '11px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 6
                                                              }}
                                                            >
                                                              <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleInstaladosProductToggle(lead.id, product.id)}
                                                                style={{ margin: 0, cursor: 'pointer' }}
                                                              />
                                                              <span style={{ flex: 1 }}>
                                                                {product.name}
                                                              </span>
                                                              <span style={{ color: '#52c41a', fontWeight: 600, marginLeft: '8px' }}>
                                                                {product.commission || 0}pts
                                                              </span>
                                                            </div>
                                                          );
                                                        })}
                                                      </Space>
                                                    </div>
                                                  );
                                                })
                                              )}
                                            </Space>
                                          );
                                        })()}
                                      </div>
                                    </>
                                  )}
                                  </Space>
                                </>
                              )}
                            </div>
                            
                            {/* Action buttons for Kanban card (same as table view) */}
                            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${kbBorder}` }}>
                              <Space size={4} wrap>
                                <Tooltip title={workspaceEmbed && onOpenLeadInWorkspace ? 'Abrir no workspace de cliente' : 'Ver detalhes'}>
                                  <Button 
                                    type="text" 
                                    icon={<EyeOutlined />} 
                                    size="small"
                                    onClick={() => handleViewLead(lead)}
                                    className="action-btn-view"
                                    style={{ fontSize: '12px' }}
                                  />
                                </Tooltip>
                                <Tooltip title="Notas">
                                  <Button 
                                    type="primary" 
                                    icon={<PaperClipOutlined />} 
                                    size="small"
                                    onClick={() => handleOpenNotes(lead)}
                                    className="action-btn-notes"
                                    style={{ 
                                      fontSize: '11px', 
                                      height: '24px',
                                      '--section-color': getButtonColors('notes').bg
                                    } as React.CSSProperties & { '--section-color': string }}
                                  />
                                </Tooltip>
                                <Tooltip title="Criar Tarefa">
                                  <Button 
                                    type="text" 
                                    icon={<CrownOutlined />} 
                                    size="small"
                                    onClick={() => handleOpenTaskModal(lead)}
                                    className="action-btn-task"
                                    style={{ fontSize: '12px' }}
                                  />
                                </Tooltip>
                                {userMayUseClientManagementEmailButton(user) ? (
                                  <Tooltip title="Enviar Email">
                                    <Button
                                      type="text"
                                      icon={<MailOutlined />}
                                      size="small"
                                      onClick={() => handleSendEmail(lead)}
                                      className="action-btn-email"
                                      style={{ fontSize: '12px' }}
                                    />
                                  </Tooltip>
                                ) : null}
                                {activeSection === 'feedback' && group.key === 'informacaoErrada' && user && user.role && ['Admin', 'Diretor Comercial', 'Team Leader', 'Gestor de Equipa'].includes(user.role) ? (
                                  <Tooltip title="Editar lead">
                                    <Button 
                                      type="text" 
                                      icon={<EditOutlined />} 
                                      size="small"
                                      onClick={() => handleEditLead(lead)}
                                      className="action-btn-edit"
                                      style={{ fontSize: '12px' }}
                                    />
                                  </Tooltip>
                                ) : null}
                                {((['feedback', 'registro'].includes(activeSection)) || (activeSection === 'entrada' && lead.lead_status === 'Não Atendeu') || (activeSection === 'instalados' && group.key === 'porInstalar' && (lead.lead_status === 'Por Instalar' || unsavedChanges.get(lead.id) === 'Por Instalar'))) ? (
                                  <Button 
                                    size="small" 
                                    onClick={() => handleRevertLead(lead)}
                                    className="action-btn-revert"
                                    style={{ fontSize: '11px' }}
                                  >
                                    Reverter
                                  </Button>
                                ) : null}
                                <Tooltip title="Nova Venda">
                                  <Button 
                                    type="primary" 
                                    icon={<ShoppingCartOutlined />} 
                                    size="small"
                                    onClick={() => handleOpenVendaForLead(lead)}
                                    style={{ 
                                      fontSize: '11px', 
                                      height: '24px',
                                      backgroundColor: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1',
                                      borderColor: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1'
                                    }}
                                  >
                                    Venda
                                  </Button>
                                </Tooltip>
                              </Space>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                  <div style={{ marginTop: '12px', textAlign: 'center' }}>
                    <Pagination
                      current={group.currentPage}
                      total={group.totalLeads}
                      pageSize={LEADS_PER_PAGE}
                      onChange={(page) => handleSubsectionPageChange(group.key, page)}
                      size="small"
                      showSizeChanger={false}
                      showQuickJumper={false}
                      showTotal={(total, range) => `${range[0]}-${range[1]} de ${total}`}
                    />
                  </div>

                </Collapse.Panel>
              ))}
            </Collapse>
      </div>
    );
  };

  const handleCloseModal = () => {
    setViewModalVisible(false);
    setSelectedLead(null);
  };

  const handleVodafoneDocument = async (lead: Lead) => {
    // Set the lead immediately for quick display
    setSelectedLeadForDocuments(lead);
    setVodafoneWizardVisible(true);
    
    // Fetch fresh lead data from API to ensure we have the latest information for autofill
    try {
      const freshLead = await apiService.getLead(lead.id);
      setSelectedLeadForDocuments(freshLead);
    } catch (error) {
      console.error('Error fetching fresh lead data for Vodafone documents:', error);
      // Keep the original lead data if fetch fails
    }
  };

  const handleCloseVodafoneWizard = () => {
    setVodafoneWizardVisible(false);
    setSelectedLeadForDocuments(null);
  };

  const handleViewDocuments = (lead: Lead) => {
    setSelectedLeadForDocumentsList(lead);
    setDocumentsModalVisible(true);
  };

  const handleCloseDocumentsModal = () => {
    setDocumentsModalVisible(false);
    setSelectedLeadForDocumentsList(null);
  };

  const refreshDocumentsModal = () => {
    // Trigger a refresh by incrementing the trigger
    setDocumentsRefreshTrigger(prev => prev + 1);
  };

  // Handle pendente reason changes
  const handlePendenteReasonChange = (leadId: number, reason: string) => {
    setPendenteReasons(prev => new Map(prev).set(leadId, reason));
    setPendenteReasonsChanged(prev => new Set(prev).add(leadId));
    setUnsavedChanges(prev => new Map(prev).set(leadId, 'Pendente'));
  };

  // Handle end of contract date changes
  const handleEndOfContractChange = (leadId: number, date: string) => {
    setEndOfContractDates(prev => new Map(prev).set(leadId, date));
    setEndOfContractDatesChanged(prev => new Set(prev).add(leadId));
    setUnsavedChanges(prev => new Map(prev).set(leadId, 'Fidelizado'));
  };

  // Handle creating event for pendente lead
  const handleCreateEventForPendente = async (lead: Lead) => {
    setSelectedLeadForEvent(lead as Lead & { title?: string; activity_type?: string });
    setEventModalVisible(true);
  };

  const handleCreateEventForInstalacao = async (lead: Lead) => {
    // Create an object with the lead data plus the event pre-fill data
    const eventData = {
      ...lead,
      // Pre-fill the event data for installation
      title: `Instalação: ${lead.name}`,
      activity_type: 'installation'
    } as Lead & { title: string; activity_type: string };
    
    setSelectedLeadForEvent(eventData);
    setEventModalVisible(true);
  };

  const handleEventModalClose = () => {
    setEventModalVisible(false);
    setSelectedLeadForEvent(null);
  };

  const handleEventModalSuccess = () => {
    // Optionally refresh leads or show success message
    message.success('Evento criado com sucesso!');
  };

  const keepChanges = () => {
    setShowUnsavedModal(false);
    setPendingSection(null);
  };

  const discardChanges = () => {
    setUnsavedChanges(new Map());
    setUnsavedSectionChanges(new Map());
    setOriginalStatusForDisplay(new Map());
    setShowUnsavedModal(false);
    if (pendingSection) {
      setActiveSection(pendingSection);
      setPendingSection(null);
    }
  };

  // Handler for MEO button click (open real site in new window)
  const handleMeoPortalClick = () => {
    window.open('http://my.animatica.pt', '_blank', 'noopener,width=1200,height=900');
  };

  // Handler for Altice Residencial MEO button click (open modal with form)
  const handleAlticeResidencialClick = async (lead: Lead) => {
    // Set the lead immediately for quick display
    setSelectedLead(lead);
    setAlticeResidencialModalVisible(true);
    
    // Fetch fresh lead data from API to ensure we have the latest information for autofill
    try {
      const freshLead = await apiService.getLead(lead.id);
      setSelectedLead(freshLead);
    } catch (error) {
      console.error('Error fetching fresh lead data for Altice Residencial:', error);
      // Keep the original lead data if fetch fails
    }
  };

  // Handler to close Altice Residencial modal
  const handleCloseAlticeResidencialModal = () => {
    setAlticeResidencialModalVisible(false);
  };

  // --- Main content area ultra-wide and dense ---
  // Add dense table row styles
  // Place this at the top of the return block
  // (React allows <style> inside JSX)

  // Add NOS wizard state
  const [nosWizardVisible, setNOSWizardVisible] = useState(false);
  const [selectedLeadForNOSDocuments, setSelectedLeadForNOSDocuments] = useState<Lead | null>(null);

  // Add NOS Securitas wizard state
  const [nosSecuritasWizardVisible, setNOSSecuritasWizardVisible] = useState(false);
  const [selectedLeadForNOSSecuritasDocuments, setSelectedLeadForNOSSecuritasDocuments] = useState<Lead | null>(null);

  // Fix handleNOSDocument and handleCloseNOSWizard
  const handleNOSDocument = async (lead: Lead) => {
    // Set the lead immediately for quick display
    setSelectedLeadForNOSDocuments(lead);
    setNOSWizardVisible(true);
    
    // Fetch fresh lead data from API to ensure we have the latest information for autofill
    try {
      const freshLead = await apiService.getLead(lead.id);
      setSelectedLeadForNOSDocuments(freshLead);
    } catch (error) {
      console.error('Error fetching fresh lead data for NOS documents:', error);
      // Keep the original lead data if fetch fails
    }
  };
  const handleCloseNOSWizard = () => {
    setNOSWizardVisible(false);
    setSelectedLeadForNOSDocuments(null);
  };

  // Handler for NOS Securitas button click (open wizard)
  const handleNOSSecuritasDocument = async (lead: Lead) => {
    // Set the lead immediately for quick display
    setSelectedLeadForNOSSecuritasDocuments(lead);
    setNOSSecuritasWizardVisible(true);
    
    // Fetch fresh lead data from API to ensure we have the latest information for autofill
    try {
      const freshLead = await apiService.getLead(lead.id);
      setSelectedLeadForNOSSecuritasDocuments(freshLead);
    } catch (error) {
      console.error('Error fetching fresh lead data for NOS Securitas documents:', error);
      // Keep the original lead data if fetch fails
    }
  };

  const handleCloseNOSSecuritasWizard = () => {
    setNOSSecuritasWizardVisible(false);
    setSelectedLeadForNOSSecuritasDocuments(null);
  };

  // Prosegur form state
  const [prosegurFormVisible, setProsegurFormVisible] = useState(false);
  const [selectedLeadForProsegur, setSelectedLeadForProsegur] = useState<Lead | null>(null);

  // Handler for Prosegur button click
  const handleProsegurForm = async (lead: Lead) => {
    // Set the lead immediately for quick display
    setSelectedLeadForProsegur(lead);
    setProsegurFormVisible(true);
    
    // Fetch fresh lead data from API to ensure we have the latest information for autofill
    try {
      const freshLead = await apiService.getLead(lead.id);
      setSelectedLeadForProsegur(freshLead);
    } catch (error) {
      console.error('Error fetching fresh lead data for Prosegur form:', error);
      // Keep the original lead data if fetch fails
    }
  };

  const handleCloseProsegurForm = () => {
    setProsegurFormVisible(false);
    setSelectedLeadForProsegur(null);
  };

  // Energy Proposal Wizard state
  const [energyProposalWizardVisible, setEnergyProposalWizardVisible] = useState(false);
  const [selectedLeadForEnergyProposal, setSelectedLeadForEnergyProposal] = useState<Lead | null>(null);
  const [selectedMarcacaoIdForProposal, setSelectedMarcacaoIdForProposal] = useState<number | null>(null);

  // Handler for Energy Proposal button click
  const handleEnergyProposal = async (lead: Lead, marcacaoId?: number) => {
    // Set the lead immediately for quick display
    setSelectedLeadForEnergyProposal(lead);
    setSelectedMarcacaoIdForProposal(marcacaoId || null);
    setEnergyProposalWizardVisible(true);
    
    // Fetch fresh lead data from API to ensure we have the latest information for autofill
    try {
      const freshLead = await apiService.getLead(lead.id);
      setSelectedLeadForEnergyProposal(freshLead);
    } catch (error) {
      console.error('Error fetching fresh lead data for Energy Proposal:', error);
      // Keep the original lead data if fetch fails
    }
  };

  const handleCloseEnergyProposalWizard = () => {
    setEnergyProposalWizardVisible(false);
    setSelectedLeadForEnergyProposal(null);
    setSelectedMarcacaoIdForProposal(null);
  };

  // PDF Upload Modal handlers
  const handlePdfUpload = (lead: Lead) => {
    setSelectedLeadForPdfUpload(lead);
    setPdfUploadModalVisible(true);
  };

  const handleClosePdfUploadModal = () => {
    setPdfUploadModalVisible(false);
    setSelectedLeadForPdfUpload(null);
  };

  const handleSendEmail = (lead: Lead) => {
    setSelectedLeadForEmail(lead);
    setEmailModalVisible(true);
  };

  const handleCloseEmailModal = () => {
    setEmailModalVisible(false);
    setSelectedLeadForEmail(null);
  };

  // Chat Modal handlers - Disabled
  // const handleMessageLead = async (lead: Lead) => {
  //   if (!lead.assigned_to || !user) {
  //     message.error('Lead não tem responsável atribuído ou utilizador não encontrado');
  //     return;
  //   }

  //   try {
  //     // Check if current user is trying to message themselves
  //     if (lead.assigned_to === user.id) {
  //       message.error('Não pode enviar mensagem para si próprio');
  //       return;
  //     }

  //     // Create or find direct conversation with the lead's responsible user
  //     const response = await httpClient.post('/api/chat/conversations/direct', {
  //       other_user_id: lead.assigned_to
  //     });

  //     if (response.data.success) {
  //       setSelectedLeadForChat(lead);
  //       setSelectedConversationId(response.data.conversation.id);
  //       setChatModalVisible(true);
  //     } else {
  //       message.error(response.data.error || 'Erro ao criar conversa');
  //     }
  //   } catch (error) {
  //     message.error('Erro ao abrir conversa');
  //   }
  // };

  // const handleCloseChatModal = () => {
  //   setChatModalVisible(false);
  //   setSelectedLeadForChat(null);
  //   setSelectedConversationId(null);
  // };

  // Document Validation Modal handlers
  const handleValidarDocumentos = async () => {
    setLoadingValidationLeads(true);
    try {
      // Get all leads in "Validação" status
      const validationLeadsData = safeLeads.filter(lead => 
        lead.lead_status === 'Validação' && 
        lead.section === 'registro'
      );
      
      setValidationLeads(validationLeadsData);
      setValidarDocumentosModalVisible(true);
      // Refresh the document lists when modal opens
      refreshValidationModal();
    } catch (error) {
      message.error('Erro ao carregar leads para validação');
    } finally {
      setLoadingValidationLeads(false);
    }
  };

  const handleCloseValidarDocumentosModal = () => {
    setValidarDocumentosModalVisible(false);
    setValidationLeads([]);
  };

  const refreshValidationModal = () => {
    setValidationRefreshTrigger(prev => prev + 1);
  };

  const handleApproveLead = async (leadId: number) => {
    try {
      // Update the lead status to "Por Instalar" and move to instalados section
      const response = await apiService.updateLead(leadId, {
        lead_status: 'Por Instalar',
        section: 'instalados'
      });
      
      if (response) {
        message.success('Lead aprovado e movido para Instalados');
        // Refresh the validation leads list
        const updatedLeads = validationLeads.filter(lead => lead.id !== leadId);
        setValidationLeads(updatedLeads);
        // Refresh the main leads list
        fetchLeads();
      }
    } catch (error) {
      message.error('Erro ao aprovar lead');
    }
  };

  const handleRejectLead = async (leadId: number) => {
    try {
      // Update the lead status to "Sem Interesse" and move back to feedback section
      const response = await apiService.updateLead(leadId, {
        lead_status: 'Sem Interesse',
        section: 'feedback'
      });
      
      if (response) {
        message.success('Lead rejeitado e movido para Feedback');
        // Refresh the validation leads list
        const updatedLeads = validationLeads.filter(lead => lead.id !== leadId);
        setValidationLeads(updatedLeads);
        // Refresh the main leads list
        fetchLeads();
      }
    } catch (error) {
      message.error('Erro ao rejeitar lead');
    }
  };

  const handlePreviewFile = async (file: any) => {
    try {
      // Open preview modal immediately and show loading state
      setPreviewTitle(file.file_name || file.document_type || 'Documento');
      setPreviewData(''); // Clear previous data
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
        setPreviewBlobUrl('');
      }
      setPreviewVisible(true);
      setPreviewLoading(true);
      
      let response;
      
      // Check if this is a generated document (Vodafone document) or uploaded file
      if (file.document_type || file.template_name) {
        // This is a generated document - use previewDocument
        response = await apiService.previewDocument(file.id);
      } else {
        // This is an uploaded file - use previewLeadFile
        
        // Ensure we have a valid lead_id
        if (!file.lead_id) {
          setPreviewLoading(false);
          message.error('Erro: ID do lead não encontrado');
          return;
        }
        
        response = await apiService.previewLeadFile(file.lead_id, file.id);
      }
      
      // Hide preview loading state
      setPreviewLoading(false);
      
      if (response.success && 'pdf_data' in response) {
        setPreviewData(response.pdf_data);
        
        // Create blob URL for better browser compatibility
        try {
          const binaryString = atob(response.pdf_data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          setPreviewBlobUrl(blobUrl);
        } catch (error) {
        }
      } else if (!response.success && 'error' in response) {
        // Show the specific error message from the backend
        setPreviewVisible(false); // Close modal on error
        message.error(response.error);
      } else {
        setPreviewVisible(false); // Close modal on error
        message.error('Erro ao carregar pré-visualização');
      }
    } catch (error) {
      setPreviewLoading(false);
      setPreviewVisible(false); // Close modal on error
      message.error('Erro ao carregar pré-visualização');
    }
  };

  const handleDownloadFile = async (file: any) => {
    try {
      let response;
      
      // Check if this is a generated document (Vodafone document) or uploaded file
      if (file.document_type || file.template_name) {
        // This is a generated document - use downloadDocument
        response = await apiService.downloadDocument(file.id);
      } else {
        // This is an uploaded file - use downloadLeadFile
        
        // Ensure we have a valid lead_id
        if (!file.lead_id) {
          message.error('Erro: ID do lead não encontrado');
          return;
        }
        
        response = await apiService.downloadLeadFile(file.lead_id, file.id);
      }
      
      if (response.success) {
        // Create a download link and trigger download
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.file_name || file.document_type || 'documento.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        message.success('Download iniciado');
      } else {
        message.error('Erro ao fazer download do ficheiro');
      }
    } catch (error) {
      message.error('Erro ao fazer download do ficheiro');
    }
  };

  // 1. Add followUpNotes state
  const [followUpNotes, setFollowUpNotes] = useState<Map<number, string>>(new Map());
  const [followUpNotesChanged, setFollowUpNotesChanged] = useState<Set<number>>(new Set());
  
      // 1. Add seminteresseNotes state
    const [seminteresseNotes, setSeminteresseNotes] = useState<Map<number, string>>(new Map());
    const [seminteresseNotesChanged, setSeminteresseNotesChanged] = useState<Set<number>>(new Set());

      // 1. Add informacaoErradaNotes state
    const [informacaoErradaNotes, setInformacaoErradaNotes] = useState<Map<number, string>>(new Map());
    const [informacaoErradaNotesChanged, setInformacaoErradaNotesChanged] = useState<Set<number>>(new Set());

    // 1. Add semCoberturaValues state
      const [semCoberturaValues, setSemCoberturaValues] = useState<Map<number, string>>(new Map());
  const [semCoberturaValuesChanged, setSemCoberturaValuesChanged] = useState<Set<number>>(new Set());

  // Sem Cobertura address and city state
  const [addressValues, setAddressValues] = useState<Map<number, string>>(new Map());
  const [addressValuesChanged, setAddressValuesChanged] = useState<Set<number>>(new Set());
  const [cityValues, setCityValues] = useState<Map<number, string>>(new Map());
  const [cityValuesChanged, setCityValuesChanged] = useState<Set<number>>(new Set());
  
  // Sem Cobertura postal code state
  const [postalCodeValues, setPostalCodeValues] = useState<Map<number, string>>(new Map());
  const [postalCodeValuesChanged, setPostalCodeValuesChanged] = useState<Set<number>>(new Set());
  const [postalCodeFirstPart, setPostalCodeFirstPart] = useState<Map<number, string>>(new Map());
  const [postalCodeSecondPart, setPostalCodeSecondPart] = useState<Map<number, string>>(new Map());

  // 2. Add getFollowUpColumns (like getPendenteColumns)
  const getFollowUpColumns = () => {
    const cols = getDefaultColumns();
    cols.splice(2, 0, {
      title: 'Nota',
      key: 'followup_note',
      render: (text: string, record: Lead) => (
        <Input.TextArea
          placeholder="Escreva a nota de follow-up..."
          value={followUpNotes.get(record.id) || ''}
          onChange={(e) => handleFollowUpNoteChange(record.id, e.target.value)}
          style={{ fontSize: '11px', minHeight: '60px' }}
          required
        />
      )
    });
    cols.splice(3, 0, {
      title: 'Criar Evento',
      key: 'create_event_followup',
      render: (text: string, record: Lead) => {
        const note = followUpNotes.get(record.id);
        const hasNote = note && note.trim().length > 0;
        return (
          <Button 
            type="primary" 
            size="small" 
            onClick={() => handleCreateEventForFollowUp(record)}
            disabled={!hasNote || creatingEvent === record.id}
            loading={creatingEvent === record.id}
            className="action-btn-create-event"
            style={{ 
              fontSize: '11px', 
              height: '24px',
              '--section-color': getButtonColors('createEvent').bg
            } as React.CSSProperties & { '--section-color': string }}
            title={!hasNote ? "Deve escrever a nota de follow-up primeiro" : "Criar evento de follow-up"}
          >
            Criar Evento
          </Button>
        );
      }
    });
    return cols;
  };

  // 2. Add getSemInteresseColumns (like getFollowUpColumns but without Criar Evento)
  const getSemInteresseColumns = () => {
    const cols = getDefaultColumns();
    cols.splice(2, 0, {
      title: 'Nota',
      key: 'seminteresse_note',
      render: (text: string, record: Lead) => (
        <Input.TextArea
          placeholder="Escreva a nota de sem interesse..."
          value={seminteresseNotes.get(record.id) || ''}
          onChange={(e) => handleSemInteresseNoteChange(record.id, e.target.value)}
          style={{ fontSize: '11px', minHeight: '60px' }}
          required
        />
      )
    });
    return cols;
  };

  // 2. Add getInformacaoErradaColumns (like getSemInteresseColumns but for informacao errada)
  const getInformacaoErradaColumns = () => {
    const cols = getDefaultColumns();
    cols.splice(2, 0, {
      title: 'Descrição:',
      key: 'informacao_errada_note',
      render: (text: string, record: Lead) => (
        <Input.TextArea
          placeholder="Escreva a descrição do motivo..."
          value={informacaoErradaNotes.get(record.id) || ''}
          onChange={(e) => handleInformacaoErradaNoteChange(record.id, e.target.value)}
          style={{ fontSize: '11px', minHeight: '60px' }}
          required
        />
      )
    });
    
    // Replace the default Ações column with custom one that includes edit button
    const actionsColumnIndex = cols.findIndex(col => col.key === 'actions');
    if (actionsColumnIndex !== -1) {
      cols[actionsColumnIndex] = {
        title: 'Ações',
        key: 'actions',
        render: (text: string, record: Lead) => (
          <Space>
            <Tooltip title={workspaceEmbed && onOpenLeadInWorkspace ? 'Abrir no workspace de cliente' : 'Ver detalhes'}>
              <Button 
                type="text" 
                icon={<EyeOutlined />} 
                size="small"
                onClick={() => handleViewLead(record)}
                className="action-btn-view"
              />
            </Tooltip>
            <Tooltip title="Notas">
              <Button 
                type="primary" 
                icon={<PaperClipOutlined />} 
                size="small"
                onClick={() => handleOpenNotes(record)}
                className="action-btn-notes"
                style={{ 
                  fontSize: '11px', 
                  height: '24px',
                  '--section-color': getButtonColors('notes').bg
                } as React.CSSProperties & { '--section-color': string }}
              />
            </Tooltip>
            <Tooltip title="Criar Tarefa">
              <Button 
                type="text" 
                icon={<CrownOutlined />} 
                size="small"
                onClick={() => handleOpenTaskModal(record)}
                className="action-btn-task"
              />
            </Tooltip>
            {/* Show edit button only for Gestor de Equipa and up */}
            {user && user.role && hasRole(user, ['Admin', 'Diretor Comercial', 'Team Leader', 'team_leader', 'Gestor de Equipa']) && (
              <Tooltip title="Editar lead">
                <Button 
                  type="text" 
                  icon={<EditOutlined />} 
                  size="small"
                  onClick={() => handleEditLead(record)}
                  className="action-btn-edit"
                />
              </Tooltip>
            )}
            {(['feedback', 'registro'].includes(activeSection) || (activeSection === 'instalados' && record.lead_status === 'Por Instalar')) && (
              <Button 
                size="small" 
                onClick={() => handleRevertLead(record)}
                className="action-btn-revert"
              >
                Reverter
              </Button>
            )}
            <Tooltip title="Nova Venda">
              <Button 
                type="primary" 
                icon={<ShoppingCartOutlined />} 
                size="small"
                onClick={() => handleOpenVendaForLead(record)}
                style={{ 
                  fontSize: '11px', 
                  height: '24px',
                  backgroundColor: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1',
                  borderColor: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1'
                }}
              >
                Venda
              </Button>
            </Tooltip>
          </Space>
        )
      };
    }
    
    return cols;
  };

  // 2. Add getFidelizadoColumns (like getSemInteresseColumns but for end_of_contract)
  const getFidelizadoColumns = () => {
    const cols = getDefaultColumns();
    cols.splice(2, 0, {
      title: 'Fim da fidelização',
      key: 'end_of_contract',
      render: (text: string, record: Lead) => (
        <Input
          type="date"
          value={endOfContractDates.get(record.id) || ''}
          onChange={(e) => handleEndOfContractChange(record.id, e.target.value)}
          style={{ fontSize: '11px' }}
          required
        />
      )
    });
    return cols;
  };

  // Add getSemCoberturaColumns for the Sem Cobertura subsection
  const getSemCoberturaColumns = () => {
    const cols = getDefaultColumns();
    cols.splice(2, 0, {
      title: 'Sem Cobertura de:',
      key: 'sem_cobertura_de',
      render: (text: string, record: Lead) => (
        <Select
          value={semCoberturaValues.get(record.id) || ''}
          onChange={(value) => handleSemCoberturaChange(record.id, value)}
          className="modern-status-select"
          style={{ width: '100%' }}
          size="small"
          placeholder="Selecionar operador"
        >
          <Option value="Vodafone">Vodafone</Option>
          <Option value="NOS">NOS</Option>
          <Option value="MEO">MEO</Option>
        </Select>
      )
    });
    
    // Add Morada column
    cols.splice(3, 0, {
      title: 'Morada',
      dataIndex: 'address',
      key: 'address',
      render: (text: string, record: Lead) => (
        <Input
          value={addressValues.get(record.id) || record.address || ''}
          onChange={(e) => handleAddressChange(record.id, e.target.value)}
          style={{ fontSize: '11px' }}
          placeholder="Inserir morada"
        />
      )
    });
    
    // Add Cidade column
    cols.splice(4, 0, {
      title: 'Cidade',
      dataIndex: 'city',
      key: 'city',
      render: (text: string, record: Lead) => (
        <Input
          value={cityValues.get(record.id) || record.city || ''}
          onChange={(e) => handleCityChange(record.id, e.target.value)}
          style={{ fontSize: '11px' }}
          placeholder="Inserir cidade"
        />
      )
    });
    
    // Add Código Postal column
    cols.splice(5, 0, {
      title: 'Código Postal',
      dataIndex: 'postal_code',
      key: 'postal_code',
      render: (text: string, record: Lead) => {
        const currentValue = postalCodeValues.get(record.id) || record.postal_code || '';
        const [firstPart, secondPart] = currentValue.split('-');
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Input
              value={postalCodeFirstPart.get(record.id) || firstPart || ''}
              onChange={(e) => handlePostalCodeFirstPartChange(record.id, e.target.value)}
              style={{ fontSize: '11px', width: '60px' }}
              placeholder="0000"
              maxLength={4}
            />
            <span style={{ fontSize: '11px' }}>-</span>
            <Input
              value={postalCodeSecondPart.get(record.id) || secondPart || ''}
              onChange={(e) => handlePostalCodeSecondPartChange(record.id, e.target.value)}
              style={{ fontSize: '11px', width: '50px' }}
              placeholder="000"
              maxLength={3}
            />
          </div>
        );
      }
    });
    
    return cols;
  };

  // Add getValidacaoColumns for the Validação subsection
  const getValidacaoColumns = () => {
    const cols = getDefaultColumns();
    cols.splice(2, 0, {
      title: 'Documentos Assinados',
      key: 'documentos_assinados',
      render: (text: string, record: Lead) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handlePdfUpload(record)}
          style={{ fontSize: '11px', height: '24px' }}
          icon={<FilePdfOutlined />}
        >
          Gerir PDFs
        </Button>
      )
    });
    return cols;
  };

  // Add getPorInstalarColumns for the Por Instalar subsection
  const getPorInstalarColumns = () => {
    const cols = getDefaultColumns();
    cols.splice(2, 0, {
      title: 'Criar Evento',
      key: 'create_instalacao_event',
      render: (text: string, record: Lead) => (
        <Button 
          type="primary" 
          size="small" 
          onClick={() => handleCreateEventForInstalacao(record)}
          disabled={creatingEvent === record.id}
          loading={creatingEvent === record.id}
          style={{ fontSize: '11px', height: '24px' }}
          title="Criar evento de instalação"
        >
          Criar Evento
        </Button>
      )
    });
    return cols;
  };


  // 3. Add handler for followUpNotes
  const handleFollowUpNoteChange = (leadId: number, note: string) => {

    setFollowUpNotes(prev => new Map(prev).set(leadId, note));
    setFollowUpNotesChanged(prev => new Set(prev).add(leadId));
    setUnsavedChanges(prev => new Map(prev).set(leadId, 'Follow-up'));
  };

  // Add handler for installation date changes per business area
  const handleInstallationDateChange = (leadId: number, businessArea: string, date: string) => {
    setInstallationDates(prev => {
      const newMap = new Map(prev);
      const leadDates = newMap.get(leadId) || new Map<string, string>();
      if (date) {
        leadDates.set(businessArea, date);
      } else {
        leadDates.delete(businessArea);
      }
      newMap.set(leadId, leadDates);
      return newMap;
    });
    setInstallationDatesChanged(prev => new Set(prev).add(leadId));
  };
  
  // Helper to get installation date for a specific business area
  const getInstallationDate = (leadId: number, businessArea: string): string | undefined => {
    const leadDates = installationDates.get(leadId);
    return leadDates?.get(businessArea);
  };

  // 3. Add handler for seminteresseNotes
  const handleSemInteresseNoteChange = (leadId: number, note: string) => {

    setSeminteresseNotes(prev => new Map(prev).set(leadId, note));
    setSeminteresseNotesChanged(prev => new Set(prev).add(leadId));
    setUnsavedChanges(prev => new Map(prev).set(leadId, 'Sem Interesse'));
  };

  // 3. Add handler for informacaoErradaNotes
      const handleInformacaoErradaNoteChange = (leadId: number, note: string) => {

      setInformacaoErradaNotes(prev => new Map(prev).set(leadId, note));
      setInformacaoErradaNotesChanged(prev => new Set(prev).add(leadId));
      setUnsavedChanges(prev => new Map(prev).set(leadId, 'Informação Errada'));
    };

    // Add handler for semCoberturaValues
    const handleSemCoberturaChange = (leadId: number, value: string) => {
      setSemCoberturaValues(prev => new Map(prev).set(leadId, value));
      setSemCoberturaValuesChanged(prev => new Set(prev).add(leadId));
      setUnsavedChanges(prev => new Map(prev).set(leadId, 'Sem Cobertura'));
    };

    // Add handlers for address and city
    const handleAddressChange = (leadId: number, value: string) => {
      setAddressValues(prev => new Map(prev).set(leadId, value));
      setAddressValuesChanged(prev => new Set(prev).add(leadId));
      setUnsavedChanges(prev => new Map(prev).set(leadId, 'Sem Cobertura'));
    };

    const handleCityChange = (leadId: number, value: string) => {
      setCityValues(prev => new Map(prev).set(leadId, value));
      setCityValuesChanged(prev => new Set(prev).add(leadId));
      setUnsavedChanges(prev => new Map(prev).set(leadId, 'Sem Cobertura'));
    };

    // Add handlers for postal code parts
    const handlePostalCodeFirstPartChange = (leadId: number, value: string) => {
      // Only allow numbers
      const numericValue = value.replace(/[^0-9]/g, '');
      setPostalCodeFirstPart(prev => new Map(prev).set(leadId, numericValue));
      
      // Combine with second part
      const secondPart = postalCodeSecondPart.get(leadId) || '';
      const combinedValue = secondPart ? `${numericValue}-${secondPart}` : numericValue;
      setPostalCodeValues(prev => new Map(prev).set(leadId, combinedValue));
      setPostalCodeValuesChanged(prev => new Set(prev).add(leadId));
      setUnsavedChanges(prev => new Map(prev).set(leadId, 'Sem Cobertura'));
    };

    const handlePostalCodeSecondPartChange = (leadId: number, value: string) => {
      // Only allow numbers
      const numericValue = value.replace(/[^0-9]/g, '');
      setPostalCodeSecondPart(prev => new Map(prev).set(leadId, numericValue));
      
      // Combine with first part
      const firstPart = postalCodeFirstPart.get(leadId) || '';
      const combinedValue = firstPart ? `${firstPart}-${numericValue}` : numericValue;
      setPostalCodeValues(prev => new Map(prev).set(leadId, combinedValue));
      setPostalCodeValuesChanged(prev => new Set(prev).add(leadId));
      setUnsavedChanges(prev => new Map(prev).set(leadId, 'Sem Cobertura'));
    };

    const handleLastContactAttempt = (leadId: number) => {
      const now = new Date();
      const timestamp = now.toLocaleString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      setLastContactAttempts(prev => new Map(prev).set(leadId, timestamp));
      setLastContactAttemptsChanged(prev => new Set(prev).add(leadId));
      
      // Increment contact attempt count
      const currentCount = contactAttemptCounts.get(leadId) || 0;
      const newCount = currentCount + 1;
      setContactAttemptCounts(prev => new Map(prev).set(leadId, newCount));
      setContactAttemptCountsChanged(prev => new Set(prev).add(leadId));
      
      // If this is triggered automatically for a "Não Atendeu" lead, save immediately
      const status = unsavedChanges.get(leadId);
      if (status === 'Não Atendeu') {
        setTimeout(async () => {
          try {
            const update: any = {
              lead_status: status,
              last_contact_attempt: timestamp,
              last_contact_date: new Date().toISOString().split('T')[0],
              contact_attempt_count: newCount
            };
            
            // Add section change if exists
            const section = unsavedSectionChanges.get(leadId);
            if (section) {
              update.section = section;
            }
            
            await apiService.updateLead(leadId, update);
            
            // Clear only this lead's data
            setUnsavedChanges(prev => { const m = new Map(prev); m.delete(leadId); return m; });
            setUnsavedSectionChanges(prev => { const m = new Map(prev); m.delete(leadId); return m; });
            setLastContactAttempts(prev => { const m = new Map(prev); m.delete(leadId); return m; });
            setLastContactAttemptsChanged(prev => { const s = new Set(prev); s.delete(leadId); return s; });
            setLastContactDates(prev => { const m = new Map(prev); m.delete(leadId); return m; });
            setLastContactDatesChanged(prev => { const s = new Set(prev); s.delete(leadId); return s; });
            setContactAttemptCountsChanged(prev => { const s = new Set(prev); s.delete(leadId); return s; });
            
            message.success('Lead movido para "Não Atendeu" e contactado automaticamente!');
            await fetchLeads();
            
            // Enable automatic lead following to open new subsection
            const lead = leads.find(l => l.id === leadId);
            if (lead) {
              setLeadToFollowId(lead.id);
            }
          } catch (error) {
            message.error('Erro ao salvar lead automaticamente. Tente novamente.');
          }
        }, 200);
      }
    };

    // Edit Lead handlers
    const handleEditLead = (lead: Lead) => {
      setEditingLead(lead);
      setEditFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        company: lead.company || '',
        address: lead.address || '',
        city: lead.city || '',
        postal_code: lead.postal_code || '',
        country: lead.country || '',
        current_provider: lead.current_provider || ''
      });
      setEditModalVisible(true);
    };

    const handleEditModalClose = () => {
      setEditModalVisible(false);
      setEditingLead(null);
      setEditFormData({});
    };

    const handleEditFormChange = (field: string, value: any) => {
      setEditFormData((prev: any) => ({
        ...prev,
        [field]: value
      }));
    };

    const handleSaveEdit = async () => {
      if (!editingLead) return;
      
      setSavingEdit(true);
      try {
        await apiService.updateLead(editingLead.id, editFormData);
        message.success('Lead atualizada com sucesso!');
        setEditModalVisible(false);
        setEditingLead(null);
        setEditFormData({});
        await fetchLeads(); // Refresh the leads list
      } catch (error) {
        message.error('Erro ao atualizar lead');
      } finally {
        setSavingEdit(false);
      }
    };

    // Notes handlers
    const handleOpenNotes = async (lead: Lead) => {
      setSelectedLeadForNotes(lead);
      setNotesPanelVisible(true);
      
      // Fetch notes for this lead
      try {
        const response = await apiService.getLeadNotes(lead.id);
        setLeadNotes(response.notes || []);
      } catch (error) {
        setLeadNotes([]);
      }
    };

    const handleCloseNotes = () => {
      setNotesPanelVisible(false);
      setSelectedLeadForNotes(null);
      setLeadNotes([]);
    };

      const handleNotesChange = (notes: any[]) => {
    setLeadNotes(notes);
    
    // Update highlighted notes map when notes change
    const highlightedNotes = notes.filter(note => note.is_highlighted);
    if (selectedLeadForNotes) {
      setHighlightedNotesMap(prev => {
        const newMap = new Map(prev);
        if (highlightedNotes.length > 0) {
          newMap.set(selectedLeadForNotes.id, highlightedNotes);
        } else {
          newMap.delete(selectedLeadForNotes.id);
        }
        return newMap;
      });
    }
  };

  const handleCreateEventForFollowUp = async (lead: Lead) => {
    setSelectedLeadForEvent(lead as Lead & { title?: string; activity_type?: string });
    setEventModalVisible(true);
  };

  // 4. Helper to get all Follow-up leads missing Nota
  const getFollowUpLeadsMissingNota = () => {
    return safeLeads.filter(
      l => l.section === 'feedback' && l.lead_status === 'Follow-up' && !(followUpNotes.get(l.id) && followUpNotes.get(l.id)?.trim())
    );
  };

  // 4. Helper to get all Sem Interesse leads missing Nota
  const getSemInteresseLeadsMissingNota = () => {
    return safeLeads.filter(
      l => l.section === 'feedback' && l.lead_status === 'Sem Interesse' && !(seminteresseNotes.get(l.id) && seminteresseNotes.get(l.id)?.trim())
    );
  };

  // 4. Helper to get all Informação Errada leads missing Nota
  const getInformacaoErradaLeadsMissingNota = () => {
    return safeLeads.filter(
      l => l.section === 'feedback' && l.lead_status === 'Informação Errada' && !(informacaoErradaNotes.get(l.id) && informacaoErradaNotes.get(l.id)?.trim())
    );
  };

  // 4. Helper to get all Sem Cobertura leads missing value
  const getSemCoberturaLeadsMissingValue = () => {
    return safeLeads.filter(
      l => l.section === 'feedback' && l.lead_status === 'Sem Cobertura' && !(semCoberturaValues.get(l.id) && semCoberturaValues.get(l.id)?.trim())
    );
  };

  // 5. Block navigation if any Pendente or Follow-up lead is missing required field
  const [showMissingNotaModal, setShowMissingNotaModal] = useState(false);
  const [missingNotaLeads, setMissingNotaLeads] = useState<Lead[]>([]);
  const [pendingSectionChangeNota, setPendingSectionChangeNota] = useState<string | null>(null);

  // 5. Block navigation if any Sem Interesse lead is missing required field
  const [showMissingSemInteresseNotaModal, setShowMissingSemInteresseNotaModal] = useState(false);
  const [missingSemInteresseNotaLeads, setMissingSemInteresseNotaLeads] = useState<Lead[]>([]);
  const [pendingSectionChangeSemInteresseNota, setPendingSectionChangeSemInteresseNota] = useState<string | null>(null);

  // 5. Block navigation if any Informação Errada lead is missing required field
  const [showMissingInformacaoErradaNotaModal, setShowMissingInformacaoErradaNotaModal] = useState(false);
  const [missingInformacaoErradaNotaLeads, setMissingInformacaoErradaNotaLeads] = useState<Lead[]>([]);
  const [pendingSectionChangeInformacaoErradaNota, setPendingSectionChangeInformacaoErradaNota] = useState<string | null>(null);

  // 5. Block navigation if any Sem Cobertura lead is missing required field
  const [showMissingSemCoberturaModal, setShowMissingSemCoberturaModal] = useState(false);
  const [missingSemCoberturaLeads, setMissingSemCoberturaLeads] = useState<Lead[]>([]);
  const [pendingSectionChangeSemCobertura, setPendingSectionChangeSemCobertura] = useState<string | null>(null);



  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const missingMotivo = getPendenteLeadsMissingMotivo();
      const missingNota = getFollowUpLeadsMissingNota();
      const missingSemInteresseNota = getSemInteresseLeadsMissingNota();
      const missingInformacaoErradaNota = getInformacaoErradaLeadsMissingNota();
      const missingSemCobertura = getSemCoberturaLeadsMissingValue();
      if (missingMotivo.length > 0 || missingNota.length > 0 || missingSemInteresseNota.length > 0 || missingInformacaoErradaNota.length > 0 || missingSemCobertura.length > 0) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [safeLeads, pendenteReasons, followUpNotes, seminteresseNotes, informacaoErradaNotes, semCoberturaValues]);



  // Intercept section/tab change
  const handleSectionChange = (key: string) => {
    // Only block navigation if leaving feedback section and there are missing motivos or notas
    if (activeSection === 'feedback' && key !== 'feedback') {
      const missingMotivo = getPendenteLeadsMissingMotivo();
      const missingNota = getFollowUpLeadsMissingNota();
      const missingSemInteresseNota = getSemInteresseLeadsMissingNota();
      const missingInformacaoErradaNota = getInformacaoErradaLeadsMissingNota();
      const missingSemCobertura = getSemCoberturaLeadsMissingValue();
      if (missingMotivo.length > 0) {
        setMissingMotivoLeads(missingMotivo);
        setShowMissingMotivoModal(true);
        setPendingSectionChange(key);
        return;
      }
      if (missingNota.length > 0) {
        setMissingNotaLeads(missingNota);
        setShowMissingNotaModal(true);
        setPendingSectionChangeNota(key);
        return;
      }
      if (missingSemInteresseNota.length > 0) {
        setMissingSemInteresseNotaLeads(missingSemInteresseNota);
        setShowMissingSemInteresseNotaModal(true);
        setPendingSectionChangeSemInteresseNota(key);
        return;
      }
      if (missingInformacaoErradaNota.length > 0) {
        setMissingInformacaoErradaNotaLeads(missingInformacaoErradaNota);
        setShowMissingInformacaoErradaNotaModal(true);
        setPendingSectionChangeInformacaoErradaNota(key);
        return;
      }
      if (missingSemCobertura.length > 0) {
        setMissingSemCoberturaLeads(missingSemCobertura);
        setShowMissingSemCoberturaModal(true);
        setPendingSectionChangeSemCobertura(key);
        return;
      }
    }
    setActiveSection(key);
    setSubsectionPagination(new Map());
  };



  // 6. Update handleConfirmSection to include followUpNotes and endOfContractDates
  const handleConfirmSection = async () => {
    try {
      // Automatically set last_contact_date for any leads with "Não Atendeu" status
      const currentDate = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
      unsavedChanges.forEach((status, leadId) => {
        if (status === 'Não Atendeu' && !lastContactDatesChanged.has(leadId)) {
          setLastContactDates(prev => new Map(prev).set(leadId, currentDate));
          setLastContactDatesChanged(prev => new Set(prev).add(leadId));
        }
      });
      
      const changedLeadIds = Array.from(new Set([
          ...Array.from(unsavedChanges.keys()),
          ...Array.from(unsavedSectionChanges.keys()),
                  ...Array.from(pendenteReasonsChanged.keys()),
          ...Array.from(followUpNotesChanged.keys()),
          ...Array.from(seminteresseNotesChanged.keys()),
          ...Array.from(informacaoErradaNotesChanged.keys()),
          ...Array.from(semCoberturaValuesChanged.keys()),
          ...Array.from(addressValuesChanged.keys()),
          ...Array.from(cityValuesChanged.keys()),
          ...Array.from(postalCodeValuesChanged.keys()),
          ...Array.from(endOfContractDatesChanged.keys()),
          ...Array.from(lastContactAttemptsChanged.keys()),
          ...Array.from(lastContactDatesChanged.keys()),
          ...Array.from(installationDatesChanged.keys()),
          ...Array.from(unsavedProductTypes.keys()),
          ...Array.from(unsavedAdditionalCards.keys()),
          ...Array.from(instaladosBusinessAreaChanged.keys()),
          ...Array.from(instaladosSelectedProductsChanged.keys()),
        ]));
      for (const leadId of changedLeadIds) {
        // Use original status if available (for special cases like "Atendeu" -> empty display)
        const status = originalStatusForDisplay.get(leadId) || unsavedChanges.get(leadId);
        const section = unsavedSectionChanges.get(leadId);
        const pendenteReason = pendenteReasonsChanged.has(leadId) ? pendenteReasons.get(leadId) : undefined;
        const followUpNote = followUpNotesChanged.has(leadId) ? followUpNotes.get(leadId) : undefined;
        const seminteresseNote = seminteresseNotesChanged.has(leadId) ? seminteresseNotes.get(leadId) : undefined;
        const informacaoErradaNote = informacaoErradaNotesChanged.has(leadId) ? informacaoErradaNotes.get(leadId) : undefined;
        const semCoberturaValue = semCoberturaValuesChanged.has(leadId) ? semCoberturaValues.get(leadId) : undefined;
        const addressValue = addressValuesChanged.has(leadId) ? addressValues.get(leadId) : undefined;
        const cityValue = cityValuesChanged.has(leadId) ? cityValues.get(leadId) : undefined;
        const postalCodeValue = postalCodeValuesChanged.has(leadId) ? postalCodeValues.get(leadId) : undefined;
        const endOfContractDate = endOfContractDatesChanged.has(leadId) ? endOfContractDates.get(leadId) : undefined;
        const lastContactAttempt = lastContactAttemptsChanged.has(leadId) ? lastContactAttempts.get(leadId) : undefined;
        const lastContactDate = lastContactDatesChanged.has(leadId) ? lastContactDates.get(leadId) : undefined;
        const contactAttemptCount = contactAttemptCountsChanged.has(leadId) ? contactAttemptCounts.get(leadId) : undefined;
        const installationDatesMap = installationDatesChanged.has(leadId) ? installationDates.get(leadId) : undefined;
        const productType = unsavedProductTypes.has(leadId) ? unsavedProductTypes.get(leadId) : undefined;
        const additionalCards = unsavedAdditionalCards.has(leadId) ? unsavedAdditionalCards.get(leadId) : undefined;
        const instaladosArea = instaladosBusinessAreaChanged.has(leadId) ? instaladosBusinessArea.get(leadId) : undefined;
        const instaladosProducts = instaladosSelectedProductsChanged.has(leadId) ? instaladosSelectedProducts.get(leadId) : undefined;
        const update: any = {};
        if (status !== undefined) update.lead_status = status;
        if (section !== undefined) update.section = section;
        if (pendenteReason !== undefined) update.pendente_reason = pendenteReason;
        if (followUpNote !== undefined) update.followup_note = followUpNote;
        if (seminteresseNote !== undefined) update.seminteresse_note = seminteresseNote;
        if (informacaoErradaNote !== undefined) update.informacao_errada_note = informacaoErradaNote;
        if (semCoberturaValue !== undefined) update.sem_cobertura_de = semCoberturaValue;
        if (addressValue !== undefined) update.address = addressValue;
        if (cityValue !== undefined) update.city = cityValue;
        if (postalCodeValue !== undefined) update.postal_code = postalCodeValue;
        if (endOfContractDate !== undefined) update.end_of_contract = endOfContractDate;
        if (lastContactAttempt !== undefined) update.last_contact_attempt = lastContactAttempt;
        if (lastContactDate !== undefined) update.last_contact_date = lastContactDate;
        if (contactAttemptCount !== undefined) update.contact_attempt_count = contactAttemptCount;
        // Save installation dates per business area as JSON
        if (installationDatesMap !== undefined) {
          const datesObj: { [key: string]: string } = {};
          installationDatesMap.forEach((date, area) => {
            datesObj[area] = date;
          });
          update.instalados_installation_dates = JSON.stringify(datesObj);
          // For backward compatibility, also set installation_date to the current business area's date
          if (instaladosArea && instaladosArea !== '1' && installationDatesMap.has(instaladosArea)) {
            update.installation_date = installationDatesMap.get(instaladosArea);
          }
        }
        if (productType !== undefined) update.product_type = productType;
        if (additionalCards !== undefined) update.additional_cards = additionalCards;
        if (instaladosArea !== undefined) update.instalados_business_area = instaladosArea;
        if (instaladosProducts !== undefined) update.instalados_selected_products = JSON.stringify(instaladosProducts);

        await apiService.updateLead(leadId, update);
      }
      setUnsavedChanges(new Map());
      setUnsavedSectionChanges(new Map());
      setOriginalStatusForDisplay(new Map());
      setUnsavedProductTypes(new Map());
      setUnsavedAdditionalCards(new Map());
      setInstaladosBusinessArea(new Map());
      setInstaladosSelectedProducts(new Map());
      setInstaladosBusinessAreaChanged(new Set());
      setInstaladosSelectedProductsChanged(new Set());
              setPendenteReasons(new Map());
        setFollowUpNotes(new Map());
        setSeminteresseNotes(new Map());
        setInformacaoErradaNotes(new Map());
        setSemCoberturaValues(new Map());
        setAddressValues(new Map());
        setInstallationDates(new Map());
        setCityValues(new Map());
        setPostalCodeValues(new Map());
        setPostalCodeFirstPart(new Map());
        setPostalCodeSecondPart(new Map());
        setEndOfContractDates(new Map());
        setPendenteReasonsChanged(new Set());
        setFollowUpNotesChanged(new Set());
        setSeminteresseNotesChanged(new Set());
        setInformacaoErradaNotesChanged(new Set());
        setSemCoberturaValuesChanged(new Set());
        setAddressValuesChanged(new Set());
        setCityValuesChanged(new Set());
        setPostalCodeValuesChanged(new Set());
        setEndOfContractDatesChanged(new Set());
        setLastContactAttempts(new Map());
        setLastContactAttemptsChanged(new Set());
        setContactAttemptCountsChanged(new Set());
        setLastContactDates(new Map());
        setLastContactDatesChanged(new Set());
        setInstallationDatesChanged(new Set());

      message.success('Alterações confirmadas para esta secção!');
      await fetchLeads();
      // Enable automatic lead following to open new subsection
      if (lastChangedLead) {
        setLeadToFollowId(lastChangedLead.id);
      }
      setLastChangedLead(null);
    } catch (error) {
      message.error('Erro ao guardar alterações. Tente novamente.');
    }
  };

  // 7. Update renderGridView and renderKanbanView to use getFollowUpColumns for Follow-up subsection
  // In renderGridView, where group.key === 'followUp', use getFollowUpColumns()
  // In renderKanbanView, for group.key === 'followUp', show Nota and Criar Evento UI like Pendente
  // 8. Add modal for missing Nota
  return (
    <>
      <style>{`
        .dense-row td {
          padding-top: 4px !important;
          padding-bottom: 4px !important;
        }
        .ant-table-thead > tr > th {
          font-size: 13px !important;
          padding-top: 6px !important;
          padding-bottom: 6px !important;
        }
        /* ClientManagement Color Overrides — full-page Gestão only */
        .client-management-container:not(.client-management-workspace-embed) {
          background: linear-gradient(180deg, #1e293b 0%, #334155 25%, #475569 50%, #64748b 75%, #94a3b8 100%) !important;
          background-attachment: scroll !important;
          background-size: 100% 100% !important;
          background-repeat: no-repeat !important;
          --section-color: ${sections.find(s => s.key === activeSection)?.color || '#6366f1'};
          background-position: center center !important;
          /* Ensure background scales with viewport */
          min-height: 100vh !important;
          width: 100% !important;
          max-width: 100vw !important;
          box-sizing: border-box !important;
        }
        /* Cohesive Glassmorphism Design - Matching MainPage Pattern */
        /* Main Card - Light glassmorphism (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) > .ant-card:first-child {
          background: rgba(255, 255, 255, 0.15) !important;
          backdrop-filter: blur(10px) !important;
          -webkit-backdrop-filter: blur(10px) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
        }
        /* Lead Cards (Kanban) - Transparent to blend with sub-section background (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-card.ant-card-small {
          background: transparent !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          border: 2px solid rgba(255, 255, 255, 0.3) !important;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-card.ant-card-small:hover {
          background: transparent !important;
          border-color: rgba(255, 255, 255, 0.5) !important;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2) inset !important;
        }
        /* Improve text readability in cards (Gestão full page only — embed uses light cards) */
        .client-management-container:not(.client-management-workspace-embed) .ant-card.ant-card-small .ant-typography,
        .client-management-container:not(.client-management-workspace-embed) .ant-card.ant-card-small div,
        .client-management-container:not(.client-management-workspace-embed) .ant-card.ant-card-small span:not(.anticon) {
          color: #ffffff !important;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important;
        }
        /* Table view rows - Much darker for contrast */
        .client-management-container .ant-table-tbody > tr {
          background: rgba(0, 0, 0, 0.3) !important;
        }
        .client-management-container .ant-table-tbody > tr:hover {
          background: rgba(0, 0, 0, 0.4) !important;
        }
        /* Collapse panels - Much darker for contrast (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item {
          background: rgba(0, 0, 0, 0.3) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
          border-width: 1px !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active {
          background: ${(() => {
            const section = sections.find(s => s.key === activeSection);
            if (!section) return 'rgba(99, 102, 241, 0.3)';
            const colors: { [key: string]: string } = {
              entrada: 'rgba(99, 102, 241, 0.3)',
              feedback: 'rgba(16, 185, 129, 0.3)',
              registro: 'rgba(139, 92, 246, 0.3)',
              instalados: 'rgba(6, 182, 212, 0.3)'
            };
            return colors[section.key] || 'rgba(99, 102, 241, 0.3)';
          })()} !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
          border-width: 1px !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-collapse-header {
          color: var(--section-color, #6366f1) !important;
          font-weight: 600 !important;
          background: transparent !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-collapse-expand-icon {
          color: var(--section-color, #6366f1) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-collapse-content {
          background: ${(() => {
            const section = sections.find(s => s.key === activeSection);
            if (!section) return 'rgba(99, 102, 241, 0.2)';
            const colors: { [key: string]: string } = {
              entrada: 'rgba(99, 102, 241, 0.2)',
              feedback: 'rgba(16, 185, 129, 0.2)',
              registro: 'rgba(139, 92, 246, 0.2)',
              instalados: 'rgba(6, 182, 212, 0.2)'
            };
            return colors[section.key] || 'rgba(99, 102, 241, 0.2)';
          })()} !important;
          border-color: rgba(255, 255, 255, 0.15) !important;
        }
        /* Ensure cards inside don't get affected by the background (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-card,
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-card-body,
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-table,
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-table-tbody > tr {
          background: transparent !important;
        }
        /* Override inline styles for main card only (Gestão full page) */
        .client-management-container:not(.client-management-workspace-embed) > .ant-card[style]:first-child {
          background: rgba(255, 255, 255, 0.15) !important;
          background-color: rgba(255, 255, 255, 0.15) !important;
          backdrop-filter: blur(10px) !important;
          -webkit-backdrop-filter: blur(10px) !important;
        }
        /* Card bodies transparent */
        .client-management-container .ant-card-body,
        .client-management-container .ant-card .ant-card-body,
        .client-management-container .ant-card-bordered .ant-card-body,
        .client-management-container .ant-card-hoverable .ant-card-body,
        .client-management-container .ant-card-small .ant-card-body {
          background: transparent !important;
          background-color: transparent !important;
        }
        /* Inputs - Slightly more opaque for usability */
        .client-management-container .ant-input:not(.modern-search-input .ant-input),
        .client-management-container .ant-picker {
          background: rgba(255, 255, 255, 0.3) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
          color: #ffffff !important;
        }
        
        /* Search input override - consistent glassmorphism throughout */
        .client-management-container .modern-search-input .ant-input-group-wrapper,
        .client-management-container .modern-search-input .ant-input-group {
          background: rgba(255, 255, 255, 0.15) !important;
          backdrop-filter: blur(10px) !important;
          -webkit-backdrop-filter: blur(10px) !important;
          border: 2px solid rgba(255, 255, 255, 0.3) !important;
          border-radius: 12px !important;
        }
        .client-management-container .modern-search-input .ant-input-group-wrapper .ant-input,
        .client-management-container .modern-search-input .ant-input-group .ant-input,
        .client-management-container .modern-search-input .ant-input-affix-wrapper,
        .client-management-container .modern-search-input .ant-input-affix-wrapper .ant-input {
          background: transparent !important;
          background-color: transparent !important;
          border: none !important;
          color: #ffffff !important;
        }
        .client-management-container .modern-search-input .ant-input-group-wrapper .ant-input::placeholder,
        .client-management-container .modern-search-input .ant-input-group .ant-input::placeholder {
          color: rgba(255, 255, 255, 0.7) !important;
        }
        .client-management-container .modern-search-input .ant-input-group-wrapper .anticon,
        .client-management-container .modern-search-input .ant-input-group .anticon {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        /* Search button addon - transparent glassmorphism */
        .client-management-container .modern-search-input .ant-input-group-wrapper .ant-input-group-addon,
        .client-management-container .modern-search-input .ant-input-group .ant-input-group-addon {
          background: transparent !important;
          border-left: 1px solid rgba(255, 255, 255, 0.2) !important;
        }
        /* Status Dropdowns - Lighter background for better visibility */
        .client-management-container .ant-select-selector {
          background: rgba(255, 255, 255, 0.4) !important;
          border-color: rgba(255, 255, 255, 0.4) !important;
          color: #1e3a8a !important;
          font-weight: 500 !important;
        }
        .client-management-container .ant-select-selector:hover {
          background: rgba(255, 255, 255, 0.5) !important;
          border-color: rgba(255, 255, 255, 0.5) !important;
        }
        .client-management-container .ant-select-focused .ant-select-selector {
          background: rgba(255, 255, 255, 0.6) !important;
          border-color: rgba(255, 255, 255, 0.6) !important;
          box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3) !important;
        }
        .client-management-container .ant-select-selection-item {
          color: #1e3a8a !important;
          font-weight: 500 !important;
        }
        .client-management-container .ant-input::placeholder {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        .client-management-container .ant-input-textarea textarea::placeholder {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        .client-management-container textarea {
          color: #ffffff !important;
        }
        .client-management-container textarea::placeholder {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        .client-management-container .ant-input:focus,
        .client-management-container .ant-select-focused .ant-select-selector,
        .client-management-container .ant-picker-focused {
          border-color: rgba(255, 255, 255, 0.5) !important;
          box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2) !important;
        }
        /* Tabs - Much better contrast (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-tabs-tab {
          background: rgba(0, 0, 0, 0.4) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
          color: rgba(255, 255, 255, 0.8) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-tabs-tab-active {
          background: ${(() => {
            const section = sections.find(s => s.key === activeSection);
            if (!section) return 'linear-gradient(135deg, #6366f1, #818cf8) !important';
            const gradients: { [key: string]: string } = {
              entrada: 'linear-gradient(135deg, #6366f1, #818cf8)',
              feedback: 'linear-gradient(135deg, #10b981, #34d399)',
              registro: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
              instalados: 'linear-gradient(135deg, #06b6d4, #22d3ee)'
            };
            return gradients[section.key] || 'linear-gradient(135deg, #6366f1, #818cf8)';
          })()} !important;
          border-color: rgba(255, 255, 255, 0.6) !important;
          color: #ffffff !important;
          font-weight: 700 !important;
          box-shadow: ${(() => {
            const section = sections.find(s => s.key === activeSection);
            if (!section) return '0 4px 12px rgba(99, 102, 241, 0.6) !important';
            const shadows: { [key: string]: string } = {
              entrada: '0 4px 12px rgba(99, 102, 241, 0.6)',
              feedback: '0 4px 12px rgba(16, 185, 129, 0.6)',
              registro: '0 4px 12px rgba(139, 92, 246, 0.6)',
              instalados: '0 4px 12px rgba(6, 182, 212, 0.6)'
            };
            return shadows[section.key] || '0 4px 12px rgba(99, 102, 241, 0.6)';
          })()} !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-tabs-tab:hover {
          background: rgba(0, 0, 0, 0.5) !important;
          color: #ffffff !important;
        }
        /* Simple badge for tabs - clean and always visible */
        .client-management-container .tab-badge-simple {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          min-width: 24px !important;
          height: 20px !important;
          padding: 0 6px !important;
          background-color: #ffffff !important;
          color: #1e3a8a !important;
          font-weight: 700 !important;
          font-size: 12px !important;
          border-radius: 10px !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3) !important;
          border: 1px solid rgba(30, 58, 138, 0.3) !important;
          line-height: 20px !important;
          white-space: nowrap !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        /* Ensure text inside badge is always visible */
        .client-management-container .tab-badge-simple * {
          color: #1e3a8a !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        /* Collapse Panels - Darker for contrast (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse {
          background: rgba(0, 0, 0, 0.1) !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item {
          border-color: rgba(255, 255, 255, 0.2) !important;
          background: rgba(0, 0, 0, 0.15) !important;
          border-width: 1px !important;
          border-style: solid !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active {
          background: ${(() => {
            const section = sections.find(s => s.key === activeSection);
            if (!section) return 'rgba(99, 102, 241, 0.25)';
            const colors: { [key: string]: string } = {
              entrada: 'rgba(99, 102, 241, 0.25)',
              feedback: 'rgba(16, 185, 129, 0.25)',
              registro: 'rgba(139, 92, 246, 0.25)',
              instalados: 'rgba(6, 182, 212, 0.25)'
            };
            return colors[section.key] || 'rgba(99, 102, 241, 0.25)';
          })()} !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
          border-width: 1px !important;
          border-style: solid !important;
        }
        /* Override ghost prop for active items - full background (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-ghost .ant-collapse-item-active {
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          background: ${(() => {
            const section = sections.find(s => s.key === activeSection);
            if (!section) return 'rgba(99, 102, 241, 0.25)';
            const colors: { [key: string]: string } = {
              entrada: 'rgba(99, 102, 241, 0.25)',
              feedback: 'rgba(16, 185, 129, 0.25)',
              registro: 'rgba(139, 92, 246, 0.25)',
              instalados: 'rgba(6, 182, 212, 0.25)'
            };
            return colors[section.key] || 'rgba(99, 102, 241, 0.25)';
          })()} !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-collapse-content {
          background: ${(() => {
            const section = sections.find(s => s.key === activeSection);
            if (!section) return 'rgba(99, 102, 241, 0.15)';
            const colors: { [key: string]: string } = {
              entrada: 'rgba(99, 102, 241, 0.15)',
              feedback: 'rgba(16, 185, 129, 0.15)',
              registro: 'rgba(139, 92, 246, 0.15)',
              instalados: 'rgba(6, 182, 212, 0.15)'
            };
            return colors[section.key] || 'rgba(99, 102, 241, 0.15)';
          })()} !important;
          border-color: rgba(255, 255, 255, 0.15) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-collapse-content-box {
          background: transparent !important;
        }
        /* Ensure cards maintain their original appearance - use dark background to match inactive state (Gestão only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-card.ant-card-small {
          background: rgba(0, 0, 0, 0.4) !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-card.ant-card-small .ant-card-body {
          background: transparent !important;
        }
        /* Ensure cards inside don't get affected by the background (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-card,
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-card-body,
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-table,
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-table-tbody > tr {
          background: transparent !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-header {
          color: #ffffff !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-collapse-header {
          color: var(--section-color, #6366f1) !important;
          font-weight: 600 !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-collapse-expand-icon {
          color: var(--section-color, #6366f1) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-content {
          background: rgba(0, 0, 0, 0.1) !important;
          border-color: rgba(255, 255, 255, 0.15) !important;
        }
        /* Tables - Transparent to blend with sub-section background */
        .client-management-container .ant-table {
          background: transparent !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
        .client-management-container .ant-table-thead > tr > th {
          background: transparent !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
          color: #ffffff !important;
          font-weight: 600 !important;
        }
        .client-management-container .ant-table-tbody > tr {
          background: transparent !important;
          border-color: rgba(255, 255, 255, 0.1) !important;
        }
        .client-management-container .ant-table-tbody > tr:hover {
          background: rgba(255, 255, 255, 0.1) !important;
        }
        .client-management-container .ant-table-tbody > tr > td {
          border-color: rgba(255, 255, 255, 0.15) !important;
          color: #ffffff !important;
          background: transparent !important;
        }
        .client-management-container .ant-table-tbody > tr > td > div {
          color: #ffffff !important;
        }
        .client-management-container .ant-table-tbody > tr > td > div > div {
          color: #ffffff !important;
        }
        /* Ensure all text elements in table cells are white */
        .client-management-container .ant-table-tbody > tr > td * {
          color: #ffffff !important;
        }
        .client-management-container .ant-table-tbody > tr > td span:not(.anticon) {
          color: #ffffff !important;
        }
        .client-management-container .ant-table-tbody > tr > td .ant-typography {
          color: #ffffff !important;
        }
        /* Select dropdowns in tables */
        .client-management-container .ant-table .ant-select-selector {
          background: rgba(255, 255, 255, 0.3) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
        }
        .client-management-container .ant-table .ant-select-selection-item {
          color: #1e3a8a !important;
          font-weight: 500 !important;
        }
        /* Buttons in tables and cards - Default styles for buttons without action classes (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-table .ant-btn:not(.action-btn-view):not(.action-btn-notes):not(.action-btn-task):not(.action-btn-email):not(.action-btn-edit):not(.action-btn-revert):not(.action-btn-create-event):not(.action-btn-pdf):not(.action-btn-vodafone):not(.action-btn-meo):not(.action-btn-nos),
        .client-management-container:not(.client-management-workspace-embed) .ant-card .ant-btn:not(.action-btn-view):not(.action-btn-notes):not(.action-btn-task):not(.action-btn-email):not(.action-btn-edit):not(.action-btn-revert):not(.action-btn-create-event):not(.action-btn-pdf):not(.action-btn-vodafone):not(.action-btn-meo):not(.action-btn-nos) {
          color: #ffffff !important;
          border-color: rgba(255, 255, 255, 0.4) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-table .ant-btn-primary:not(.action-btn-notes):not(.action-btn-revert):not(.action-btn-create-event):not(.action-btn-pdf):not(.action-btn-vodafone):not(.action-btn-meo):not(.action-btn-nos),
        .client-management-container:not(.client-management-workspace-embed) .ant-card .ant-btn-primary:not(.action-btn-notes):not(.action-btn-revert):not(.action-btn-create-event):not(.action-btn-pdf):not(.action-btn-vodafone):not(.action-btn-meo):not(.action-btn-nos) {
          background: rgba(59, 130, 246, 0.8) !important;
          border-color: rgba(59, 130, 246, 0.8) !important;
          color: #ffffff !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-table .ant-btn:hover:not(.action-btn-view):not(.action-btn-notes):not(.action-btn-task):not(.action-btn-email):not(.action-btn-edit):not(.action-btn-revert):not(.action-btn-create-event):not(.action-btn-pdf):not(.action-btn-vodafone):not(.action-btn-meo):not(.action-btn-nos),
        .client-management-container:not(.client-management-workspace-embed) .ant-card .ant-btn:hover:not(.action-btn-view):not(.action-btn-notes):not(.action-btn-task):not(.action-btn-email):not(.action-btn-edit):not(.action-btn-revert):not(.action-btn-create-event):not(.action-btn-pdf):not(.action-btn-vodafone):not(.action-btn-meo):not(.action-btn-nos) {
          border-color: rgba(255, 255, 255, 0.6) !important;
          color: #ffffff !important;
        }
        /* Allow inline styles to override button colors */
        .client-management-container .ant-table .ant-btn[style*="color"],
        .client-management-container .ant-table .ant-btn[style*="backgroundColor"],
        .client-management-container .ant-table .ant-btn[style*="borderColor"] {
          /* Inline styles take precedence - no override needed */
        }
        /* Tags in tables */
        .client-management-container .ant-table .ant-tag {
          color: #ffffff !important;
          border-color: rgba(255, 255, 255, 0.4) !important;
        }
        /* Links in tables */
        .client-management-container .ant-table a {
          color: #60a5fa !important;
        }
        .client-management-container .ant-table a:hover {
          color: #93c5fd !important;
        }
        /* Icons in tables */
        .client-management-container .ant-table .anticon {
          color: #ffffff !important;
        }
        /* Text Colors - White for readability (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-typography,
        .client-management-container:not(.client-management-workspace-embed) h1,
        .client-management-container:not(.client-management-workspace-embed) h2,
        .client-management-container:not(.client-management-workspace-embed) h3,
        .client-management-container:not(.client-management-workspace-embed) h4,
        .client-management-container:not(.client-management-workspace-embed) .ant-typography-title {
          color: #ffffff !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-typography-secondary {
          color: rgba(255, 255, 255, 0.8) !important;
        }
        /* Select dropdowns (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-select-selection-item {
          color: #ffffff !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-select-arrow {
          color: rgba(255, 255, 255, 0.8) !important;
        }
        /* Dropdown menus */
        .client-management-container .ant-dropdown {
          background: rgba(255, 255, 255, 0.2) !important;
          backdrop-filter: blur(10px) !important;
          -webkit-backdrop-filter: blur(10px) !important;
          border: 1px solid rgba(255, 255, 255, 0.3) !important;
        }
        .client-management-container .ant-dropdown-menu {
          background: transparent !important;
        }
        .client-management-container .ant-dropdown-menu-item {
          color: #ffffff !important;
        }
        .client-management-container .ant-dropdown-menu-item:hover {
          background: rgba(255, 255, 255, 0.1) !important;
        }
        /* Pagination (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-pagination-item {
          background: rgba(255, 255, 255, 0.15) !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-pagination-item a {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-pagination-item-active {
          background: rgba(255, 255, 255, 0.25) !important;
          border-color: rgba(255, 255, 255, 0.4) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-pagination-item-active a {
          color: #ffffff !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-pagination-prev,
        .client-management-container:not(.client-management-workspace-embed) .ant-pagination-next {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-pagination-total-text {
          color: rgba(255, 255, 255, 0.8) !important;
        }
        /* Buttons - Ensure Readability (Gestão full page only — embed uses light controls) */
        /* Primary buttons - Keep existing colors but ensure text is readable */
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-primary {
          color: #ffffff !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-primary:hover,
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-primary:focus {
          color: #ffffff !important;
        }
        /* Default buttons - Much darker for contrast */
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-default {
          background: rgba(0, 0, 0, 0.5) !important;
          backdrop-filter: blur(10px) !important;
          -webkit-backdrop-filter: blur(10px) !important;
          border-color: rgba(255, 255, 255, 0.4) !important;
          color: #ffffff !important;
          font-weight: 500 !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-default:hover,
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-default:focus {
          background: rgba(0, 0, 0, 0.6) !important;
          border-color: rgba(255, 255, 255, 0.5) !important;
          color: #ffffff !important;
        }
        /* Text buttons - White text */
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-text {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-text:hover,
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-text:focus {
          background: rgba(255, 255, 255, 0.1) !important;
          color: #ffffff !important;
        }
        /* Icon-only buttons */
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-icon-only {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-icon-only:hover {
          background: rgba(255, 255, 255, 0.15) !important;
          color: #ffffff !important;
        }
        /* Button icons */
        .client-management-container:not(.client-management-workspace-embed) .ant-btn .anticon {
          color: inherit !important;
        }
        /* Small buttons */
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-sm {
          color: #ffffff !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-btn-sm.ant-btn-default {
          background: rgba(255, 255, 255, 0.2) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
          color: #ffffff !important;
        }
        /* Badges - White text */
        .client-management-container:not(.client-management-workspace-embed) .ant-badge {
          color: #ffffff !important;
        }
        /* General badge count - but tabs override this */
        .client-management-container:not(.client-management-workspace-embed) .ant-badge-count:not(.ant-tabs-tab .ant-badge-count):not(.ant-tabs-tab-active .ant-badge-count) {
          background: rgba(255, 255, 255, 0.3) !important;
          color: #ffffff !important;
          border-color: rgba(255, 255, 255, 0.4) !important;
        }
        /* Tags - Glassmorphism */
        .client-management-container:not(.client-management-workspace-embed) .ant-tag {
          background: rgba(255, 255, 255, 0.2) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
          color: #ffffff !important;
        }
        /* Radio buttons and checkboxes (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-radio-wrapper {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-radio-wrapper .ant-radio-inner {
          border-color: rgba(255, 255, 255, 0.5) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-radio-wrapper-checked .ant-radio-inner {
          border-color: #ffffff !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-radio-wrapper-checked .ant-radio-inner::after {
          background-color: #ffffff !important;
        }
        /* Tooltips */
        .client-management-container .ant-tooltip-inner {
          background: rgba(0, 0, 0, 0.85) !important;
          color: #ffffff !important;
          border: none !important;
          box-shadow: none !important;
        }
        .client-management-container .ant-tooltip-arrow::before {
          border: none !important;
          box-shadow: none !important;
        }
        /* DatePicker (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-picker-input > input {
          color: #ffffff !important;
        }
        /* Collapse arrow (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-expand-icon {
          color: rgba(255, 255, 255, 0.8) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) .ant-collapse-item-active .ant-collapse-expand-icon {
          color: var(--section-color, #6366f1) !important;
        }
        /* Empty states (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .ant-empty-description {
          color: rgba(255, 255, 255, 0.7) !important;
        }
        /* Links (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) a {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        .client-management-container:not(.client-management-workspace-embed) a:hover {
          color: #ffffff !important;
        }
        /* All text elements - ensure white on dark Gestão page only (never in workspace embed) */
        .client-management-container:not(.client-management-workspace-embed) span:not(.anticon):not(.ant-badge-count),
        .client-management-container:not(.client-management-workspace-embed) div:not(.ant-select-dropdown):not(.ant-picker-dropdown),
        .client-management-container:not(.client-management-workspace-embed) p,
        .client-management-container:not(.client-management-workspace-embed) label {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        /* Ensure icons are visible (Gestão full page only) */
        .client-management-container:not(.client-management-workspace-embed) .anticon {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        /* Button text specifically */
        .client-management-container .ant-btn span {
          color: inherit !important;
        }
        /* Select options */
        .client-management-container .ant-select-item {
          color: #1f2937 !important;
        }
        .client-management-container .ant-select-item-option-selected {
          background: rgba(59, 130, 246, 0.2) !important;
        }
        /* Modal content - keep readable */
        .client-management-container .ant-modal-content {
          background: rgba(255, 255, 255, 0.95) !important;
          backdrop-filter: blur(20px) !important;
        }
        .client-management-container .ant-modal-header {
          background: rgba(255, 255, 255, 0.95) !important;
        }
        .client-management-container .ant-modal-body {
          background: transparent !important;
        }
        .client-management-container .ant-modal-title {
          color: #1f2937 !important;
        }
        /* Drawer - keep readable */
        .client-management-container .ant-drawer-content {
          background: rgba(255, 255, 255, 0.95) !important;
          backdrop-filter: blur(20px) !important;
        }
        .client-management-container .ant-drawer-header {
          background: rgba(255, 255, 255, 0.95) !important;
        }
        .client-management-container .ant-drawer-title {
          color: #1f2937 !important;
        }
        .client-management-container .ant-drawer-body {
          background: transparent !important;
        }
        ${workspaceEmbed ? `
        /* Workspace embed — same behaviour, light shell aligned with Client Workspace */
        .client-management-workspace-embed.client-management-container {
          --section-color: ${sections.find(s => s.key === activeSection)?.color || '#6366f1'};
          background: transparent !important;
          background-image: none !important;
          min-height: 0 !important;
          max-width: 100% !important;
          width: 100% !important;
        }
        .client-management-workspace-embed.client-management-container > .ant-card:first-child,
        .client-management-workspace-embed.client-management-container > .ant-card[style]:first-child {
          background: transparent !important;
          background-color: transparent !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          border: none !important;
          box-shadow: none !important;
        }
        .client-management-workspace-embed.client-management-container .ant-tabs-nav::before {
          border-bottom-color: rgba(15, 23, 42, 0.08) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-tabs-tab {
          background: #e8ecf4 !important;
          border: 1px solid rgba(15, 23, 42, 0.08) !important;
          color: #475569 !important;
        }
        .client-management-workspace-embed.client-management-container .ant-tabs-tab-active {
          background: #ffffff !important;
          border-color: rgba(30, 58, 95, 0.28) !important;
          color: #0f172a !important;
          font-weight: 600 !important;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-tabs-tab:hover {
          background: #f1f5f9 !important;
          color: #0f172a !important;
        }
        .client-management-workspace-embed.client-management-container .ant-tabs-tab-btn {
          color: inherit !important;
        }
        .client-management-workspace-embed.client-management-container .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #0f172a !important;
        }
        .client-management-workspace-embed.client-management-container .section-tab-badge-active {
          background-color: #e2e8f0 !important;
          color: #0f172a !important;
          border: 1px solid rgba(15, 23, 42, 0.12) !important;
        }
        .client-management-workspace-embed.client-management-container .section-tab-badge-inactive {
          background-color: #cbd5e1 !important;
          color: #1e293b !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse {
          background: transparent !important;
          border: none !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-item {
          background: #eef2f7 !important;
          border: 1px solid rgba(15, 23, 42, 0.08) !important;
          border-radius: 10px !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-item-active {
          background: ${(() => {
            const section = sections.find(s => s.key === activeSection);
            const colors: { [key: string]: string } = {
              entrada: 'rgba(99, 102, 241, 0.09)',
              feedback: 'rgba(16, 185, 129, 0.09)',
              registro: 'rgba(139, 92, 246, 0.09)',
              instalados: 'rgba(6, 182, 212, 0.09)',
            };
            return section ? colors[section.key] || 'rgba(99, 102, 241, 0.09)' : 'rgba(99, 102, 241, 0.09)';
          })()} !important;
          border-color: rgba(15, 23, 42, 0.1) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-header {
          color: #0f172a !important;
          font-weight: 600 !important;
          font-size: 14px !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-expand-icon {
          color: #475569 !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-item-active .ant-collapse-header {
          color: #0f172a !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-item-active .ant-collapse-expand-icon {
          color: #1e3a5f !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-content {
          background: #f8fafc !important;
          border-color: rgba(15, 23, 42, 0.06) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-item-active .ant-collapse-content {
          background: #fafbfd !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-item-active .ant-card.ant-card-small {
          background: #ffffff !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small {
          background: #ffffff !important;
          border: 1px solid rgba(15, 23, 42, 0.1) !important;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 2px 8px rgba(15, 23, 42, 0.06) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small:hover {
          border-color: rgba(30, 58, 95, 0.28) !important;
          box-shadow: 0 4px 14px rgba(15, 23, 42, 0.1) !important;
        }
        /* Kanban cards: pagination, tags, all button types readable on white (workspace embed) */
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-item {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.14) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-item a {
          color: #334155 !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-item-active {
          background: #e8ecf4 !important;
          border-color: rgba(30, 58, 95, 0.35) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-item-active a {
          color: #0f172a !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-prev .ant-pagination-item-link,
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-next .ant-pagination-item-link {
          color: #334155 !important;
          border-color: rgba(15, 23, 42, 0.12) !important;
          background: #ffffff !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-total-text {
          color: #64748b !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-disabled .ant-pagination-item-link,
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-disabled button {
          color: #cbd5e1 !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination .ant-pagination-item-ellipsis {
          color: #64748b !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-jump-prev .anticon,
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-jump-next .anticon,
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-prev .anticon,
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-next .anticon {
          color: #475569 !important;
        }
        .client-management-workspace-embed.client-management-container .ant-collapse-content .ant-pagination-item:not(.ant-pagination-item-active) a:hover {
          color: #0f172a !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-text:not(.ant-btn-primary):not(.ant-btn-danger),
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-icon-only:not(.ant-btn-primary):not(.ant-btn-danger) {
          color: #1e3a5f !important;
          background: transparent !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-text:not(.ant-btn-primary):not(.ant-btn-danger):hover,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-icon-only:not(.ant-btn-primary):not(.ant-btn-danger):hover,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-text:not(.ant-btn-primary):not(.ant-btn-danger):focus,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-icon-only:not(.ant-btn-primary):not(.ant-btn-danger):focus {
          color: #0f172a !important;
          background: rgba(15, 23, 42, 0.08) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-text:not(.ant-btn-primary):not(.ant-btn-danger) .anticon,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-icon-only:not(.ant-btn-primary):not(.ant-btn-danger) .anticon {
          color: #1e3a5f !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-primary.ant-btn-icon-only,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .action-btn-notes.ant-btn-primary {
          background: var(--section-color, #6366f1) !important;
          border-color: var(--section-color, #6366f1) !important;
          color: #ffffff !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-primary.ant-btn-icon-only:hover,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-primary.ant-btn-icon-only:focus,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .action-btn-notes.ant-btn-primary:hover,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .action-btn-notes.ant-btn-primary:focus {
          background: var(--section-color, #6366f1) !important;
          border-color: var(--section-color, #6366f1) !important;
          color: #ffffff !important;
          opacity: 0.92 !important;
          filter: brightness(0.95) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-primary,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-primary span {
          color: #ffffff !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-primary .anticon {
          color: #ffffff !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-default {
          background: #ffffff !important;
          color: #0f172a !important;
          border-color: rgba(15, 23, 42, 0.18) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .action-btn-revert.ant-btn {
          background: #ef4444 !important;
          border-color: #ef4444 !important;
          color: #ffffff !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .action-btn-revert.ant-btn:hover,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .action-btn-revert.ant-btn:focus {
          background: #dc2626 !important;
          border-color: #dc2626 !important;
          color: #ffffff !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-default:not(.action-btn-revert):hover,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-default:not(.action-btn-revert):focus {
          background: #f1f5f9 !important;
          color: #0f172a !important;
          border-color: rgba(15, 23, 42, 0.28) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-sm:not(.ant-btn-primary) {
          color: #1e293b !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-btn-sm.ant-btn-primary {
          color: #ffffff !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-card-body {
          padding-bottom: 18px !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-tag {
          background: rgba(15, 23, 42, 0.06) !important;
          border-color: rgba(15, 23, 42, 0.12) !important;
          color: #1e293b !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .action-btn-notes .anticon,
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .action-btn-revert .anticon {
          color: #ffffff !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card .ant-select-selector {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.12) !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card .ant-select-selection-item,
        .client-management-workspace-embed.client-management-container .ant-card .ant-select-selection-placeholder {
          color: #0f172a !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card .ant-select-arrow,
        .client-management-workspace-embed.client-management-container .ant-card .ant-select-clear {
          color: #64748b !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card .ant-select-clear:hover {
          color: #334155 !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small input[type="checkbox"] {
          width: 16px !important;
          height: 16px !important;
          accent-color: #1e3a5f !important;
          cursor: pointer !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card .ant-input,
        .client-management-workspace-embed.client-management-container .ant-card .ant-input-affix-wrapper,
        .client-management-workspace-embed.client-management-container .ant-card textarea {
          color: #0f172a !important;
          background: #ffffff !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card .ant-picker {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.12) !important;
          color: #0f172a !important;
        }
        .client-management-workspace-embed.client-management-container .ant-card.ant-card-small .ant-card-body {
          color: #334155 !important;
        }
        .client-management-workspace-embed.client-management-container .ant-empty-description {
          color: #64748b !important;
        }
        ` : ''}
      `}</style>
      <div
        className={`client-management-container${workspaceEmbed ? ' client-management-workspace-embed' : ''}`}
        style={
          workspaceEmbed
            ? {
                padding: '4px 2px 8px',
                width: '100%',
                height: '100%',
                minHeight: 0,
                boxSizing: 'border-box',
                maxWidth: '100%',
                margin: 0,
                overflow: 'auto',
                background: 'transparent',
              }
            : {
                padding: '24px',
                width: '100vw',
                minHeight: '100vh',
                boxSizing: 'border-box',
                maxWidth: '100vw',
                margin: 0,
              }
        }
      >
        <Card
          bodyStyle={{
            padding: workspaceEmbed ? '8px 10px 12px' : '24px',
            minHeight: workspaceEmbed ? 0 : 400,
            background: 'transparent',
          }}
          style={{
            width: '100%',
            boxShadow: workspaceEmbed ? 'none' : '0 4px 16px rgba(0,0,0,0.1)',
            borderRadius: workspaceEmbed ? 0 : '12px',
            background: workspaceEmbed ? 'transparent' : 'rgba(255, 255, 255, 0.15)',
            backdropFilter: workspaceEmbed ? undefined : 'blur(10px)',
            border: workspaceEmbed ? 'none' : undefined,
          }}
        >
          {!workspaceEmbed && (
          <div style={{ marginBottom: '32px' }}>
            <Title level={3} style={{ margin: 0, marginBottom: '8px', fontSize: 28, fontWeight: 700, color: '#ffffff' }}>
              Gestão de Clientes
            </Title>
            <Text type="secondary" style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.8)' }}>
              Gerir leads e acompanhar o progresso do trabalho
              {getActiveFiltersCount() > 0 && (
                <span style={{ color: '#6366f1', marginLeft: '8px', fontWeight: 500 }}>
                  (Filtrado: {filteredLeads.length} leads)
                </span>
              )}
            </Text>
          </div>
          )}

          {!workspaceEmbed && (
          <>
          {/* User Selection Dropdown for Team Leaders and up */}
          {user && user.role && hasRole(user, ['Admin', 'Diretor Comercial', 'Team Leader', 'Gestor de Equipa']) && (
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={16}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Text strong style={{ fontSize: 14, marginRight: '8px', color: '#ffffff' }}>
                    Ver leads de:
                  </Text>
                  <Select
                    value={selectedUserId}
                    onChange={setSelectedUserId}
                    className="modern-user-select"
                    style={{ width: 250 }}
                    size="large"
                    placeholder="Selecionar utilizador..."
                    loading={loadingUsers}
                    disabled={loadingUsers}
                  >
                    {viewableUsers.map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.username}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <Button
                    type="default"
                    icon={<FolderOutlined />}
                    onClick={() => setResourcesDrawerVisible(true)}
                    style={{ fontSize: 14, height: '36px', borderRadius: '8px' }}
                  >
                    Recursos
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleValidarDocumentos}
                    style={{ fontSize: 14, height: '36px', borderRadius: '8px' }}
                  >
                    Validar Documentos
                  </Button>
                </div>
              </Col>
            </Row>
          )}

          {/* Search, Filters, and Cobertura Buttons - All in one row */}
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={24}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                {/* Left side: Search Bar and Filter/View Mode Buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  {/* Search Bar - 30% bigger with Info Box */}
                  <div style={{ flex: '0 0 auto', minWidth: '390px', maxWidth: '520px', position: 'relative' }}>
                    <Search
                      placeholder="Procurar por nome, email, telefone ou NIF..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      className="modern-search-input"
                      style={{ width: '100%' }}
                      prefix={<SearchOutlined style={{ color: 'rgba(255, 255, 255, 0.9)' }} />}
                      size="small"
                      enterButton={false}
                    />
                    
                    {/* Info Box - Top Right of Search Bar */}
                    <div style={{ position: 'absolute', top: '-25px', right: '0', zIndex: 10 }}>
                      <Tooltip
                        title={
                          <div style={{ padding: '8px 0' }}>
                            <div style={{ fontWeight: 600, marginBottom: '8px' }}>Como procurar leads:</div>
                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }}>
                              <li><strong>Nome:</strong> Digite o nome completo ou parcial do lead</li>
                              <li><strong>Email:</strong> Procure pelo endereço de email completo</li>
                              <li><strong>Telefone:</strong> Digite o número de telefone (com ou sem espaços)</li>
                              <li><strong>NIF:</strong> Procure pelo número de identificação fiscal</li>
                            </ul>
                          </div>
                        }
                        placement="topRight"
                        overlayStyle={{ maxWidth: '350px' }}
                        overlayInnerStyle={{ 
                          border: 'none',
                          boxShadow: 'none'
                        }}
                      >
                        <Button
                          type="text"
                          icon={<InfoCircleOutlined />}
                          style={{ 
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '18px',
                            padding: '4px 8px',
                            height: 'auto',
                            backgroundColor: 'transparent',
                            border: 'none',
                            outline: 'none',
                            boxShadow: 'none'
                          }}
                        />
                      </Tooltip>
                    </div>
                  </div>
                  
                  {/* Filter and View Mode Buttons */}
                  <Space size="middle">
                <Dropdown
                  menu={{
                    items: []
                  }}
                  dropdownRender={() => (
                    <div style={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)', 
                      borderRadius: '12px', 
                      padding: '20px',
                      minWidth: '320px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                    }}>
                      {/* Operadora Filter */}
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px', color: '#ffffff' }}>Operadora</div>
                        <Select
                          mode="multiple"
                          placeholder="Selecionar operadoras"
                          value={advancedFilters.operadora}
                          onChange={handleOperadoraFilterChange}
                          className="modern-filter-select"
                          style={{ width: '100%' }}
                          size="large"
                          options={[
                            { label: 'Vodafone', value: 'Vodafone' },
                            { label: 'NOS', value: 'NOS' },
                            { label: 'MEO', value: 'MEO' }
                          ]}
                        />
                      </div>

                      {/* Mês Filter */}
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px', color: '#ffffff' }}>Mês</div>
                        <Select
                          mode="multiple"
                          placeholder="Selecionar meses"
                          value={advancedFilters.mes}
                          onChange={handleMesFilterChange}
                          className="modern-filter-select"
                          style={{ width: '100%' }}
                          size="large"
                          options={[
                            { label: 'Janeiro', value: 'janeiro' },
                            { label: 'Fevereiro', value: 'fevereiro' },
                            { label: 'Março', value: 'março' },
                            { label: 'Abril', value: 'abril' },
                            { label: 'Maio', value: 'maio' },
                            { label: 'Junho', value: 'junho' },
                            { label: 'Julho', value: 'julho' },
                            { label: 'Agosto', value: 'agosto' },
                            { label: 'Setembro', value: 'setembro' },
                            { label: 'Outubro', value: 'outubro' },
                            { label: 'Novembro', value: 'novembro' },
                            { label: 'Dezembro', value: 'dezembro' }
                          ]}
                        />
                      </div>

                      {/* Dia Filter */}
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px', color: '#ffffff' }}>Dia</div>
                        <Select
                          mode="multiple"
                          placeholder="Selecionar dias"
                          value={advancedFilters.dia}
                          onChange={handleDiaFilterChange}
                          className="modern-filter-select"
                          style={{ width: '100%' }}
                          size="large"
                          options={Array.from({ length: 31 }, (_, i) => ({
                            label: `${i + 1}`,
                            value: i + 1
                          }))}
                        />
                      </div>

                      {/* Active Filters Summary */}
                      {(advancedFilters.operadora.length > 0 || advancedFilters.dia.length > 0 || advancedFilters.mes.length > 1) && (
                        <div style={{ 
                          padding: '12px', 
                          backgroundColor: '#eff6ff', 
                          borderRadius: '8px',
                          marginBottom: '16px',
                          fontSize: '13px'
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: '6px', color: '#ffffff' }}>Filtros Ativos:</div>
                          {advancedFilters.operadora.length > 0 && (
                            <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Operadora: {advancedFilters.operadora.join(', ')}</div>
                          )}
                          {advancedFilters.mes.length > 1 && (
                            <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Mês: {advancedFilters.mes.join(', ')}</div>
                          )}
                          {advancedFilters.dia.length > 0 && (
                            <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Dia: {advancedFilters.dia.join(', ')}</div>
                          )}
                        </div>
                      )}
                      


                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                        <Button 
                          size="small" 
                          onClick={clearAllFilters}
                          className="action-btn-clear"
                          style={{ 
                            fontSize: '13px', 
                            borderRadius: '6px', 
                            height: '32px'
                          }}
                        >
                          Limpar
                        </Button>
                        <Button 
                          size="small" 
                          type="primary"
                          className="action-btn-apply"
                          style={{ 
                            fontSize: '13px', 
                            borderRadius: '6px', 
                            height: '32px',
                            '--section-color': getButtonColors('apply').bg
                          } as React.CSSProperties & { '--section-color': string }}
                        >
                          Aplicar
                        </Button>
                      </div>
                    </div>
                  )}
                  trigger={['click']}
                  placement="bottomRight"
                >
                  <Button 
                    icon={<FilterOutlined />}
                    className="action-btn-filter"
                    style={{ 
                      fontSize: 14, 
                      height: '36px', 
                      borderRadius: '8px', 
                      fontWeight: 500,
                      ...(getActiveFiltersCount() > 0 ? {
                        background: getButtonColors('filter').bg,
                        borderColor: getButtonColors('filter').border,
                        color: getButtonColors('filter').text
                      } : {
                        background: getButtonColors('viewModeInactive').bg,
                        borderColor: getButtonColors('viewModeInactive').border,
                        color: getButtonColors('viewModeInactive').text
                      })
                    }}
                  >
                    Filtros {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}
                  </Button>
                </Dropdown>
                {/* See Own Leads Button - Only visible for Team Leader and up */}
                {user && user.role && isAdminOrDCOrTeamLeader(user.role) && (
                  <Button 
                    className="action-btn-my-leads"
                    style={{ 
                      fontSize: 14, 
                      height: '36px', 
                      borderRadius: '8px', 
                      fontWeight: 500,
                      ...(showOwnLeadsOnly ? {
                        background: getButtonColors('myLeads').bg,
                        borderColor: getButtonColors('myLeads').border,
                        color: getButtonColors('myLeads').text
                      } : {
                        background: getButtonColors('myLeadsInactive').bg,
                        borderColor: getButtonColors('myLeadsInactive').border,
                        color: getButtonColors('myLeadsInactive').text
                      })
                    }}
                    onClick={() => setShowOwnLeadsOnly(!showOwnLeadsOnly)}
                    title={showOwnLeadsOnly ? 'Mostrar todos os leads' : 'Mostrar apenas as minhas leads'}
                  >
                    {showOwnLeadsOnly ? 'Minhas Leads ✓' : 'Minhas Leads'}
                  </Button>
                )}
                {!workspaceEmbed && (
                <Button 
                  className="action-btn-view-mode"
                  icon={<TableOutlined />}
                  onClick={() => setViewMode('grid')}
                  style={{ 
                    fontSize: 14, 
                    height: '36px', 
                    borderRadius: '8px', 
                    fontWeight: 500,
                    ...(viewMode === 'grid' ? {
                      background: getButtonColors('viewMode').bg,
                      borderColor: getButtonColors('viewMode').border,
                      color: getButtonColors('viewMode').text
                    } : {
                      background: getButtonColors('viewModeInactive').bg,
                      borderColor: getButtonColors('viewModeInactive').border,
                      color: getButtonColors('viewModeInactive').text
                    })
                  }}
                >
                  Tabela
                </Button>
                )}
                {!workspaceEmbed && (
                <Button 
                  className="action-btn-view-mode"
                  icon={<AppstoreOutlined />}
                  onClick={() => setViewMode('kanban')}
                  style={{ 
                    fontSize: 14, 
                    height: '36px', 
                    borderRadius: '8px', 
                    fontWeight: 500,
                    ...(viewMode === 'kanban' ? {
                      background: getButtonColors('viewMode').bg,
                      borderColor: getButtonColors('viewMode').border,
                      color: getButtonColors('viewMode').text
                    } : {
                      background: getButtonColors('viewModeInactive').bg,
                      borderColor: getButtonColors('viewModeInactive').border,
                      color: getButtonColors('viewModeInactive').text
                    })
                  }}
                >
                  Kanban
                </Button>
                )}
                {(unsavedChanges.size > 0 || unsavedSectionChanges.size > 0 || pendenteReasonsChanged.size > 0 || followUpNotesChanged.size > 0 || seminteresseNotesChanged.size > 0 || informacaoErradaNotesChanged.size > 0 || endOfContractDatesChanged.size > 0 || lastContactAttemptsChanged.size > 0 || contactAttemptCountsChanged.size > 0 || installationDatesChanged.size > 0 || unsavedProductTypes.size > 0 || unsavedAdditionalCards.size > 0 || instaladosBusinessAreaChanged.size > 0 || instaladosSelectedProductsChanged.size > 0) && (
                  <Button 
                    type="primary" 
                    onClick={handleConfirmSection} 
                    className="action-btn-save"
                    style={{ 
                      fontSize: 14, 
                      height: '36px', 
                      borderRadius: '8px', 
                      fontWeight: 500,
                      '--section-color': getButtonColors('save').bg
                    } as React.CSSProperties & { '--section-color': string }}
                  >
                    Guardar Alterações ({(unsavedChanges.size + unsavedSectionChanges.size + pendenteReasonsChanged.size + followUpNotesChanged.size + seminteresseNotesChanged.size + informacaoErradaNotesChanged.size + endOfContractDatesChanged.size + lastContactAttemptsChanged.size + contactAttemptCountsChanged.size + installationDatesChanged.size + unsavedProductTypes.size + unsavedAdditionalCards.size + instaladosBusinessAreaChanged.size + instaladosSelectedProductsChanged.size)} alterações)
                  </Button>
                )}
                  </Space>
                </div>
                
                {/* Right side: Venda and Cobertura Buttons */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
                  <Button 
                    icon={<ShoppingCartOutlined />}
                    className="action-btn-venda"
                    style={{ 
                      fontSize: 14, 
                      height: '36px', 
                      borderRadius: '8px', 
                      fontWeight: 600,
                      background: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1',
                      borderColor: sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1',
                      color: '#ffffff',
                      boxShadow: `0 4px 12px ${sectionColors[activeSection as keyof typeof sectionColors] ? `${sectionColors[activeSection as keyof typeof sectionColors]}66` : 'rgba(99, 102, 241, 0.4)'}`,
                      transition: 'all 0.3s ease',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      const sectionColor = sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1';
                      const shadowColor = sectionColor === '#6366f1' ? 'rgba(99, 102, 241, 0.6)' : 
                                        sectionColor === '#10b981' ? 'rgba(16, 185, 129, 0.6)' :
                                        sectionColor === '#8b5cf6' ? 'rgba(139, 92, 246, 0.6)' :
                                        'rgba(6, 182, 212, 0.6)';
                      e.currentTarget.style.boxShadow = `0 6px 16px ${shadowColor}`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      const sectionColor = sectionColors[activeSection as keyof typeof sectionColors] || '#6366f1';
                      const shadowColor = sectionColor === '#6366f1' ? 'rgba(99, 102, 241, 0.4)' : 
                                        sectionColor === '#10b981' ? 'rgba(16, 185, 129, 0.4)' :
                                        sectionColor === '#8b5cf6' ? 'rgba(139, 92, 246, 0.4)' :
                                        'rgba(6, 182, 212, 0.4)';
                      e.currentTarget.style.boxShadow = `0 4px 12px ${shadowColor}`;
                    }}
                    onClick={() => handleOpenVendaNew()}
                  >
                    {adjustedCommissions?.active_breakpoint && (
                      <div style={{
                        position: 'absolute',
                        top: -14,
                        left: -6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        background: '#ff4d4f',
                        borderRadius: 12,
                        padding: '2px 6px',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                        zIndex: 1,
                        border: '1px solid #fff'
                      }}>
                        <FireOutlined 
                          style={{ 
                            fontSize: 12,
                            color: '#fff'
                          }} 
                        />
                        <span style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#fff',
                          lineHeight: 1
                        }}>
                          {adjustedCommissions.current_instalados || currentInstalados}
                        </span>
                      </div>
                    )}
                    {adjustedCommissionsSeguranca?.active_breakpoint && (
                      <div style={{
                        position: 'absolute',
                        top: -14,
                        right: -6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                        borderRadius: 12,
                        padding: '2px 6px',
                        boxShadow: '0 2px 4px rgba(238, 90, 111, 0.4)',
                        zIndex: 1,
                        border: '1px solid #fff'
                      }}>
                        <SafetyCertificateOutlined 
                          style={{ 
                            fontSize: 12,
                            color: '#fff'
                          }} 
                        />
                        <span style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#fff',
                          lineHeight: 1
                        }}>
                          {adjustedCommissionsSeguranca.current_instalados || segurancaProductsSold}
                        </span>
                      </div>
                    )}
                    Venda
                  </Button>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'vodafone',
                          label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                              <div style={{ 
                                width: '12px', 
                                height: '12px', 
                                borderRadius: '50%', 
                                backgroundColor: '#e60000',
                                boxShadow: '0 0 4px rgba(230, 0, 0, 0.5)'
                              }}></div>
                              <span style={{ fontWeight: 600 }}>Vodafone</span>
                            </div>
                          ),
                          onClick: () => window.open(coberturaUrls[0], '_blank', 'width=1200,height=800,resizable,scrollbars')
                        },
                        {
                          key: 'meo',
                          label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                              <div style={{ 
                                width: '12px', 
                                height: '12px', 
                                borderRadius: '50%', 
                                backgroundColor: '#ff6b00',
                                boxShadow: '0 0 4px rgba(255, 107, 0, 0.5)'
                              }}></div>
                              <span style={{ fontWeight: 600 }}>MEO</span>
                            </div>
                          ),
                          onClick: () => window.open(coberturaUrls[1], '_blank', 'width=1200,height=800,resizable,scrollbars')
                        },
                        {
                          key: 'nos',
                          label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                              <div style={{ 
                                width: '12px', 
                                height: '12px', 
                                borderRadius: '50%', 
                                backgroundColor: '#00a651',
                                boxShadow: '0 0 4px rgba(0, 166, 81, 0.5)'
                              }}></div>
                              <span style={{ fontWeight: 600 }}>NOS</span>
                            </div>
                          ),
                          onClick: () => window.open(coberturaUrls[2], '_blank', 'width=1200,height=800,resizable,scrollbars')
                        }
                      ],
                      style: {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '12px',
                        padding: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <Button 
                      className="action-btn-cobertura"
                      style={{ 
                        fontSize: 14, 
                        height: '36px', 
                        borderRadius: '8px', 
                        fontWeight: 600,
                        background: getButtonColors('cobertura').bg,
                        borderColor: getButtonColors('cobertura').border,
                        color: getButtonColors('cobertura').text,
                        boxShadow: `0 4px 12px ${getButtonColors('cobertura').bg}40`,
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = `0 6px 16px ${getButtonColors('cobertura').bg}60`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = `0 4px 12px ${getButtonColors('cobertura').bg}40`;
                      }}
                    >
                      Cobertura
                    </Button>
                  </Dropdown>
                </div>
              </div>
            </Col>
          </Row>
          </>
          )}

          {/* Section Tabs */}
          <Tabs 
            activeKey={activeSection} 
            onChange={handleSectionChange}
            type="card"
            style={{ marginBottom: workspaceEmbed ? '12px' : '24px', fontSize: 14 }}
            items={sections.map(section => {
              const isActive = activeSection === section.key;
              const count = getFilteredCount(section.key) || 0;
              return {
                key: section.key,
                label: (
                  <div style={{ fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: section.color, marginRight: '4px' }}>●</span>
                    <span>{section.label}</span>
                    <span 
                      className={`section-tab-badge ${isActive ? 'section-tab-badge-active' : 'section-tab-badge-inactive'}`}
                      data-section={section.key}
                      style={{
                        backgroundColor: isActive ? '#0f172a' : '#1e3a8a',
                        color: '#ffffff',
                        border: isActive ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
                        display: 'inline-flex',
                        visibility: 'visible',
                        opacity: 1
                      }}
                    >
                      {count > 99 ? '99+' : count.toString()}
                    </span>
                  </div>
                )
              };
            })}
          />

          {/* Content Area */}
          <div style={{ minHeight: workspaceEmbed ? 0 : '300px', width: '100%' }}>
            {viewMode === 'grid' ? renderGridView() : renderKanbanView()}
          </div>


        </Card>

        <LeadDetailsModal 
          open={viewModalVisible} 
          lead={selectedLead} 
          onClose={handleCloseModal} 
        />

        <VodafoneDocumentWizard
          open={vodafoneWizardVisible}
          lead={selectedLeadForDocuments}
          onClose={handleCloseVodafoneWizard}
          onSave={() => {
            // Show success message that documents are now available
            message.success('Documentos guardados! Pode visualizá-los na secção "Documentos".');
            // Refresh documents modal if it's open
            refreshDocumentsModal();
          }}
        />

        <LeadDocumentsModal
          open={documentsModalVisible}
          leadId={selectedLeadForDocumentsList?.id || 0}
          leadName={selectedLeadForDocumentsList ? `${selectedLeadForDocumentsList.name}` : ''}
          onClose={handleCloseDocumentsModal}
          onRefresh={refreshDocumentsModal}
          refreshTrigger={documentsRefreshTrigger}
        />

        <Modal
          open={showUnsavedModal}
          onCancel={keepChanges}
          onOk={discardChanges}
          okText="Descartar Alterações"
          cancelText="Cancelar"
          title="Tem alterações por guardar!"
        >
          <p style={{ fontSize: 14 }}>Tem alterações não guardadas nesta secção. Se continuar, irá perder as alterações. Deseja descartar as alterações?</p>
        </Modal>


      </div>
      <Modal
        open={showMissingMotivoModal}
        title="Motivo obrigatório para leads pendentes"
        onCancel={() => setShowMissingMotivoModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowMissingMotivoModal(false)}>
            OK
          </Button>
        ]}
      >
        <p>Antes de sair desta secção, indique o motivo para todos os leads em estado <b>Pendente</b>:</p>
        <ul>
          {missingMotivoLeads.map(lead => (
            <li key={lead.id}>
              <strong style={getLeadCategoryStyle(lead.lead_category)}>{lead.name}</strong> ({lead.email || lead.phone || 'Sem contacto'})
            </li>
          ))}
        </ul>
      </Modal>

      <EventModal
        open={eventModalVisible}
        onClose={handleEventModalClose}
        onSuccess={handleEventModalSuccess}
        preSelectedLead={selectedLeadForEvent}
      />

      <NOSDocumentWizard
        open={nosWizardVisible}
        lead={selectedLeadForNOSDocuments}
        onClose={handleCloseNOSWizard}
        onSave={() => {
          message.success('Documentos NOS guardados! Pode visualizá-los na secção "Documentos".');
          refreshDocumentsModal();
        }}
      />

      <NOSSecuritasDocumentWizard
        open={nosSecuritasWizardVisible}
        lead={selectedLeadForNOSSecuritasDocuments}
        onClose={handleCloseNOSSecuritasWizard}
        onSave={() => {
          message.success('Documentos NOS Securitas guardados! Pode visualizá-los na secção "Documentos".');
          refreshDocumentsModal();
        }}
      />

      <EnergyProposalWizard
        open={energyProposalWizardVisible}
        lead={selectedLeadForEnergyProposal}
        marcacaoId={selectedMarcacaoIdForProposal}
        onClose={handleCloseEnergyProposalWizard}
        onSave={() => {
          message.success('Proposta enviada com sucesso!');
        }}
      />

      <ProsegurForm
        open={prosegurFormVisible}
        lead={selectedLeadForProsegur}
        onClose={handleCloseProsegurForm}
        onSave={() => {
          message.success('Dados Prosegur guardados com sucesso!');
        }}
      />

      <PDFUploadModal
        visible={pdfUploadModalVisible}
        onClose={handleClosePdfUploadModal}
        lead={selectedLeadForPdfUpload}
        fieldName="documentos_assinados"
        stage="registro"
        businessArea={selectedLeadForPdfUpload?.business_area || 'telecommunications'}
      />

      <EmailModal
        visible={emailModalVisible}
        onClose={handleCloseEmailModal}
        lead={selectedLeadForEmail}
      />

      {/* Chat Modal - Disabled */}
      {/* <ChatModal
        isOpen={chatModalVisible}
        onClose={handleCloseChatModal}
        currentUser={user}
        preSelectedLead={selectedLeadForChat}
        preSelectedConversationId={selectedConversationId || undefined}
      /> */}

      {/* Document Validation Modal */}
      <Modal
        title="Validar Documentos"
        open={validarDocumentosModalVisible}
        onCancel={handleCloseValidarDocumentosModal}
        footer={null}
        width={1000}
        confirmLoading={loadingValidationLeads}
      >
        {loadingValidationLeads ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text>A carregar leads para validação...</Text>
          </div>
        ) : validationLeads.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">Nenhum lead em validação encontrado</Text>
          </div>
        ) : (
          <div>
            <Text strong style={{ fontSize: 16, marginBottom: 16, display: 'block' }}>
              Leads em Validação ({validationLeads.length})
            </Text>
            {validationLeads.map((lead) => (
              <Card
                key={lead.id}
                style={{ marginBottom: 16 }}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Space>
                        <Text strong style={getLeadCategoryStyle(lead.lead_category)}>{lead.name}</Text>
                        {/* Show user icon for high rank users' own leads */}
                        {user && (user.role === 'team_leader' || user.role === 'Team Leader' || user.role === 'admin' || user.role === 'Admin' || user.role === 'super_admin' || user.role === 'Diretor Comercial' || user.role === 'Gestor de Equipa') && 
                         lead.assigned_to === user.id && (
                          <span style={{ fontSize: '14px' }}>🌸</span>
                        )}
                        {/* Show star icon for leads with highlighted notes */}
                        {(() => {
                          const highlightedNotes = highlightedNotesMap.get(lead.id);
                          return highlightedNotes && highlightedNotes.length > 0;
                        })() && (
                          <StarFilled style={{ color: '#faad14', fontSize: '14px' }} />
                        )}
                      </Space>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {lead.email || lead.phone || 'Sem contacto'} • {lead.business_area || 'Telecomunicações'}
                      </Text>
                      {/* Show team indicator for high rank users viewing team members' leads */}
                      {(user?.role === 'team_leader' || user?.role === 'Team Leader' || user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'super_admin' || user?.role === 'Diretor Comercial' || user?.role === 'Gestor de Equipa') && lead.assigned_to !== user?.id && (
                        <Tag color="blue" style={{ marginTop: '4px', fontSize: '10px' }}>
                          Equipa
                        </Tag>
                      )}
                    </div>
                    <Space>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => handleApproveLead(lead.id)}
                        icon={<CheckCircleOutlined />}
                      >
                        Aprovar
                      </Button>
                      {userMayUseClientManagementEmailButton(user) && (
                        <Button
                          size="small"
                          onClick={() => handleSendEmail(lead)}
                          icon={<MailOutlined />}
                        >
                          Enviar Email
                        </Button>
                      )}
                        {/* Chat button disabled */}
                        {/* {user?.role && isAdminOrDCOrTeamLeader(user.role) && lead.assigned_to && lead.assigned_to !== user.id && (
                          <Button
                            size="small"
                            onClick={() => handleMessageLead(lead)}
                            icon={<MessageOutlined />}
                            style={{ marginLeft: 8 }}
                          >
                            Mensagem
                          </Button>
                        )} */}
                      <Button
                        danger
                        size="small"
                        onClick={() => handleRejectLead(lead.id)}
                      >
                        Rejeitar
                      </Button>
                    </Space>
                  </div>
                }
              >
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Documentos Carregados:</Text>
                  <DocumentValidationList lead={lead} onPreview={handlePreviewFile} onDownload={handleDownloadFile} refreshTrigger={validationRefreshTrigger} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </Modal>

      {/* PDF Preview Modal */}
      <Modal
        title={`Pré-visualização - ${previewTitle}`}
        open={previewVisible}
        onCancel={() => {
          setPreviewVisible(false);
          if (previewBlobUrl) {
            URL.revokeObjectURL(previewBlobUrl);
            setPreviewBlobUrl('');
          }
        }}
        footer={[
          <Button key="close" onClick={() => {
            setPreviewVisible(false);
            if (previewBlobUrl) {
              URL.revokeObjectURL(previewBlobUrl);
              setPreviewBlobUrl('');
            }
          }}>
            Fechar
          </Button>,
        ]}
        width={800}
        style={{ top: 20 }}
      >
        {previewLoading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '600px',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <Spin size="large" />
            <div>A carregar pré-visualização...</div>
          </div>
        ) : previewData ? (
          <div>
            <div style={{ marginBottom: '10px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
              Debug: Data length: {previewData.length} chars | Blob URL: {previewBlobUrl ? 'Created' : 'Not created'}
            </div>
            <iframe
              src={previewBlobUrl || `data:application/pdf;base64,${previewData}`}
              width="100%"
              height="600px"
              style={{ border: 'none' }}
              onLoad={() => {}}
              onError={(e) => {}}
            />
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '600px',
            color: 'rgba(255, 255, 255, 0.7)'
          }}>
            Nenhum documento para pré-visualizar
          </div>
        )}
      </Modal>

      <Modal
        open={showMissingNotaModal}
        onCancel={() => setShowMissingNotaModal(false)}
        onOk={() => {
          setShowMissingNotaModal(false);
          if (pendingSectionChangeNota) {
            setActiveSection(pendingSectionChangeNota);
            setSubsectionPagination(new Map());
          }
        }}
        okText="OK"
        cancelText="Cancelar"
        title="Nota obrigatória para leads em Follow-up"
      >
        <p style={{ fontSize: 14 }}>Antes de sair desta secção, indique a nota para todos os leads em estado <b>Follow-up</b>:</p>
        <ul>
          {missingNotaLeads.map(lead => (
            <li key={lead.id}><span style={getLeadCategoryStyle(lead.lead_category)}>{lead.name}</span> ({lead.email || lead.phone || 'sem contacto'})</li>
          ))}
        </ul>
      </Modal>

      <Modal
        open={showMissingSemInteresseNotaModal}
        onCancel={() => setShowMissingSemInteresseNotaModal(false)}
        onOk={() => {
          setShowMissingSemInteresseNotaModal(false);
          if (pendingSectionChangeSemInteresseNota) {
            setActiveSection(pendingSectionChangeSemInteresseNota);
            setSubsectionPagination(new Map());
          }
        }}
        okText="OK"
        cancelText="Cancelar"
        title="Nota obrigatória para leads em Sem Interesse"
      >
        <p style={{ fontSize: 14 }}>Antes de sair desta secção, indique a nota para todos os leads em estado <b>Sem Interesse</b>:</p>
        <ul>
          {missingSemInteresseNotaLeads.map(lead => (
            <li key={lead.id}><span style={getLeadCategoryStyle(lead.lead_category)}>{lead.name}</span> ({lead.email || lead.phone || 'sem contacto'})</li>
          ))}
        </ul>
      </Modal>

      <Modal
        open={showMissingInformacaoErradaNotaModal}
        onCancel={() => setShowMissingInformacaoErradaNotaModal(false)}
        onOk={() => {
          setShowMissingInformacaoErradaNotaModal(false);
          if (pendingSectionChangeInformacaoErradaNota) {
            setActiveSection(pendingSectionChangeInformacaoErradaNota);
            setSubsectionPagination(new Map());
          }
        }}
        okText="OK"
        cancelText="Cancelar"
        title="Informação Errada obrigatória para leads"
      >
        <p style={{ fontSize: 14 }}>Antes de sair desta secção, indique a informação errada para todos os leads:</p>
        <ul>
          {missingInformacaoErradaNotaLeads.map(lead => (
            <li key={lead.id}><span style={getLeadCategoryStyle(lead.lead_category)}>{lead.name}</span> ({lead.email || lead.phone || 'sem informação'})</li>
          ))}
        </ul>
      </Modal>

      <Modal
        open={showMissingSemCoberturaModal}
        onCancel={() => setShowMissingSemCoberturaModal(false)}
        onOk={() => {
          setShowMissingSemCoberturaModal(false);
          if (pendingSectionChangeSemCobertura) {
            setActiveSection(pendingSectionChangeSemCobertura);
            setSubsectionPagination(new Map());
          }
        }}
        okText="OK"
        cancelText="Cancelar"
        title="Sem Cobertura obrigatório para leads"
      >
        <p style={{ fontSize: 14 }}>Antes de sair desta secção, indique o operador sem cobertura para todos os leads:</p>
        <ul>
          {missingSemCoberturaLeads.map(lead => (
            <li key={lead.id}><span style={getLeadCategoryStyle(lead.lead_category)}>{lead.name}</span> ({lead.email || lead.phone || 'sem informação'})</li>
          ))}
        </ul>
      </Modal>

      {/* Edit Lead Modal */}
      <Modal
        title="Editar Lead"
        open={editModalVisible}
        onCancel={handleEditModalClose}
        footer={[
          <Button key="cancel" onClick={handleEditModalClose}>
            Cancelar
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            loading={savingEdit}
            onClick={handleSaveEdit}
          >
            Guardar
          </Button>,
        ]}
        width={800}
      >
        {editingLead && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Nome *</Text>
                  <Input
                    value={editFormData.name || ''}
                    onChange={(e) => handleEditFormChange('name', e.target.value)}
                    placeholder="Nome completo"
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Email</Text>
                  <Input
                    value={editFormData.email || ''}
                    onChange={(e) => handleEditFormChange('email', e.target.value)}
                    placeholder="email@exemplo.com"
                  />
                </div>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Telefone</Text>
                  <Input
                    value={editFormData.phone || ''}
                    onChange={(e) => handleEditFormChange('phone', e.target.value)}
                    placeholder="+351 123 456 789"
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Empresa</Text>
                  <Input
                    value={editFormData.company || ''}
                    onChange={(e) => handleEditFormChange('company', e.target.value)}
                    placeholder="Nome da empresa"
                  />
                </div>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Morada</Text>
                  <Input
                    value={editFormData.address || ''}
                    onChange={(e) => handleEditFormChange('address', e.target.value)}
                    placeholder="Morada completa"
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Cidade</Text>
                  <Input
                    value={editFormData.city || ''}
                    onChange={(e) => handleEditFormChange('city', e.target.value)}
                    placeholder="Cidade"
                  />
                </div>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Código Postal</Text>
                  <Input
                    value={editFormData.postal_code || ''}
                    onChange={(e) => handleEditFormChange('postal_code', e.target.value)}
                    placeholder="1000-000"
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>País</Text>
                  <Input
                    value={editFormData.country || ''}
                    onChange={(e) => handleEditFormChange('country', e.target.value)}
                    placeholder="Portugal"
                  />
                </div>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Operador Atual</Text>
                  <Input
                    value={editFormData.current_provider || ''}
                    onChange={(e) => handleEditFormChange('current_provider', e.target.value)}
                    placeholder="Operador atual"
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  {/* Empty column for layout */}
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Notes Panel */}
      <NotesPanel
        visible={notesPanelVisible}
        onCancel={handleCloseNotes}
        leadId={selectedLeadForNotes?.id || 0}
        notes={leadNotes}
        onNotesChange={handleNotesChange}
        onClose={handleCloseNotes}
      />

      {/* Altice Residencial Modal */}
      <Modal
        title={`Altice Residencial - ${selectedLead ? selectedLead.name : 'Nova Venda'}`}
        open={alticeResidencialModalVisible}
        onCancel={handleCloseAlticeResidencialModal}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        <AlticeResidencialForm 
          lead={selectedLead ? { 
            id: selectedLead.id, 
            name: selectedLead.name,
            email: selectedLead.email,
            phone: selectedLead.phone,
            address: selectedLead.address,
            city: selectedLead.city
          } : undefined}
          onClose={handleCloseAlticeResidencialModal}
        />
      </Modal>

      {/* Task Modal */}
      <Modal
        title="✨ Criar Nova Tarefa"
        open={taskModalVisible}
        onCancel={() => setTaskModalVisible(false)}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        <TaskMaster 
          preSelectedLead={selectedLeadForTask}
          onTaskCreated={() => setTaskModalVisible(false)}
          isModal={true}
        />
      </Modal>

      {/* Assignment Modal */}
      <Modal
        title="Atribuir Lead"
        open={assignmentModalVisible}
        onCancel={handleAssignmentCancel}
        footer={null}
        width={400}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Lead: </Text>
          <Text>{leadToAssign?.name}</Text>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Text strong>Atribuir a:</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Selecionar utilizador"
            showSearch
            optionFilterProp="children"
            onChange={handleAssignmentConfirm}
          >
            {availableUsers.map(user => (
              <Option key={user.id} value={user.id}>
                {user.username}
              </Option>
            ))}
          </Select>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Button onClick={handleAssignmentCancel} style={{ marginRight: 8 }}>
            Cancelar
          </Button>
        </div>
      </Modal>

      {/* Product Resources Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FolderOutlined style={{ fontSize: '20px', color: '#ffffff' }} />
            <span style={{ fontSize: '18px', fontWeight: 600, color: '#ffffff' }}>Recursos de Produtos</span>
          </div>
        }
        placement="right"
        onClose={() => setResourcesDrawerVisible(false)}
        open={resourcesDrawerVisible}
        width={500}
        mask={true}
        maskStyle={{
          backgroundColor: 'rgba(0, 0, 0, 0.45)'
        }}
        maskClosable={true}
        className="product-resources-drawer"
        zIndex={999}
        headerStyle={{
          background: 'transparent',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '20px 24px'
        }}
        bodyStyle={{
          padding: '0',
          background: 'transparent',
          minHeight: '100%'
        }}
      >
        <Tabs
          activeKey={activeResourceTab}
          onChange={setActiveResourceTab}
          items={[
            {
              key: 'telecomunicacao',
              label: 'Telecomunicação',
              children: (
                <div>
                  {loadingResources ? (
                    <GhostLoader />
                  ) : (
                    <>
                      {user && user.role && (isAdminOrDCOrTeamLeader(user.role) || canCreateFolders(user.role)) && (
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                          {isAdminOrDCOrTeamLeader(user.role) && (
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              loading={uploadingPdf}
                              onClick={() => handleUploadClick('telecomunicacao')}
                              style={{ 
                                flex: 1,
                                height: '40px',
                                background: 'transparent',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                color: '#ffffff',
                                fontWeight: 500
                              }}
                            >
                              Carregar PDF
                            </Button>
                          )}
                          {canCreateFolders(user.role) && (
                            <Button
                              type="primary"
                              icon={<FolderOutlined />}
                              onClick={() => {
                                folderForm.setFieldsValue({ category: 'telecomunicacao', parent_folder_id: null });
                                setFolderModalVisible(true);
                              }}
                              style={{ 
                                flex: 1,
                                height: '40px',
                                background: 'transparent',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                color: '#ffffff',
                                fontWeight: 500
                              }}
                            >
                              Criar Pasta
                            </Button>
                          )}
                        </div>
                      )}
                      {/* Display Folders */}
                      {(() => {
                        // Filter to only show root folders (folders without parent_folder_id)
                        const rootFolders = resourceFolders.telecomunicacao.filter(f => !f.parent_folder_id || f.parent_folder_id === null);
                        return rootFolders.length > 0 && (
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                              Pastas
                            </div>
                            <Collapse ghost>
                              {rootFolders.map((folder) => 
                                renderFolderWithSubfolders(folder, 'telecomunicacao', resourceFolders.telecomunicacao, productPdfs.telecomunicacao)
                              )}
                            </Collapse>
                          </div>
                        );
                      })()}
                      
                      {/* Display PDFs without folders */}
                      {(() => {
                        const pdfsWithoutFolders = productPdfs.telecomunicacao.filter(pdf => !pdf.folder_id || pdf.folder_id === null || pdf.folder_id === undefined);
                        return pdfsWithoutFolders.length > 0 && (
                          <>
                            {resourceFolders.telecomunicacao.length > 0 && (
                              <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, marginBottom: '12px', fontSize: '14px', marginTop: '16px' }}>
                                PDFs (sem pasta)
                              </div>
                            )}
                            <Collapse ghost>
                              {pdfsWithoutFolders.map((item) => (
                        <Panel
                          key={item.id.toString()}
                          header={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', minWidth: 0 }}>
                              <FilePdfIcon style={{ fontSize: '20px', color: '#ffffff', flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                <div style={{ color: '#ffffff', fontWeight: 600, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  Carregado por {item.uploaded_by} em {new Date(item.uploaded_at).toLocaleDateString('pt-PT')}
                                </div>
                              </div>
                              {user && user.role && isAdminOrDCOrTeamLeader(user.role) && (
                                <Popconfirm
                                  title={
                                    <div>
                                      <div style={{ fontWeight: 600, marginBottom: '8px' }}>Eliminar PDF</div>
                                      <div>Tem a certeza que deseja eliminar este PDF?</div>
                                    </div>
                                  }
                                  onConfirm={() => handleDeleteProductPdf('telecomunicacao', item.id)}
                                  okText="Sim"
                                  cancelText="Não"
                                  okButtonProps={{ danger: true }}
                                >
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: '#ff4d4f', flexShrink: 0 }}
                                  />
                                </Popconfirm>
                              )}
                            </div>
                          }
                          style={{
                            background: 'transparent',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '8px',
                            marginBottom: '12px'
                          }}
                        >
                          <PdfViewerPanel item={item} />
                        </Panel>
                              ))}
                            </Collapse>
                          </>
                        );
                      })()}
                      
                      {productPdfs.telecomunicacao.length === 0 && resourceFolders.telecomunicacao.length === 0 && (
                        <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
                          Nenhum recurso disponível
                        </div>
                      )}
                    </>
                  )}
                </div>
              )
            },
            {
              key: 'seguranca',
              label: 'Segurança',
              children: (
                <div>
                  {loadingResources ? (
                    <GhostLoader />
                  ) : (
                    <>
                      {user && user.role && (isAdminOrDCOrTeamLeader(user.role) || canCreateFolders(user.role)) && (
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                          {isAdminOrDCOrTeamLeader(user.role) && (
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              loading={uploadingPdf}
                              onClick={() => handleUploadClick('seguranca')}
                              style={{ 
                                flex: 1,
                                height: '40px',
                                background: 'transparent',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                color: '#ffffff',
                                fontWeight: 500
                              }}
                            >
                              Carregar PDF
                            </Button>
                          )}
                          {canCreateFolders(user.role) && (
                            <Button
                              type="primary"
                              icon={<FolderOutlined />}
                              onClick={() => {
                                folderForm.setFieldsValue({ category: 'seguranca', parent_folder_id: null });
                                setFolderModalVisible(true);
                              }}
                              style={{ 
                                flex: 1,
                                height: '40px',
                                background: 'transparent',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                color: '#ffffff',
                                fontWeight: 500
                              }}
                            >
                              Criar Pasta
                            </Button>
                          )}
                        </div>
                      )}
                      {/* Display Folders */}
                      {(() => {
                        // Filter to only show root folders (folders without parent_folder_id)
                        const rootFolders = resourceFolders.seguranca.filter(f => !f.parent_folder_id || f.parent_folder_id === null);
                        return rootFolders.length > 0 && (
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                              Pastas
                            </div>
                            <Collapse ghost>
                              {rootFolders.map((folder) => 
                                renderFolderWithSubfolders(folder, 'seguranca', resourceFolders.seguranca, productPdfs.seguranca)
                              )}
                            </Collapse>
                          </div>
                        );
                      })()}
                      
                      {/* Display PDFs without folders */}
                      {(() => {
                        const pdfsWithoutFolders = productPdfs.seguranca.filter(pdf => !pdf.folder_id);
                        return pdfsWithoutFolders.length > 0 && (
                          <>
                            {resourceFolders.seguranca.length > 0 && (
                              <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, marginBottom: '12px', fontSize: '14px', marginTop: '16px' }}>
                                PDFs (sem pasta)
                              </div>
                            )}
                            <Collapse ghost>
                              {pdfsWithoutFolders.map((item) => (
                        <Panel
                          key={item.id.toString()}
                          header={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', minWidth: 0 }}>
                              <FilePdfIcon style={{ fontSize: '20px', color: '#ffffff', flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                <div style={{ color: '#ffffff', fontWeight: 600, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  Carregado por {item.uploaded_by} em {new Date(item.uploaded_at).toLocaleDateString('pt-PT')}
                                </div>
                              </div>
                              {user && user.role && isAdminOrDCOrTeamLeader(user.role) && (
                                <Popconfirm
                                  title={
                                    <div>
                                      <div style={{ fontWeight: 600, marginBottom: '8px' }}>Eliminar PDF</div>
                                      <div>Tem a certeza que deseja eliminar este PDF?</div>
                                    </div>
                                  }
                                  onConfirm={() => handleDeleteProductPdf('seguranca', item.id)}
                                  okText="Sim"
                                  cancelText="Não"
                                  okButtonProps={{ danger: true }}
                                >
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: '#ff4d4f', flexShrink: 0 }}
                                  />
                                </Popconfirm>
                              )}
                            </div>
                          }
                          style={{
                            background: 'transparent',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '8px',
                            marginBottom: '12px'
                          }}
                        >
                          <PdfViewerPanel item={item} />
                        </Panel>
                              ))}
                            </Collapse>
                          </>
                        );
                      })()}
                      
                      {productPdfs.seguranca.length === 0 && resourceFolders.seguranca.length === 0 && (
                        <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
                          Nenhum recurso disponível
                        </div>
                      )}
                    </>
                  )}
                </div>
              )
            },
            {
              key: 'energia',
              label: 'Energia',
              children: (
                <div>
                  {loadingResources ? (
                    <GhostLoader />
                  ) : (
                    <>
                      {user && user.role && (isAdminOrDCOrTeamLeader(user.role) || canCreateFolders(user.role)) && (
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                          {isAdminOrDCOrTeamLeader(user.role) && (
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              loading={uploadingPdf}
                              onClick={() => handleUploadClick('energia')}
                              style={{ 
                                flex: 1,
                                height: '40px',
                                background: 'transparent',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                color: '#ffffff',
                                fontWeight: 500
                              }}
                            >
                              Carregar PDF
                            </Button>
                          )}
                          {canCreateFolders(user.role) && (
                            <Button
                              type="primary"
                              icon={<FolderOutlined />}
                              onClick={() => {
                                folderForm.setFieldsValue({ category: 'energia', parent_folder_id: null });
                                setFolderModalVisible(true);
                              }}
                              style={{ 
                                flex: 1,
                                height: '40px',
                                background: 'transparent',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                color: '#ffffff',
                                fontWeight: 500
                              }}
                            >
                              Criar Pasta
                            </Button>
                          )}
                        </div>
                      )}
                      {/* Display Folders */}
                      {(() => {
                        // Filter to only show root folders (folders without parent_folder_id)
                        const rootFolders = resourceFolders.energia.filter(f => !f.parent_folder_id || f.parent_folder_id === null);
                        return rootFolders.length > 0 && (
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                              Pastas
                            </div>
                            <Collapse ghost>
                              {rootFolders.map((folder) => 
                                renderFolderWithSubfolders(folder, 'energia', resourceFolders.energia, productPdfs.energia)
                              )}
                            </Collapse>
                          </div>
                        );
                      })()}
                      
                      {/* Display PDFs without folders */}
                      {(() => {
                        const pdfsWithoutFolders = productPdfs.energia.filter(pdf => !pdf.folder_id);
                        return pdfsWithoutFolders.length > 0 && (
                          <>
                            {resourceFolders.energia.length > 0 && (
                              <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, marginBottom: '12px', fontSize: '14px', marginTop: '16px' }}>
                                PDFs (sem pasta)
                              </div>
                            )}
                            <Collapse ghost>
                              {pdfsWithoutFolders.map((item) => (
                        <Panel
                          key={item.id.toString()}
                          header={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', minWidth: 0 }}>
                              <FilePdfIcon style={{ fontSize: '20px', color: '#ffffff', flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                <div style={{ color: '#ffffff', fontWeight: 600, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  Carregado por {item.uploaded_by} em {new Date(item.uploaded_at).toLocaleDateString('pt-PT')}
                                </div>
                              </div>
                              {user && user.role && isAdminOrDCOrTeamLeader(user.role) && (
                                <Popconfirm
                                  title={
                                    <div>
                                      <div style={{ fontWeight: 600, marginBottom: '8px' }}>Eliminar PDF</div>
                                      <div>Tem a certeza que deseja eliminar este PDF?</div>
                                    </div>
                                  }
                                  onConfirm={() => handleDeleteProductPdf('energia', item.id)}
                                  okText="Sim"
                                  cancelText="Não"
                                  okButtonProps={{ danger: true }}
                                >
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: '#ff4d4f', flexShrink: 0 }}
                                  />
                                </Popconfirm>
                              )}
                            </div>
                          }
                          style={{
                            background: 'transparent',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '8px',
                            marginBottom: '12px'
                          }}
                        >
                          <PdfViewerPanel item={item} />
                        </Panel>
                              ))}
                            </Collapse>
                          </>
                        );
                      })()}
                      
                      {productPdfs.energia.length === 0 && resourceFolders.energia.length === 0 && (
                        <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
                          Nenhum recurso disponível
                        </div>
                      )}
                    </>
                  )}
                </div>
              )
            }
          ]}
        />
      </Drawer>

      {/* PDF Viewer Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FilePdfIcon style={{ fontSize: '20px', color: '#e60000' }} />
            <span>{selectedPdfName}</span>
          </div>
        }
        open={pdfViewerVisible}
        onCancel={() => {
          setPdfViewerVisible(false);
          // Clean up blob URL to free memory
          if (selectedPdfUrl && selectedPdfUrl.startsWith('blob:')) {
            URL.revokeObjectURL(selectedPdfUrl);
            setSelectedPdfUrl('');
            sessionStorage.removeItem('recursos_selectedPdfUrl');
          }
        }}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ padding: '0', height: 'calc(100vh - 200px)', overflow: 'hidden' }}
      >
        {selectedPdfUrl ? (
          <iframe
            src={selectedPdfUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title={selectedPdfName}
          />
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>A carregar PDF...</div>
          </div>
        )}
      </Modal>

      {/* Create Folder Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FolderOutlined style={{ fontSize: '20px', color: '#6366f1' }} />
            <span>Criar Nova Pasta</span>
          </div>
        }
        open={folderModalVisible}
        onCancel={() => {
          setFolderModalVisible(false);
          folderForm.resetFields();
        }}
        onOk={() => folderForm.submit()}
        confirmLoading={creatingFolder}
        okText="Criar"
        cancelText="Cancelar"
      >
        <Form
          form={folderForm}
          layout="vertical"
          onFinish={handleCreateFolder}
        >
          <Form.Item
            name="name"
            label="Nome da Pasta"
            rules={[
              { required: true, message: 'Por favor, insira o nome da pasta' },
              { max: 255, message: 'O nome não pode exceder 255 caracteres' }
            ]}
          >
            <Input placeholder="Digite o nome da pasta" />
          </Form.Item>
          <Form.Item
            name="category"
            label="Categoria"
            rules={[{ required: true, message: 'Por favor, selecione a categoria' }]}
          >
            <Select disabled>
              <Option value="telecomunicacao">Telecomunicação</Option>
              <Option value="seguranca">Segurança</Option>
              <Option value="energia">Energia</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="parent_folder_id"
            label="Pasta Pai (Opcional)"
            getValueFromEvent={(value) => {
              // Ensure we return the value properly (number or undefined)
              if (value === undefined || value === null || value === '') {
                return undefined;
              }
              return typeof value === 'string' ? parseInt(value, 10) : value;
            }}
          >
            <Select
              placeholder="Selecione uma pasta pai (opcional)"
              allowClear
              showSearch
              filterOption={(input, option) => {
                const category = folderForm.getFieldValue('category') as keyof typeof resourceFolders;
                if (!category || !resourceFolders[category]) return false;
                const folders = resourceFolders[category] || [];
                const folder = folders.find(f => f.id === option?.value);
                if (!folder) return false;
                const folderPath = getFolderPath(folder, folders);
                return folderPath.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {folderForm.getFieldValue('category') && (() => {
                const category = folderForm.getFieldValue('category') as keyof typeof resourceFolders;
                const folders = resourceFolders[category] || [];
                return folders.map((folder) => (
                  <Option key={folder.id} value={folder.id}>
                    {getFolderPath(folder, folders)}
                  </Option>
                ));
              })()}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Folder Selection Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PlusOutlined style={{ fontSize: '20px', color: '#6366f1' }} />
            <span>Carregar PDF</span>
          </div>
        }
        open={uploadFolderModalVisible}
        onCancel={() => {
          setUploadFolderModalVisible(false);
          setPendingUpload(null);
          folderForm.resetFields(['upload_folder_id']);
        }}
        onOk={() => {
          if (pendingUpload) {
            // Get the selected folder ID from the form
            // Get the selected folder ID from the form (don't validate since it's optional)
            const selectedFolderId = folderForm.getFieldValue('upload_folder_id');
            
            // Close modal first
            setUploadFolderModalVisible(false);
            const category = pendingUpload.category;
            // Convert to number if it's a string, or use null if undefined/empty
            const folderIdToUse = selectedFolderId && selectedFolderId !== '' && selectedFolderId !== 'null' && selectedFolderId !== 'undefined' 
              ? (typeof selectedFolderId === 'number' ? selectedFolderId : parseInt(selectedFolderId, 10))
              : null;
            setPendingUpload(null);
            folderForm.resetFields(['upload_folder_id']);
            
            // Then open file picker
            setTimeout(() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.pdf';
              input.onchange = (e: any) => {
                const file = e.target.files[0];
                if (file) {
                  handleUploadProductPdf(category, file, folderIdToUse);
                }
              };
              input.click();
            }, 100);
          }
        }}
        okText="Selecionar Ficheiro"
        cancelText="Cancelar"
      >
        <Form
          form={folderForm}
          layout="vertical"
        >
          <Form.Item
            name="upload_folder_id"
            label="Adicionar à Pasta (Opcional)"
            help="Deixe em branco para adicionar à categoria sem pasta"
          >
            <Select
              placeholder="Selecione uma pasta (opcional)"
              allowClear
              showSearch
              filterOption={(input, option) => {
                if (!pendingUpload) return false;
                const category = pendingUpload.category as keyof typeof resourceFolders;
                const folders = resourceFolders[category] || [];
                const folder = folders.find(f => f.id === option?.value);
                if (!folder) return false;
                const folderPath = getFolderPath(folder, folders);
                return folderPath.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {pendingUpload && (() => {
                const category = pendingUpload.category as keyof typeof resourceFolders;
                const folders = resourceFolders[category] || [];
                return folders.map((folder) => (
                  <Option key={folder.id} value={folder.id}>
                    {getFolderPath(folder, folders)}
                  </Option>
                ));
              })()}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Venda Modal */}
      <VendaModal
        visible={vendaModalVisible}
        onClose={() => {
          setVendaModalVisible(false);
          setSelectedLeadForVenda(null);
          setVendaModalSkipCustomer(false);
        }}
        leadId={selectedLeadForVenda?.id}
        clientData={selectedLeadForVenda ? {
          name: selectedLeadForVenda.name,
          email: selectedLeadForVenda.email || undefined,
          phone: selectedLeadForVenda.phone || undefined,
          address: selectedLeadForVenda.address || undefined
        } : undefined}
        skipCustomerStep={vendaModalSkipCustomer}
        onComplete={() => {
          setVendaModalVisible(false);
          setSelectedLeadForVenda(null);
          setVendaModalSkipCustomer(false);
          // Refresh leads after sale
          fetchLeads();
        }}
        onOperatorSelected={(operator: OperatorType, customerData: OperatorCustomerData) => {
          // Handle operator selection from VendaModal - open corresponding wizard/modal
          const leadForOperator = selectedLeadForVenda;
          // Create a temporary lead object from customer data if no selectedLeadForVenda
          const leadToUse: Lead = leadForOperator || {
            id: 0, // Temporary ID
            name: customerData.name,
            email: customerData.email || undefined,
            phone: customerData.phone || undefined,
            address: customerData.address || undefined,
            lead_status: 'Fecho',
            lead_source: 'venda_modal',
            business_area: 'telecomunicacao',
            customer_type: 'residencial',
            created_by: user?.id || 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          } as Lead;
          
          switch (operator) {
            // Telecomunicações
            case 'nos':
              handleNOSDocument(leadToUse);
              break;
            case 'meo':
              handleMeoPortalClick();
              break;
            case 'vodafone':
              handleVodafoneDocument(leadToUse);
              break;
            case 'altice':
              handleAlticeResidencialClick(leadToUse);
              break;
            // Segurança
            case 'prosegur':
              handleProsegurForm(leadToUse);
              break;
            case 'nos_securitas':
              handleNOSSecuritasDocument(leadToUse);
              break;
            // Energia / Solar
            case 'energy':
              handleEnergyProposal(leadToUse);
              break;
            default:
              break;
          }
        }}
      />
    </>
  );
};

export default ClientManagement; 